import DocumentIntake from './DocumentIntake';
import { APPEAL_STATUS } from '../constants/statusCodes';
import { useYearRange } from '../constants/yearRange';
import DocumentsBubble from './DocumentsBubble';
import NoticeExtractor from './NoticeExtractor';
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { fileUrl } from '../utils/fileUrl';
import DocTypeOptions from '../components/DocTypeOptions';
import { useDocVocab } from '../utils/docVocab';
import { uploadFile } from '../utils/uploadFile';
import { fileDelete } from '../utils/fileDelete';
import { loadYearValues } from '../utils/yearValues';
import DocumentScanner from './DocumentScanner';
import ClientDeliveries from './ClientDeliveries';
import AdminPanel from './AdminPanel';
import ConsultantsPage from './ConsultantsPage';
import AssessorsCollectorsPage from './AssessorsCollectorsPage';
import TableManagementPage from './TableManagementPage';
import AddClientModal from '../components/AddClientModal';
import AddConsultantModal from '../components/AddConsultantModal';
import EmployeeTable from '../components/EmployeeTable';
import EntityNotes from '../components/EntityNotes';
import ContactModal from '../components/ContactModal';
import PropertyContactsCard from '../components/PropertyContactsCard';
import ClientDocuments from '../components/ClientDocuments';
import { representedInYear, retirementLabel, yearState, yearChip } from '../utils/retirement';
import { TsAssignmentDisplay, ParcelTsDisplay } from '../components/AssignmentPopover';
import { rolesForClient, roleNames, tsText, tsRowsForParcel } from '../utils/assignments';
import TaxBillWorklist from './TaxBillWorklist';
import TaxBillApprovals from './TaxBillApprovals';
import TaxBillTransmittals from './TaxBillTransmittals';
import ErrorBoundary from '../components/ErrorBoundary';
import { loadExpectedBills } from '../utils/expectedBills';
import OrgChart from '../components/OrgChart';
import ActivityFeed from './ActivityFeed';
import OpenQuestions from './OpenQuestions';
import TaxSavingsPacket from './TaxSavingsPacket';
import ReportView from '../components/ReportView';
import { pickerSections, getReport } from '../reports/registry';
import { isAdminStaff } from '../utils/isAdminStaff';
import { loadPresets, renamePreset, deletePreset } from '../utils/reportPresets';
import TaskEngine from './TaskEngine';
import ParcelModal from './ParcelModal';
import DocumentsBrowser from '../components/DocumentsBrowser';
import NoticeAudit from './NoticeAudit';
import Calendar from './Calendar';
import Feedback from './Feedback';
import FloatingReportButton from '../components/FloatingReportButton';
import Header from '../components/AppShell/Header';
import ColumnPicker from '../components/ColumnPicker';
import { useColumnPrefs } from '../hooks/useColumnPrefs';
import AddPropertyModal from '../components/AddPropertyModal';
import PropertiesGrid from '../components/Properties/PropertiesGrid';
import ValueYearSelect from '../components/ValueYearSelect';
import { parseLocalDate, fmtMoney } from '../utils/propertyRollup';
import { splitsForYear } from '../utils/prorata';
import { fetchAll } from '../reports/pagination';
import { formatPhone } from '../utils/format';
import { roleAbbr, roleLabel } from '../constants/roles';
import { exportToCsv } from '../utils/exportCsv';
import ScrollX, { pinCell, PINNED_TABLE, redrawKey } from '../components/ScrollX';

const WIGLEY_TIPS = [
  "Always file before the protest deadline — extensions are rare and risky.",
  "A 1% reduction in assessed value can mean thousands in tax savings.",
  "Personal property renditions are due April 15 in most Texas counties.",
  "Keep your authorization letters current — assessors won't talk to us without them.",
  "Document every conversation with an assessor's office. Dates and names matter.",
  "Unequal appraisal is often a stronger argument than market value in Texas.",
  "Check for exemptions on every parcel — they're easy to miss and hard to recover.",
  "Prior year tax bills are your best starting point for budget estimates.",
];

// parcels.services is a comma-joined TEXT written by ParcelModal's picker with `.join(',')` — no space.
// Display-side normalisation so "Appeal Work,Process Tax Bills" doesn't wrap; the stored form is left
// alone (ParcelModal's reader splits on ',' and trims, so both spacings round-trip identically).
const fmtServices = (v) => String(v || '').split(',').map(x => x.trim()).filter(Boolean).join(', ');

// ⛔ TWO COLUMNS HOLD THE PARCEL TYPE AND THE FALLBACK IS LOAD-BEARING — do not "simplify" it away.
// ParcelModal's Parcel Type picker (:3053-3069) writes parcel_type_detail and nothing writes parcel_type
// any more, but the legacy column is still the ONLY value 35 of 36 parcels have: the split is 35 legacy-only
// (the SigerTax/LaSalle import, which maps column 33 to parcel_type) against 1 detail-only, with zero rows
// carrying both. A bare swap to parcel_type_detail would blank Type for every LaSalle parcel. This mirrors
// the read fallback ParcelModal:3053 already uses. It goes when those 35 rows are migrated, not before.
const parcelTypeOf = (p) => p.parcel_type_detail || p.parcel_type;

function LegalEntityEditor({ clientId, isDark, c, s }) {
  const [entities, setEntities] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (!clientId) return;
    supabase.from('clients').select('legal_entity_names').eq('id', clientId).single()
      .then(({ data }) => {
        setEntities(data?.legal_entity_names || '');
        setLoading(false);
      });
  }, [clientId]);

  async function save() {
    setSaving(true);
    await supabase.from('clients').update({ legal_entity_names: entities }).eq('id', clientId);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  if (loading) return <div style={{ fontSize:'13px', color:c.muted }}>Loading...</div>;

  return (
    <div>
      <div style={{ fontSize:'12px', color:c.muted, marginBottom:'8px' }}>
        Enter all legal entity names associated with this client, separated by commas.
        These will be used to match documents during intake.
      </div>
      <div style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
        <textarea
          value={entities}
          onChange={e => setEntities(e.target.value)}
          placeholder="e.g. SS Antelope LLC, Antelope Holdings LLC, Antelope Investment Group..."
          style={{ flex:1, padding:'8px 11px', background:isDark?'var(--ink)':'#ffffff', border:`1px solid ${isDark?'#434C68':'#c8ccd8'}`, borderRadius:'7px', color:isDark?'#e8eaf2':'var(--ink)', fontSize:'13px', outline:'none', fontFamily:"'Public Sans',system-ui,sans-serif", resize:'vertical', minHeight:'60px' }}
        />
        <div style={{ display:'flex', flexDirection:'column', gap:'6px', alignItems:'flex-end' }}>
          <button onClick={save} disabled={saving}
            style={{ padding:'7px 16px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'7px', color: 'white', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif", whiteSpace:'nowrap' }}>
            {saving ? 'Saving...' : '💾 Save'}
          </button>
          {saved && <div style={{ fontSize:'11px', color:'#34d399', fontWeight:'600' }}>✓ Saved</div>}
        </div>
      </div>
      {entities && (
        <div style={{ marginTop:'10px', display:'flex', flexWrap:'wrap', gap:'6px' }}>
          {entities.split(',').map(e => e.trim()).filter(Boolean).map((e, i) => (
            <span key={i} style={{ padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'600', background:'rgba(180,0,0,0.1)', color:'var(--red)', border:'1px solid rgba(180,0,0,0.25)' }}>{e}</span>
          ))}
        </div>
      )}
    </div>
  );
}


export default function StaffPortal({ user, onSignOut }) {
  // EntityNotes' year tabs ← folder_year_config (one source; was this literal, twice inline).
  const { years: yearRange } = useYearRange();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [properties, setProperties] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [yearValues, setYearValues] = useState(() => new Map());   // parcel_id → { market, taxable } for the selected year (utils/yearValues)
  // Every pro-rata line (utils/prorata): a split parcel shows under each of the client's properties it has a share in, on
  // the Properties list and the property card. Read again when a parcel card closes (its split may have changed).
  const [prorataRows, setProrataRows] = useState([]);
  const [prorataReload, setProrataReload] = useState(0);
  const [allClients, setAllClients] = useState([]);
  const [ntrgStaff, setNtrgStaff] = useState([]); // active NTRG staff — name resolution for assignment display
  const staffNameById = (id) => (ntrgStaff.find(x => x.id === id) || {}).name || '';
  // Read-only assignment display (editing lives in Utilities → Table Management → Client Assignments).
  const [clientAssignments, setClientAssignments] = useState([]); // client_role_assignments rows (CS/TS/CM)
  const [contactRoleOpts, setContactRoleOpts] = useState([]);    // lookup_options for list 'Contact Roles' — {label,code,category}
  const [tsBillCount, setTsBillCount] = useState(0); // dashboard 'Tax Bill Processing' red count — my bills to process
  const [taxbillScan, setTaxbillScan] = useState(null); // worklist→scanner launch: null | {mode:'match'} | {mode:'row', ...target}
  const [csBillCount, setCsBillCount] = useState(0); // dashboard 'Tax Bill Approvals' red count — bills awaiting my CS approval
  const [transmitBillCount, setTransmitBillCount] = useState(0); // dashboard 'Tax Bill Transmittals' red count — approved bills ready for my CS to transmit
  const asgRows = (clientId, role) => rolesForClient(clientAssignments, clientId, role);
  const csDisplay = (clientId) => roleNames(clientAssignments, ntrgStaff, clientId, 'CS');
  const cmDisplay = (clientId) => roleNames(clientAssignments, ntrgStaff, clientId, 'CM');
  const tsDisplayText = (clientId) => tsText(clientAssignments, ntrgStaff, clientId);
  // Role LABELS for the All Contacts ROLES badges. The delivery roles moved to a property-scoped lookup
  // (2026-08-02_delivery_roles_to_property.sql), so CLIENT_CONTACT_ROLES no longer carries them and
  // roleLabel/roleAbbr fall through to the raw code ('invoice', 'primary_property_contact'). Same two-step
  // the other two surfaces use — ContactModal.js:23-25, ParcelModal.js:231-232 — not a third path.
  useEffect(() => {
    (async () => {
      const lid = (await supabase.from('lookup_lists').select('id').eq('name', 'Contact Roles').maybeSingle()).data?.id;
      if (lid) setContactRoleOpts((await supabase.from('lookup_options').select('label,code,category,sort_order').eq('list_id', lid).eq('active', true).order('sort_order')).data || []);
    })();
  }, []);
  // The lookup's label wins; roleLabel covers the client-level 'primary' that still lives in
  // CLIENT_CONTACT_ROLES; the raw code is the last resort so an unknown role is still visible rather than blank.
  const contactRoleLabel = (code) => (contactRoleOpts.find(o => o.code === code) || {}).label || roleLabel(code);

  const reloadAssignments = useCallback(async () => {
    const { data } = await supabase.from('client_role_assignments').select('*');
    setClientAssignments(data || []);
  }, []);
  const [allProperties, setAllProperties] = useState([]);
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);   // load finished — distinct from "no rows"; see PropertiesGrid's empty state
  // ⛔ EVERY PAGE THE RENDER ACTUALLY HANDLES. Anything else — a value persisted by a previous
  // deploy, a hand-edited URL, a typo — is NOT a page, and restoring it renders an empty body or a
  // dead end. This list is the guard; add to it when you add a page.
  const VALID_PAGES = new Set([
    'dashboard', 'calendar', 'clients', 'consultants', 'intake', 'documents', 'properties',
    'utilities', 'ntrg', 'users', 'admin', 'activity', 'audit', 'exchange', 'extractor',
    'feedback', 'parcels', 'reporting', 'scanner', 'tasks', 'deliveries',
    'assessors_collectors', 'tablemgmt', 'ntrg_empinfo', 'ntrg_orgchart',
    'taxbill_approvals', 'taxbill_transmittals', 'taxbill_worklist', 'report', 'open_questions', 'tax_savings_packet',
  ]);
  // ⛔ 'report' IS VALID BUT NOT RESTORABLE. It needs a reportId, which is component state and does
  // NOT survive a reload — so restoring the page without it lands on "No such report." This is
  // exactly the production break: anyone who opened a report got ?page=report in the address bar
  // and wh_page='report' in sessionStorage, and their next login restored the page without the id.
  // Legacy 'exec_summary' has the same shape — it was a real page until the definition replaced it,
  // and a stale copy now matches no branch at all, rendering an empty body.
  const isRestorablePage = (p) => VALID_PAGES.has(p) && p !== 'report';
  // A page that was renamed or folded into another: a bookmark, a persisted wh_page or a history entry from before
  // still lands somewhere real. 'reports' (the top-nav Reports page) became Live Reporting in step 8 part 6.
  const LEGACY_PAGE = { reports: 'reporting' };
  const restorablePage = (p) => { const q = LEGACY_PAGE[p] || p; return isRestorablePage(q) ? q : 'dashboard'; };
  const [activePage, setActivePage] = useState(() => {
    try {
      const url = new URL(window.location.href);
      const p = url.searchParams.get('page');
      if (p) return restorablePage(p);
    } catch {}
    return restorablePage(sessionStorage.getItem('wh_page'));
  });
  // ⛔ DECLARED HERE, ABOVE THE EFFECTS THAT READ THEM. A dep array evaluates during render, so
  // `[reportId]` against a declaration further down is a TDZ ReferenceError on mount. Caught by
  // npm run lint:tdz while fixing the crash these very lines exist to prevent.
  // Which registered report the generic ReportView is showing. NOT a navigation dimension —
  // same treatment as drillTask/createParcelFor per the note above navigateTo: its own setter,
  // no history entry, no URL param. Page 'report' + this id is what renders.
  const [reportId, setReportId] = useState(null);
  // The popstate handler subscribes once with [] deps, so it must read the CURRENT report id
  // through a ref rather than the null captured in its mount closure — same reason dataRef exists.
  const reportIdRef = React.useRef(null);
  const [reportNotice, setReportNotice] = useState('');
  // Saved reports (report_presets — step 8): this person's, shown as cards at the top of Live Reporting. The one being
  // opened travels with reportId into ReportView, which starts from its options instead of the report's defaults.
  const [reportPreset, setReportPreset] = useState(null);
  const [presets, setPresets] = useState([]);
  const [presetMsg, setPresetMsg] = useState('');
  useEffect(() => {
    if (activePage !== 'reporting') return undefined;
    let alive = true;
    loadPresets(staffUser?.id ? String(staffUser.id) : null).then((r) => {
      if (!alive) return;
      setPresets(r.ok ? r.data : []); setPresetMsg(r.ok ? '' : r.error);
    });
    return () => { alive = false; };
  }, [activePage]);
  const [navOpen, setNavOpen] = useState(false);
  const [drillClient, setDrillClient] = useState(() => {
    try { const s=sessionStorage.getItem('wh_drillClient'); return s?JSON.parse(s):null; } catch{return null;}
  });
  const [drillProperty, setDrillProperty] = useState(() => {
    try { const s=sessionStorage.getItem('wh_drillProperty'); return s?JSON.parse(s):null; } catch{return null;}
  });
  const [drillContact, setDrillContact] = useState(() => {
    try { const s=sessionStorage.getItem('wh_drillContact'); return s?JSON.parse(s):null; } catch{return null;}
  });
  const [legalEntitiesOpen, setLegalEntitiesOpen] = useState(false); // Legal Entity Names card collapsed by default (inline collapse — NOT a nav overlay, intentionally not history-integrated)
  const [showExpiredLinks, setShowExpiredLinks] = useState(false); // Secure Upload Links: expired/deactivated links collapsed by default
  // Dashboard task cards — ONE collapse state shared by Assigned to You + Personal To-Dos, so they always
  // match. Same convention as the Filed Documents panel below: '0'/'1', ABSENT MEANS COLLAPSED, try/catch both
  // ways. The key can't be read in a lazy initializer — staffUser resolves after the
  // first render, so a mount-time read would key on 'anon' and never pick up the staffer's saved value.
  const [dashTasksCollapsed, setDashTasksCollapsed] = useState(true);   // default collapsed on first load
  const [clientContacts, setClientContacts] = useState([]); // Contact-centric: one row per client_contacts person w/ nested client_contact_roles:[{role}] (incl roleless)
  const [clientDetailTab, setClientDetailTab] = useState('properties'); // R1c: client detail tab (Property View / All Contacts View)
  // Opening a client lands on Property View (Philip 2026-11-03). Keyed on the client's id, so drilling into a property
  // or contact and coming back to the same client keeps the tab the user chose.
  useEffect(() => { setClientDetailTab('properties'); }, [drillClient?.id]);
  const [contactSearch, setContactSearch] = useState(''); // R2-S2: All Contacts search
  const [contactSortDir, setContactSortDir] = useState('asc'); // R2-S3: All Contacts Name sort direction
  const [propertySearch, setPropertySearch] = useState(''); // R3: Property View search
  const [propertySort, setPropertySort] = useState({ key: 'name', dir: 'asc' }); // R3: Property View sort — any column, click again to reverse
  const [manageAccessProp, setManageAccessProp] = useState(null);
  const [usersClient, setUsersClient] = useState(null); // selected client on manage users page
  const [usersView, setUsersView] = useState('users'); // 'users' | 'properties'
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name:'', email:'', title:'', role:'user', is_primary:false });
  const [addingUser, setAddingUser] = useState(false);
  const [manageAccessUser, setManageAccessUser] = useState(null); // user-centric assignment
  const [userSearch, setUserSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [previewFile, setPreviewFile] = useState(null); // {url, name, type}
  const [uploadTokens, setUploadTokens] = useState([]);
  const [propertyTokens, setPropertyTokens] = useState([]);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [tokenNote, setTokenNote] = useState(''); // eslint-disable-line
  const [newTokenUrl, setNewTokenUrl] = useState('');

  // activePage → sessionStorage mirror (keeps the preview tab / refresh on the same page).
  // History + URL (?page=) are owned by navigateTo and the entry-0 mount effect (Phase 2) —
  // this effect no longer pushes history or writes the URL.
  useEffect(() => { sessionStorage.setItem('wh_page', activePage); }, [activePage]);
  useEffect(() => { reportIdRef.current = reportId; }, [reportId]);

  // Listen for browser back/forward — restore the FULL snapshot from history.state.wh (Phase 3).
  // Falls back to ?page= (drills cleared) for old/stateless entries. See restoreSnapshot below.
  useEffect(() => {
    const onPop = (e) => {
      try {
        const wh = e.state && e.state.wh;
        // Same guard as the mount initializer: a stateless history entry pointing at ?page=report
        // (or anything unknown) must not restore into a page that cannot render.
        const urlPage = new URL(window.location.href).searchParams.get('page');
        const snap = wh || {
          page: restorablePage(urlPage),
          client: null, property: null, contact: null, parcel: null, modal: null,
        };
        if (LEGACY_PAGE[snap.page]) snap.page = LEGACY_PAGE[snap.page];
        if (snap.page === 'report' && !getReport(reportIdRef.current)) snap.page = 'reporting';
        restoreSnapshot(snap);
      } catch {}
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line
  }, []);

  useEffect(() => { sessionStorage.setItem('wh_drillClient', JSON.stringify(drillClient)); }, [drillClient]);
  useEffect(() => { sessionStorage.setItem('wh_drillProperty', JSON.stringify(drillProperty)); }, [drillProperty]);
  useEffect(() => { sessionStorage.setItem('wh_drillContact', JSON.stringify(drillContact)); }, [drillContact]);

  useEffect(() => {
    if (drillClient) {
      supabase.from('upload_tokens').select('*').eq('client_id', drillClient.id).order('created_at', { ascending: false }).then(({ data }) => setUploadTokens(data || []));
    }
  }, [drillClient]);

  // Property upload tokens — single source of truth: loads whenever drillProperty is set, including
  // a Back/Forward restore (Phase 3). Replaces the former inline fetches on the property-row clicks.
  useEffect(() => {
    if (drillProperty) {
      supabase.from('upload_tokens').select('*').eq('property_id', drillProperty.id).order('created_at', { ascending: false }).then(({ data }) => setPropertyTokens(data || []));
    }
  }, [drillProperty]);

  // Full property row for the Details card — drillProperty may be a subset/synthetic row (missing county/address/
  // purchase), so re-fetch the complete row by id. Re-runs on any drillProperty change, so a post-edit refresh lands.
  useEffect(() => {
    if (!drillProperty?.id) { setPropFull(null); return; }
    supabase.from('properties').select('*').eq('id', drillProperty.id).single().then(({ data }) => setPropFull(data || null));
  }, [drillProperty]);

  const loadClientContacts = useCallback((clientId) => {
    // ⛔ ROLES LIVE IN contact_properties, NOT client_contact_roles. The delivery roles (Invoice, TBT,
    // Authorizations, …) moved to a PROPERTY-scoped assignment in 2026-08-02_delivery_roles_to_property.sql;
    // client_contact_roles keeps only the client-level 'primary' flag and is empty. Same access pattern as
    // ContactModal.js:28. client_contact_roles stays in the select because :1764 and :2027 still read it.
    return supabase.from('client_contacts')
      .select('id, first_name, last_name, title, email, phone, created_at, client_contact_roles(role), contact_properties(role)')
      .eq('client_id', clientId)
      .then(({ data, error }) => {
        // A bad embed would return null and empty the whole contacts list — say so rather than showing none.
        if (error) console.error('[loadClientContacts]', error.message);
        const arr = data || []; setClientContacts(arr); return arr;
      });
  }, []);

  useEffect(() => {
    if (drillClient) loadClientContacts(drillClient.id);
  }, [drillClient]);

  useEffect(() => {
    supabase.from('ntrg_staff').select('id,name').eq('active', true).order('name').then(({ data }) => setNtrgStaff(data || []));
    reloadAssignments();
  }, [reloadAssignments]);

  const [propertyTasks, setPropertyTasks] = useState([]);
  const [contactProperties, setContactProperties] = useState([]); // R4a: drillContact's properties, one entry per property with its roles (contact_properties has one row per role)
  const [propSearchQ, setPropSearchQ] = useState(''); // eslint-disable-line
  const [propertyFiles, setPropertyFiles] = useState([]);
  const [propertyFilesRefreshing, setPropertyFilesRefreshing] = useState(false);

  async function loadPropertyFiles(property) {
    const prop = property || drillProperty;
    if (!prop) return;
    setPropertyFilesRefreshing(true);
    // ⛔ SILENT 1000-ROW CEILING. Neither query below paginates, and NOT passing .limit() does not mean
    // "no limit" — PostgREST caps every response at db-max-rows (1000) with no error and no warning. Past
    // that, the panel AND its file-count badge both truncate silently, and the badge would agree with the
    // truncated list rather than the truth, so nothing on screen reveals it. Same failure class as the
    // un-paginated collector load (CLAUDE.md) — "the query succeeded" is not "the query was complete".
    // Not fixed because a single property holding 1000+ filed documents is implausible today; if that ever
    // stops being true, both queries need a .range(from, from+999) loop, not a bigger .limit().
    const { data: byId } = await supabase.from('file_exchange')
      .select('*')
      .eq('property_id', prop.id)
      .order('created_at', { ascending: false });
    if (byId && byId.length > 0) {
      setPropertyFiles(byId);
    } else {
      const { data: props } = await supabase.from('properties')
        .select('id').eq('master_property_id', prop.master_property_id);
      if (props && props.length > 0) {
        const ids = props.map(p => p.id);
        const { data: byMaster } = await supabase.from('file_exchange')
          .select('*').in('property_id', ids)
          .order('created_at', { ascending: false });
        setPropertyFiles(byMaster || []);
      } else {
        setPropertyFiles([]);
      }
    }
    setPropertyFilesRefreshing(false);
  }
  const [drillTask, setDrillTask] = useState(null);
  const [myNotifications, setMyNotifications] = useState([]);   // unread notifications for the current staffer (bell)
  const [myTasks, setMyTasks] = useState([]);
  const [personalTodos, setPersonalTodos] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [parcelSearch, setParcelSearch] = useState('');
  const [parcelClientFilter, setParcelClientFilter] = useState('');
  const [parcelStateFilter, setParcelStateFilter] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [showRetiredClients, setShowRetiredClients] = useState(false);
  // ⛔ ONE VALUE YEAR FOR THE WHOLE APP, and sessionStorage is where it lives. This used to be a SECOND,
  // parallel year: folder_year_config decided what the retirement filters and counts used, while the parcel
  // modal kept its own selection in sessionStorage['wh_valueYear'] and the two never met. Choosing 2021 in a
  // modal left every count on the Clients page still answering for 2026.
  //
  // folder_year_config.current_value_year is now the DEFAULT — the firm's working year, used until this
  // session has a choice of its own — which is what its name says it is.
  // ⛔ ONE WORDING, ONE PLACE. "Not recorded" rather than "not available": this is a gap in what we hold
  // (the data starts at 2021 and is still being loaded), not a limitation on what the app can show. The
  // tooltip is per cell; the explanation under the table is once per table — never repeated per row.
  const noYearData = (p) => `No data recorded for this parcel in ${p._pydYear || currentValueYear}`;
  const blankYearCell = (p) => <span title={noYearData(p)} style={{ color: c.muted, fontSize: '11px', cursor: 'help' }}>—</span>;
  const anyYearDataMissing = (rows) => (rows || []).some(x => x && x._pydMissing);
  const [currentValueYear, setCurrentValueYear] = useState(() => sessionStorage.getItem('wh_valueYear') || '2026');
  useEffect(() => {
    if (sessionStorage.getItem('wh_valueYear')) return;   // a session choice outranks the firm default
    supabase.from('folder_year_config').select('current_value_year').maybeSingle()
      .then(({ data }) => { if (data?.current_value_year) setCurrentValueYear(String(data.current_value_year)); });
  }, []);
  // ⛔ THE ONLY WRITER. sessionStorage is NOT reactive — it fires no event in the same tab — so a write here
  // cannot notify ParcelModal and a write there cannot notify this. The modal reads wh_valueYear at mount
  // (it opens over this screen, so it cannot be stale) and reports a switch back through onYearChange.
  const setValueYear = (yr) => { const v = String(yr); sessionStorage.setItem('wh_valueYear', v); setCurrentValueYear(v); };
  const [clientSearch, setClientSearch] = useState('');
  const [selectedProp, setSelectedProp] = useState(null);
  const [exchFileType, setExchFileType] = useState('');   // a doc_types KEY — the shared type list, no default guess
  const { vocab } = useDocVocab();   // folders & types (utils/docVocab) — Exchange's type lists, uploads, re-filing, folder labels
  const [exchClient, setExchClient] = useState(null);
  const [exchLevel, setExchLevel] = useState(null); // 'property' | 'general'
  const [exchNotify, setExchNotify] = useState(false);
  const [exchRecipients, setExchRecipients] = useState([]); // selected portal users to notify
  const [exchRecipientSearch, setExchRecipientSearch] = useState('');
  const [filingFile, setFilingFile] = useState(null); // file being manually filed from received
  const [filingType, setFilingType] = useState('');   // a doc_types KEY
  const [filingProp, setFilingProp] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [staffName, setStaffName] = useState('');
  const [staffFirstName, setStaffFirstName] = useState('');
  const [staffUser, setStaffUser] = useState(null);
  // Filed Documents panel — collapse state, per staff ('0'/'1', absent = collapsed).
  // ⛔ MUST SIT BELOW the staffUser declaration above. TWO separate traps here, both real:
  //   (a) NOT a lazy initializer. staffUser resolves AFTER first render, so an initializer would key off `undefined`,
  //       read the wrong slot once and never correct — the 92be788 trap.
  //   (b) the effect's DEP ARRAY [staffUser?.id] is evaluated during RENDER. Written above the declaration it
  //       throws a temporal-dead-zone ReferenceError on mount and blanks the page — the 6d48353 trap. Caught by
  //       the no-use-before-define pass, which is why that check runs on every change to this file.
  const [filedDocsCollapsed, setFiledDocsCollapsed] = useState(true);   // default collapsed
  const toggleFiledDocs = () => setFiledDocsCollapsed(v => {
    const next = !v;
    try { if (staffUser?.id) localStorage.setItem(`wh_filedDocs_collapsed_${staffUser.id}`, next ? '1' : '0'); } catch { /* private mode / quota — collapsing still works, it just isn't remembered */ }
    return next;
  });
  // Column sort for that panel. Client-side over rows already loaded; default newest-first, as before.
  const [filedDocsSort, setFiledDocsSort] = useState({ key: 'created_at', dir: 'desc' });
  useEffect(() => {
    if (!staffUser?.id) return;
    try { setFiledDocsCollapsed(localStorage.getItem(`wh_filedDocs_collapsed_${staffUser.id}`) !== '0'); } catch { /* unreadable storage → stay collapsed */ }
  }, [staffUser?.id]);
  // ⛔ Must sit BELOW the staffUser declaration — this runs during render, and a `const` above its
  // declaration is a TDZ ReferenceError that takes the whole portal down (eslint reports it as a
  // no-use-before-define WARNING only, and the build still compiles).
  const dashCollapseKey = `wh_dashboard_tasks_collapsed_${staffUser?.id || 'anon'}`;
  useEffect(() => {   // read once the staffer is known (see the state declaration for why not a lazy init)
    try { setDashTasksCollapsed(localStorage.getItem(dashCollapseKey) !== '0'); } catch { setDashTasksCollapsed(true); }
  }, [dashCollapseKey]);
  const toggleDashTasks = () => {
    const n = !dashTasksCollapsed;
    setDashTasksCollapsed(n);
    try { localStorage.setItem(dashCollapseKey, n ? '1' : '0'); } catch {}
  };

  // Dashboard bill badges — the logged-in user's three red counts, from ONE load of expected_bills.
  //   TS  'Tax Bill Processing' — bills still to process (not_started/outstanding) PLUS 'rejected' ones the CS
  //       sent back. Processed/approved/orphan bills are in Approvals, not here. Counts the SELECTED value year.
  //   CS  'Tax Bill Approvals'    — bills awaiting this CS's approval ('processed'). Rejected have left for the TS.
  //   CS  'Tax Bill Transmittals' — APPROVED bills (cleared the C2a file/transmit fork) ready to transmit.
  //
  // The two CS counts span a ROLLING YEAR WINDOW, not a single value year — a bill awaiting approval/transmit
  // shows on its CS's bubble regardless of which year it's in, and there is a live 2021 example (a bill submitted
  // 2026-08-25 against a parcel retired from 2022). Current year, ~4 back through next year. Each bill appears in
  // exactly one year's expected_bills output (all paths filter on p_tax_year; orphan path has NOT EXISTS), so
  // summing the window never double-counts.
  //
  // ⛔ ONE fetch feeds all three — DO NOT split them back into three loaders. They were separate, and between them
  //    they fired 13 expected_bills calls per dashboard mount: the CS and Transmittals loaders pulled the SAME six
  //    years and differed only in the workflow_status they counted, and the TS loader re-pulled a year already in
  //    the window. That RPC computes the whole firm's worklist across parcels × parcel_collectors × collectors ×
  //    collector_installments × tax_bills, so a duplicate call is not free — it was the single largest Disk IO
  //    consumer in the database (~74k calls). Adding a fourth badge means adding a filter here, not a fetch.
  const billCountYears = () => { const c = new Date().getFullYear(); return Array.from({ length: 6 }, (_, i) => c - 4 + i); };  // [c-4 .. c+1]
  const reloadBillCounts = useCallback(() => {
    if (!staffUser?.id || !clientAssignments.length) { setTsBillCount(0); setCsBillCount(0); setTransmitBillCount(0); return; }
    const years = billCountYears();
    const tsYear = Number(sessionStorage.getItem('wh_valueYear')) || new Date().getFullYear();
    // ⛔ The TS badge follows the selected value year, which is normally INSIDE the window and so already fetched.
    //    When it is not — someone working a year outside [c-4 .. c+1] — fetch it as an extra year rather than
    //    counting nothing: a silently-zero badge reads as "no work", which is the one wrong answer to give.
    //    The extra year is counted by the TS badge ONLY; the CS counts stay scoped to the window.
    const fetchYears = years.includes(tsYear) ? years : [...years, tsYear];
    Promise.all(fetchYears.map(y => loadExpectedBills(y).then(rows => [y, rows || []]))).then(pairs => {
      const byYear = new Map(pairs);
      const windowRows = years.flatMap(y => byYear.get(y) || []);
      const isMyCs = (r) => {
        const cid = rolesForClient(clientAssignments, r.client_id, 'CS')[0]?.staff_id;
        return cid && String(cid) === String(staffUser.id);
      };
      setTsBillCount((byYear.get(tsYear) || []).filter(r => {
        if (!['not_started', 'outstanding', 'rejected'].includes(r.workflow_status)) return false;
        const tid = tsRowsForParcel(clientAssignments, r.client_id, { state: r.state, ts_id: r.ts_id })[0]?.staff_id;
        return tid && String(tid) === String(staffUser.id);
      }).length);
      setCsBillCount(windowRows.filter(r => r.workflow_status === 'processed' && isMyCs(r)).length);
      setTransmitBillCount(windowRows.filter(r => r.workflow_status === 'approved' && isMyCs(r)).length);
    });
  }, [staffUser, clientAssignments]);
  useEffect(() => { reloadBillCounts(); }, [reloadBillCounts]);
  // The taxbill scanner is a modal-within-the-worklist: it's now the 'taxbillScan' nav-snapshot overlay
  // (opens/closes via navigateTo({modal})), so Back/nav close it through the one history mechanism — the old
  // activePage-based auto-close crutch is retired. Payload (mode/target) stays in the taxbillScan state.
  const darkMode = false; // Phase 1 soft-retired dark mode; kept as a const so remaining `darkMode` refs (incl. prop pass-throughs) resolve to the light branch.
  const [feedbackPrefill, setFeedbackPrefill] = useState(null);
  const [showTip, setShowTip] = useState(false);
  const [currentTip, setCurrentTip] = useState('');
  const [showToast, setShowToast] = useState(true);
  const [deepLinkMsg, setDeepLinkMsg] = useState(''); // Phase 2b: "that <entity> no longer exists" toast for a stale deep link
  const fileInputRef = React.useRef(null);

  // Parcel modal
  const [drillParcel, setDrillParcel] = useState(null);
  const [drillParcelFocus, setDrillParcelFocus] = useState(null); // deep-link section for ParcelModal (e.g. 'collectors' from the pull worklist)
  // Which BILL the deep link is about: { collectorId, installmentNumber }. A Tax Bill Processing row is about one
  // installment, not the Taxes section, so the modal scrolls to that installment's card and outlines it.
  const [drillParcelBill, setDrillParcelBill] = useState(null);
  // Bumped when the parcel modal closes → TaxBillWorklist re-runs its loader. Its only load effect is keyed
  // [year, pulledState], so an installment processed in the modal would otherwise keep showing as Outstanding
  // until the year changed or the page remounted — i.e. the fix would read as not working.
  const [worklistRefresh, setWorklistRefresh] = useState(0);
  const [modal, setModal] = useState(null); // topmost overlay dimension of the nav snapshot: null | 'addProperty' | 'addClient' | 'addConsultant' | 'contact' | 'colsProps' | 'colsParcels' | 'taxbillScan' | 'createParcel' | 'propDocs' | 'editProperty' | 'genToken'. Payload (if any) stays in its own React state; the snapshot carries only the NAME so Back closes the overlay.
  const [createParcelFor, setCreateParcelFor] = useState(null); // E2a: property to seed a new parcel (create mode); TEMP-triggered for now
  // Onboarding's New Parcel card: the client/property chosen there before ParcelModal can open. The
  // property drill inherits its property from the page; Onboarding has none, so it collects one.
  const [onbParcelClientId, setOnbParcelClientId] = useState('');
  const [onbParcelPropertyId, setOnbParcelPropertyId] = useState('');
  const [editProperty, setEditProperty] = useState(null); // property being edited (Add+Edit modal); null = closed
  const [propFull, setPropFull] = useState(null);         // full property row for the Details card (defensive vs subset/synthetic drillProperty)

  // Deep-link into a parcel from a worklist row. ParcelModal needs a FULL parcels row (it reads sys_year,
  // client_name, property_id, master_property_id, re_pp, retired_year), and a worklist row is a different
  // shape — hence the fetch.
  //
  // ⛔ `year` matters and is easy to miss. ParcelModal initialises valueYear from sessionStorage['wh_valueYear']
  // (ParcelModal:221); it takes no year prop. Without stamping it first, the modal opens on whatever year the
  // session last used — which LOOKS correct whenever that happens to match the worklist's year and is silently
  // wrong when it doesn't. Pass the worklist's year so the TS lands on the year the row is about.
  const openParcelDeepLink = async (parcelId, focus, year, bill = null) => {
    if (!parcelId) { setDeepLinkMsg("Can't open that row — it has no parcel."); setTimeout(() => setDeepLinkMsg(''), 6000); return; }
    const { data, error } = await supabase.from('parcels').select('*').eq('id', parcelId).maybeSingle();
    // Say so. The old version returned silently on failure, so a click did nothing and looked like a dead row.
    if (error || !data) {
      setDeepLinkMsg(`Couldn't open that parcel: ${error ? error.message : 'no parcel found for this row'}`);
      setTimeout(() => setDeepLinkMsg(''), 6000);
      return;
    }
    if (year) sessionStorage.setItem('wh_valueYear', String(year));
    setDrillParcelFocus(focus || null);
    setDrillParcelBill(bill);
    navigateTo({ drillParcel: data });
  };
  const openParcelForCollectors = (parcelId, year) => openParcelDeepLink(parcelId, 'collectors', year);

  // Live Reporting: a report row's Property opens that row's parcel card as an overlay ON the report (Philip
  // 2026-10-27, Josh's request), in the row's year — the report stays mounted underneath, so closing the card (or
  // Back) returns to it exactly as it was. openParcelDeepLink stamps the year for the card only (wh_valueYear), not
  // the app's page year.
  const openReportRow = (row) => openParcelDeepLink(row.parcel_id, null, row.tax_year);

  // ── Phase 2 (Approach A): browser-history snapshots ──────────────────
  // navStateRef mirrors the live nav snapshot (objects). Updated BOTH by the per-render effect
  // (latest committed state) AND synchronously inside navigateTo (in-flight composition), so
  // navigateTo never reads stale closure drill values when building a history entry.
  const navStateRef = React.useRef({ page: activePage, drillClient, drillProperty, drillContact, drillParcel, modal });
  useEffect(() => {
    navStateRef.current = { page: activePage, drillClient, drillProperty, drillContact, drillParcel, modal };
  });

  // Phase 2b: capture entity-drill IDs from the URL on the FIRST render (before the entry-0 effect can
  // rewrite the URL), so a pasted/refreshed deep link is resolvable by the cold-load effect below.
  const deepLinkRef = React.useRef(undefined);
  if (deepLinkRef.current === undefined) {
    const u = new URLSearchParams(window.location.search);
    deepLinkRef.current = { client: u.get('client'), property: u.get('property'), parcel: u.get('parcel'), contact: u.get('contact') };
  }

  // ── Phase 3 (Approach A): browser-history RESTORE ────────────────────
  // dataRef mirrors the global lists so the popstate handler (subscribed once, [] deps) reads
  // CURRENT data, not the empty arrays captured in its mount closure. clientContacts is included
  // for the zero-flash contact path in restoreSnapshot.
  const dataRef = React.useRef({ allClients, allProperties, parcels, clientContacts });
  useEffect(() => {
    dataRef.current = { allClients, allProperties, parcels, clientContacts };
  });

  // If a Back/Forward snapshot arrives before the lists finish loading, stash it here; the retry
  // effect below re-applies it once the lists land. Self-heals, then clears.
  const pendingRestoreRef = React.useRef(null);

  // Restore a full nav snapshot (page + 4 drills) from history.state. Uses RAW setters — NEVER
  // navigateTo — so it pushes ZERO history entries (no stack corruption). Rehydrates drills by .id
  // from dataRef, and writes navStateRef so the NEXT navigateTo merges against the restored truth.
  const restoreSnapshot = (snap, prefetched) => {
    const { allClients, allProperties, parcels, clientContacts } = dataRef.current;
    // prefetched rows (Phase 2b cold-load) win over in-memory list lookups; otherwise resolve by id from the lists.
    const client   = (prefetched && prefetched.client)   || (snap.client   ? (allClients.find(x => x.id === snap.client)     || null) : null);
    const property = (prefetched && prefetched.property) || (snap.property ? (allProperties.find(x => x.id === snap.property) || null) : null);
    const parcel   = (prefetched && prefetched.parcel)   || (snap.parcel   ? (parcels.find(x => x.id === snap.parcel)         || null) : null);

    // Contact (per-client): resolve synchronously from already-loaded contacts when possible
    // (zero flash, no extra fetch); otherwise load this client's contacts, then set.
    let contact = null, contactAsync = false;
    if (snap.contact && client) {
      const found = clientContacts.find(x => x.id === snap.contact);
      if (found) contact = found; else contactAsync = true;
    }

    // Page + drills in one tick → React batches → correct gated view, no intermediate flash.
    setActivePage(snap.page);
    setDrillClient(client);
    setDrillProperty(property);
    setDrillParcel(parcel);
    setModal(snap.modal ?? null);
    if (!contactAsync) setDrillContact(contact);

    // Keep navStateRef truthful for the next navigateTo (async contact filled in the .then below).
    navStateRef.current = { page: snap.page, drillClient: client, drillProperty: property,
                            drillContact: contactAsync ? null : contact, drillParcel: parcel, modal: snap.modal ?? null };

    if (contactAsync) {
      loadClientContacts(client.id).then(arr => {
        const ct = arr.find(x => x.id === snap.contact) || null;
        setDrillContact(ct);
        navStateRef.current = { ...navStateRef.current, drillContact: ct };
      });
    }

    // Only the global-list dims can fail to resolve (lists not loaded yet). Stash for the retry.
    const missing = (snap.client && !client) || (snap.property && !property) || (snap.parcel && !parcel);
    pendingRestoreRef.current = missing ? snap : null;
  };

  // Self-heal: when the global lists finish loading, re-apply any snapshot that couldn't resolve.
  useEffect(() => {
    if (pendingRestoreRef.current) restoreSnapshot(pendingRestoreRef.current);
    // eslint-disable-next-line
  }, [allClients, allProperties, parcels]);

  // Phase 2b: resolve URL drill IDs into entity rows, deepest-first (parcel → property → client), with
  // graceful not-found. `missing` = the deepest EXPLICITLY-requested id that didn't resolve (drives the toast).
  const resolveDeepLink = async (ids) => {
    let client = null, property = null, parcel = null;
    if (ids.parcel) {
      const { data } = await supabase.from('parcels').select('*').eq('id', ids.parcel).maybeSingle();
      parcel = data || null;
    }
    const propId = ids.property || parcel?.property_id || null;
    if (propId) {
      const { data } = await supabase.from('properties').select('*').eq('id', propId).maybeSingle();
      property = data || null;
    }
    const clientId = ids.client || property?.client_id || parcel?.client_id || null;
    if (clientId) {
      const { data } = await supabase.from('clients').select('*').eq('id', clientId).maybeSingle();
      client = data || null;
    }
    let missing = null;
    if (ids.parcel && !parcel) missing = 'parcel';
    else if (ids.property && !property) missing = 'property';
    else if (ids.client && !client) missing = 'client';
    return { client, property, parcel, missing };
  };

  // Serialize a snapshot to history.state — IDs only (objects are big/stale).
  const toHist = (s) => ({ wh: {
    page:     s.page,
    client:   s.drillClient   ? s.drillClient.id   : null,
    property: s.drillProperty ? s.drillProperty.id : null,
    contact:  s.drillContact  ? s.drillContact.id  : null,
    parcel:   s.drillParcel   ? s.drillParcel.id   : null,
    modal:    s.modal ?? null,
  } });

  // Phase 2a: mirror the ENTITY drills into the URL query so a deep link is shareable/refresh-stable.
  // Takes a toHist().wh object (ids). `page` keeps its existing rule (omitted on dashboard). Only the four
  // entity drills go in the URL — transient modals are deliberately NOT bookmarkable. (Cold-load resolution
  // of these params is Phase 2b; 2a only WRITES them.)
  const buildUrl = (h) => {
    const url = new URL(window.location.href);
    if (h.page === 'dashboard') url.searchParams.delete('page'); else url.searchParams.set('page', h.page);
    for (const k of ['client', 'property', 'parcel', 'contact']) {
      if (h[k]) url.searchParams.set(k, h[k]); else url.searchParams.delete(k);
    }
    return url.toString();
  };

  // Single navigation helper, sole owner of history + URL (Phase 2). All page/drill changes route
  // through navigateTo(patch): it applies ONLY the keys present in patch (partial-patch; the
  // state-setting block below is byte-identical to Phase 1), then pushes ONE history entry holding
  // the full merged snapshot — but only when the snapshot actually changed (no-op guard). popstate
  // RESTORE is Phase 3. drillTask, createParcelFor and feedbackPrefill are not navigation dims and
  // keep their own setters.
  // opts.replace: overwrite the current history entry instead of adding one — for leaving an overlay whose job is
  // done (a saved Add form), so Back doesn't reopen the used form.
  const navigateTo = (patch, opts = {}) => {
    if ('page'          in patch) setActivePage(patch.page);
    if ('drillClient'   in patch) setDrillClient(patch.drillClient);
    if ('drillProperty' in patch) setDrillProperty(patch.drillProperty);
    if ('drillContact'  in patch) setDrillContact(patch.drillContact);
    if ('drillParcel'   in patch) setDrillParcel(patch.drillParcel);
    if ('modal'         in patch) setModal(patch.modal);

    // Merge patch over the latest snapshot (from the ref, never the closure).
    const prev = navStateRef.current;
    const next = {
      page:          'page'          in patch ? patch.page          : prev.page,
      drillClient:   'drillClient'   in patch ? patch.drillClient   : prev.drillClient,
      drillProperty: 'drillProperty' in patch ? patch.drillProperty : prev.drillProperty,
      drillContact:  'drillContact'  in patch ? patch.drillContact  : prev.drillContact,
      drillParcel:   'drillParcel'   in patch ? patch.drillParcel   : prev.drillParcel,
      modal:         'modal'         in patch ? patch.modal          : prev.modal,
    };
    navStateRef.current = next; // push ref ahead so back-to-back calls in one tick compose

    const a = toHist(prev).wh, b = toHist(next).wh;
    const changed = a.page!==b.page || a.client!==b.client || a.property!==b.property || a.contact!==b.contact || a.parcel!==b.parcel || a.modal!==b.modal;
    if (changed) {
      if (opts.replace) window.history.replaceState(toHist(next), '', buildUrl(toHist(next).wh));
      else window.history.pushState(toHist(next), '', buildUrl(toHist(next).wh));
    }
  };

  // Stamp entry-0 once on mount, from the SAME sources that render the first view:
  // page from ?page=/sessionStorage (:110 init), drills from the sessionStorage mirror (:119-127),
  // parcel null (:277) — all already reflected in navStateRef.current at first render. replaceState
  // (not push) labels the existing load entry so Back to the stack bottom has real state for Phase 3.
  // ⛔ A REPORT ROUTE WITH NO DEFINITION REDIRECTS TO THE PICKER. Never a dead-end string: a bare
  // "No such report." with no way out is not an acceptable terminal state anywhere in the app, and
  // it is what shipped — a persisted ?page=report restored without its reportId rendered exactly
  // that, on login, as the whole page body. The initializer now prevents the restore; this catches
  // every other route in (a hand-typed URL, an unregistered id, a definition removed later).
  // The report being shown, when this person may see it (an adminOnly card — Productivity — is admins' only).
  const openReport = getReport(reportId);
  const reportAllowed = !!openReport && (!openReport.adminOnly || isAdminStaff(staffUser));
  useEffect(() => {
    if (activePage !== 'report' || reportAllowed) return;
    setReportNotice(reportId ? 'That report is no longer available.' : 'Choose a report to open.');
    navigateTo({ page: 'reporting' });
    // eslint-disable-next-line
  }, [activePage, reportId, reportAllowed]);

  useEffect(() => {
    const s = navStateRef.current;
    const dl = deepLinkRef.current;
    const hasDeepLink = dl && (dl.client || dl.property || dl.parcel || dl.contact);
    // On a deep link, KEEP the URL params intact for the cold-load resolver below; otherwise mirror state → URL.
    window.history.replaceState(toHist(s), '', hasDeepLink ? window.location.href : buildUrl(toHist(s).wh));
  // eslint-disable-next-line
  }, []);

  // Phase 2b: cold-load resolver — a fresh/pasted deep link (URL has drill IDs, but sessionStorage didn't
  // supply the objects). Fetch the chain by ID, hand the rows to restoreSnapshot (the single resolver),
  // toast + strip on not-found. Runs once; in-session nav + same-tab refresh (sessionStorage) don't need it.
  useEffect(() => {
    const dl = deepLinkRef.current;
    if (!dl || (!dl.client && !dl.property && !dl.parcel && !dl.contact)) return;
    let cancelled = false;
    (async () => {
      const res = await resolveDeepLink(dl);
      if (cancelled) return;
      if (res.missing) {
        setDeepLinkMsg(`That ${res.missing} no longer exists.`);
        setTimeout(() => setDeepLinkMsg(''), 6000);
      }
      const snap = {
        page: activePage,
        client:   res.client?.id   ?? null,
        property: res.property?.id ?? null,
        parcel:   res.parcel?.id   ?? null,
        contact:  (res.client && dl.contact) ? dl.contact : null,
        modal: null,
      };
      restoreSnapshot(snap, res);
      // Finalize the URL — keep the resolved drills, strip any dead params.
      window.history.replaceState(toHist(navStateRef.current), '', buildUrl(toHist(navStateRef.current).wh));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, []);

  // Nav reset: the Clients nav item / dashboard Clients tile return to the All Clients list by
  // clearing stale drills. In-page drilling uses bare navigateTo and is unaffected.
  const goToClientsList = () => navigateTo({ page:'clients', drillClient:null, drillProperty:null, drillContact:null, drillParcel:null, modal:null });
  // Option A: a top-nav click always dismisses any open overlay (modal:null) alongside the page change.
  const handleNavChange = (page) => { if (page === 'clients') { goToClientsList(); } else { navigateTo({ page, modal: null }); } };
  const [expandedProps, setExpandedProps] = useState(() => new Set());
  const [contactModal, setContactModal] = useState(null);   // null | { contact, prefillPropertyId } — one create-or-edit ContactModal
  const [contactRefresh, setContactRefresh] = useState(0);  // bump to re-load the read-only Property Contacts card after a save
  const [addPropertyMsg, setAddPropertyMsg] = useState('');

  const loadClientData = useCallback(async (clientId, clientName) => {
    const { data: props } = await supabase.from('properties').select('*').eq('client_id', clientId).order('name');
    if (props && props.length > 0) {
      setProperties(props);
    } else if (clientName) {
      // fallback: try matching by generated client_id from name
      const generatedId = clientName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30);
      const { data: props2 } = await supabase.from('properties').select('*').eq('client_id', generatedId).order('name');
      if (props2 && props2.length > 0) {
        setProperties(props2);
      } else {
        const { data: clientParcels } = await supabase.from('parcels').select('*').eq('client_name', clientName);
        const masterIds = [...new Set((clientParcels||[]).map(p=>p.master_property_id).filter(Boolean))];
        const syntheticProps = masterIds.map(mid => {
          const sample = (clientParcels||[]).find(p=>p.master_property_id===mid);
          const propRecord = allProperties.find(p=>p.master_property_id===mid);
          return { id: String(mid), name: propRecord?.name||sample?.parcel_name||('Property #'+mid), master_property_id: mid, city: propRecord?.city||sample?.city||'', state: propRecord?.state||sample?.state||'', client_id: clientId, location_code: propRecord?.location_code||null, client_group: propRecord?.client_group||null, consultant_firm: propRecord?.consultant_firm||null, client_manager: propRecord?.client_manager||null };
        }).sort((a,b)=>a.name.localeCompare(b.name));
        setProperties(syntheticProps);
      }
    } else {
      setProperties([]);
    }
    const { data: users } = await supabase.from('client_portal_users').select('*, client_access_properties(property_id)').eq('client_id', clientId).order('name');
    setPortalUsers(users || []);
    const { data: fx } = await supabase.from('file_exchange').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
    setFiles(fx || []);
  }, []);

  const loadClientTasks = async (clientId, clientName, propertyId) => {
    if (propertyId) {
      const { data } = await supabase.from('tasks').select('*')
        .eq('property_id', propertyId)
        .not('status','in','("Complete","Cancelled")')
        .order('due_date', { ascending:true, nullsFirst:false });
      setPropertyTasks(data || []);
    }
  };

  useEffect(() => {
    async function loadStaffData() {
      if (!user) return; // ← FIXED: guard against undefined user on first render
      const { data: staff } = await supabase.from('ntrg_staff').select('*').eq('auth_user_id', user.id).single();
      if (staff) {
        setStaffName(staff.name);
        setStaffFirstName(staff.name.split(' ')[0]);
        setStaffUser(staff);

        const { data: myT } = await supabase.from('tasks')
          .select('*')
          .eq('assigned_to', staff.id)
          .is('deleted_at', null)          // delete is SOFT (TaskEngine:1080) — without this the card keeps deleted tasks
          .not('status','in','("Complete","Cancelled")')
          .order('due_date', { ascending: true, nullsFirst: false });
        setMyTasks(myT || []);

        const { data: todoRows } = await supabase.from('personal_todos')
          .select('*')
          .eq('owner_id', staff.id)
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: true });
        setPersonalTodos(todoRows || []);
      }
      const { data: cls } = await supabase.from('clients').select('*').order('name');
      if (cls && cls.length > 0) { setClients(cls); setAllClients(cls); /* Don't auto-select client */ }

      // ⛔ THE TWO head:true COUNTS WERE REMOVED WITH THEIR LAST READERS. Both existed only to print a
      // whole-table total above a YEAR-GATED list, which is the bug this change removes; every remaining
      // count is derived from the loaded arrays, so it agrees with the rows on screen by construction.
      // If a whole-table total is ever wanted again, label it as one — do not compare it to a filtered list.

      let allParcels = [];
      let from = 0;
      const batchSize = 1000;
      while (true) {
        const { data: batch } = await supabase.from('parcels').select('*').order('id').range(from, from + batchSize - 1);
        if (!batch || batch.length === 0) break;
        allParcels = allParcels.concat(batch);
        if (batch.length < batchSize) break;
        from += batchSize;
      }
      // ⛔ PAGINATE ON THE PRIMARY KEY, SORT HERE. This loop used to page on `.order('client_name')`, which is a
      //    NON-UNIQUE sort key — and offset pagination over a non-unique key is not stable. Rows sharing a
      //    client_name may be ordered differently between two page requests, so a row can come back on both sides
      //    of a boundary while another is skipped entirely: silent loss, no error, in the array that Parcels, the
      //    Clients search, both drills, PropertiesGrid and the nav-snapshot restore all read. `id` is unique, so
      //    every row appears exactly once. Sorting client-side is also free — client_name has no index, so each
      //    page made Postgres re-sort all ~3,800 rows. NULLs last, matching the ORDER BY ... ASC this replaces.
      allParcels.sort((a, b) => {
        if (a.client_name == null) return b.client_name == null ? 0 : 1;
        if (b.client_name == null) return -1;
        return a.client_name.localeCompare(b.client_name);
      });
      setParcels(allParcels);

      let allProps = [];
      from = 0;
      while (true) {
        const { data: batch } = await supabase.from('properties').select('id, name, client_id, city, state, master_property_id, location_code, client_group, consultant_firm, client_manager, appeal_deadline, services_re, admin_assigned, first_year, retired_year, reactivated_year').order('name').range(from, from + batchSize - 1);
        if (!batch || batch.length === 0) break;
        allProps = allProps.concat(batch);
        if (batch.length < batchSize) break;
        from += batchSize;
      }
      setAllProperties(allProps);
      // ⛔ AN EXPLICIT FLAG, NOT AN INFERENCE FROM THE LIST BEING EMPTY. PropertiesGrid used to print
      // "Loading properties..." for EVERY empty result, so a year that hid every row claimed the app was
      // still working. An empty array cannot tell you which it is, and no row count can: a whole-table
      // total would resolve "empty because nothing exists" but never "empty because the load is still
      // running". Only the load itself knows, so the load says so.
      // Set after the pagination loop completes, so a partial page never reads as loaded.
      setPropertiesLoaded(true);
    }
    loadStaffData();
    // Show welcome toast for 5 seconds
    setTimeout(() => setShowToast(false), 5000);
  }, [user, loadClientData]);

  // ⛔ appeal_status is PER VALUE YEAR — parcel_year_data.appeal_status. parcels.appeal_status is RETIRED
  // (kept, unwritten), so the Properties grid and its roll-ups must read the SELECTED year, or the list
  // shows a stale parcel-wide value while the parcel modal shows the year's. That split-brain is what this
  // change removes; 20 rows had already drifted for 2026.
  //
  // Its OWN effect, keyed on the year: the main load runs on [user] and would otherwise keep last year's
  // statuses after the working year changes. Keyed on parcels.length (not the array) so patching the rows
  // can't re-trigger it. A parcel with no row for the year reads Value Pending — a new appeal year starts
  // pending by definition.
  // The Properties list's Market / Taxable Value for the SELECTED year — each parcel's final value by the cards' and
  // reports' rule (utils/yearValues). It read parcel_values for the hardcoded TAX_YEAR, so imported years showed "—".
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    fetchAll('parcel_prorata', 'parcel_id,property_id,tax_year,pro_rata_pct', (q) => q.order('id')).then(
      (rows) => { if (alive) setProrataRows(rows); },
      (e) => console.warn('[properties] pro-rata read failed:', e?.message || e));
    return () => { alive = false; };
  }, [user, prorataReload]);

  useEffect(() => {
    if (!currentValueYear) return undefined;
    let alive = true;
    loadYearValues(currentValueYear).then(
      (m) => { if (alive) setYearValues(m); },
      (e) => console.warn('[properties] year values read failed:', e?.message || e));
    return () => { alive = false; };
  }, [currentValueYear]);

  useEffect(() => {
    if (!currentValueYear || parcels.length === 0) return;
    let alive = true;
    (async () => {
      const byParcel = new Map();
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase.from('parcel_year_data')
          .select('parcel_id, appeal_status, appeal_deadline, services')
          .eq('tax_year', String(currentValueYear))
          .range(from, from + 999);
        if (error) { console.warn('[parcel_year_data by year] read failed:', error.message); return; }
        (data || []).forEach(r => byParcel.set(r.parcel_id, r));
        if (!data || data.length < 1000) break;
      }
      if (!alive) return;
      const yr = String(currentValueYear);
      // ⛔ APPEAL DEADLINE AND SERVICES ARE YEAR-GRAINED AND WERE READING THE BASE ROW. parcels.appeal_deadline
      // and parcels.services are the PARCEL_BASE_MIRROR cache, which ParcelModal:1195 refreshes only when the
      // edited year IS the current year — so every parcels table showed the current year's figures whatever year
      // was selected. With a year selector that is worse than no selector: the year moves and the columns do not.
      //
      // ⛔ A MISSING YEAR ROW MEANS BLANK, NEVER THE BASE ROW. Falling back to parcels.* is exactly the bug.
      // Our data starts at 2021 and is still being loaded, so blanks are common now and will thin out; the
      // marker below says NOT RECORDED rather than not available, because this is a gap in what we hold, not a
      // limit on what we can show.
      //
      // _pydMissing is set from the ABSENCE OF A ROW, not from a null value — a recorded-but-empty deadline is a
      // different fact from no record at all, and only the second deserves the explanation.
      setParcels(prev => prev.map(p => {
        const row = byParcel.get(p.id);
        const status = (row && row.appeal_status) || APPEAL_STATUS.value_pending;
        const deadline = row ? (row.appeal_deadline ?? null) : null;
        const services = row ? (row.services ?? null) : null;
        const missing = !row;
        if (p.appeal_status === status && p.appeal_deadline === deadline && p.services === services
            && p._pydMissing === missing && p._pydYear === yr) return p;
        return { ...p, appeal_status: status, appeal_deadline: deadline, services, _pydMissing: missing, _pydYear: yr };
      }));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValueYear, parcels.length]);

    useEffect(() => {
    if (drillClient) {
      const clientId = clients.find(c=>c.name===drillClient.name)?.id || 
        drillClient.name.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'').slice(0,30);
      loadClientTasks(clientId, drillClient.name, null);
    }
  }, [drillClient]);

  useEffect(() => {
    if (drillProperty) {
      loadClientTasks(null, null, drillProperty.id);
      loadPropertyFiles(drillProperty);
    } else {
      setPropertyFiles([]);
    }
  }, [drillProperty]);

  useEffect(() => {
    if (drillContact) {
      supabase.from('contact_properties')
        .select('property_id, role, re_pp_scope, properties(id, name, master_property_id, location_code, city, state, consultant_firm)')
        .eq('contact_id', drillContact.id)
        .then(({ data }) => {
          const byProperty = new Map();
          (data || []).filter(r => r.properties).forEach(r => {
            if (!byProperty.has(r.property_id)) byProperty.set(r.property_id, { ...r.properties, roles: [] });
            byProperty.get(r.property_id).roles.push({ role: r.role, scope: r.re_pp_scope });
          });
          setContactProperties([...byProperty.values()]);
        });
    } else {
      setContactProperties([]);
    }
  }, [drillContact]);

  function getGreeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  }

  function getTodayString() {
    return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }

  function showRandomTip() {
    setCurrentTip(WIGLEY_TIPS[Math.floor(Math.random() * WIGLEY_TIPS.length)]);
    setShowTip(true);
    setTimeout(() => setShowTip(false), 6000);
  }

  // ⛔ NOT NAMED uploadFile. It was, which SHADOWED the imported storage helper of the same name, so the
  // `await uploadFile(filePath, file)` below called THIS function again instead of uploading — Exchange's send
  // never stored anything and its progress bar never finished.
  async function sendExchangeFile(file) {
    if (!file) return;
    const clientId = selectedProp?.client_id || exchClient?.id || selectedClient?.id;
    if (!clientId) return;
    // The type is resolved BEFORE the upload, from the folder table: no type, no upload (no orphaned object).
    const type = vocab?.type(exchFileType);
    if (!type) { alert('Pick a file type first.'); return; }
    const folder = vocab.folder(type.folder_key);
    setUploading(true); setUploadProgress(0);
    const progressTimer = setInterval(() => { setUploadProgress(p => p >= 90 ? 90 : p + Math.random() * 15 + 5); }, 150);
    const propPath = selectedProp ? selectedProp.id : 'client-level';
    const filePath = `${clientId}/${propPath}/${Date.now()}_${file.name}`;
    const { error } = await uploadFile(filePath, file);
    clearInterval(progressTimer); setUploadProgress(100);
    if (!error) {
      // filed_at/filed_by: this upload IS a filing — the staffer picked the client, the level and the type,
      // and the folder is stamped from them. Without it the row is indistinguishable from an untriaged intake
      // upload, and a client-level send never appears in the client's Documents (which lists filed rows only).
      const { error: insErr } = await supabase.from('file_exchange').insert({ client_id: clientId, property_id: selectedProp?.id || null, uploaded_by_name: staffName, direction: 'ntrg_to_client', file_name: file.name, file_type: type.legacy_name, type_key: type.key, file_size_bytes: file.size, storage_path: filePath, folder_name: folder.legacy_name, folder_key: folder.key, filed_by: staffName, filed_at: new Date().toISOString(), notify_email: true });
      if (insErr) { alert(`The file uploaded but wasn't recorded: ${insErr.message}`); setUploading(false); setUploadProgress(0); return; }
      const {data:fx} = await supabase.from('file_exchange').select('*').eq('client_id', clientId).order('created_at',{ascending:false});
      setFiles(fx||[]);
    }
    setTimeout(() => { setUploading(false); setUploadProgress(0); }, 800);
  }

  async function togglePropertyAssignment(userId, propertyId, isAssigned) {
    if (isAssigned) { await supabase.from('client_access_properties').delete().eq('user_id', userId).eq('property_id', propertyId); }
    else { const id = `cap-${userId}-${propertyId}`.slice(0, 50); await supabase.from('client_access_properties').insert({ id, user_id: userId, property_id: propertyId }); }
    // Reload portal users for current client
    if (usersClient) {
      const { data } = await supabase.from('client_portal_users').select('*, client_access_properties(property_id)').eq('client_id', usersClient.id).order('name');
      setPortalUsers(data || []);
    }
  }

  async function addPortalUser() {
    if (!usersClient || !newUser.name || !newUser.email) return;
    setAddingUser(true);
    // Build insert — only include columns that exist, add optional ones gracefully
    const insertData = {
      client_id: usersClient.id,
      name: newUser.name,
      email: newUser.email.toLowerCase().trim(),
    };
    // Try adding optional columns — they may not exist yet if SQL wasn't run
    try { insertData.title = newUser.title || null; } catch(e) {}
    try { insertData.role = newUser.is_primary ? 'admin' : 'user'; } catch(e) {}
    try { insertData.is_primary = newUser.is_primary; } catch(e) {}
    try { insertData.status = 'pending'; } catch(e) {}
    try { insertData.created_by = staffName; } catch(e) {}
    try { insertData.created_at = new Date().toISOString(); } catch(e) {}

    const { data, error } = await supabase.from('client_portal_users').insert(insertData).select().single();
    console.log('[addPortalUser] result:', data, error);
    if (error) {
      // Try minimal insert if full insert failed due to missing columns
      if (error.code === '42703') { // undefined column
        const { data: d2, error: e2 } = await supabase.from('client_portal_users').insert({
          client_id: usersClient.id,
          name: newUser.name,
          email: newUser.email.toLowerCase().trim(),
        }).select().single();
        console.log('[addPortalUser] minimal insert:', d2, e2);
        if (!e2 && d2) {
          setPortalUsers(prev => [...prev, { ...d2, client_access_properties: [] }]);
          setNewUser({ name:'', email:'', title:'', role:'user', is_primary:false });
          setShowAddUser(false);
          alert('User added. Note: run the SQL migrations to enable title, status, and role fields.');
        } else {
          alert(`Error adding user: ${e2?.message || 'Unknown error'}`);
        }
      } else {
        alert(`Error adding user: ${error?.message || 'Unknown error'}`);
      }
    } else if (data) {
      setPortalUsers(prev => [...prev, { ...data, client_access_properties: [] }]);
      setNewUser({ name:'', email:'', title:'', role:'user', is_primary:false });
      setShowAddUser(false);
    }
    setAddingUser(false);
  }

  async function archivePortalUser(userId) {
    if (!window.confirm('Archive this user? They will lose portal access immediately. Their property assignments will be cleared. This can be undone by reactivating them.')) return;
    await supabase.from('client_portal_users').update({ status: 'archived', archived_at: new Date().toISOString(), archived_by: staffName }).eq('id', userId);
    await supabase.from('client_access_properties').delete().eq('user_id', userId);
    setPortalUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'archived', client_access_properties: [] } : u));
  }

  async function reactivatePortalUser(userId) {
    await supabase.from('client_portal_users').update({ status: 'active', archived_at: null, archived_by: null }).eq('id', userId);
    setPortalUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'active' } : u));
  }

  async function addTodo(title) {
    const t = (title || '').trim(); if (!t) return;
    const { data } = await supabase.from('personal_todos')
      .insert({ owner_id: staffUser.id, title: t, sort_order: personalTodos.length })
      .select().single();
    if (data) setPersonalTodos(prev => [...prev, data]);
  }
  async function toggleTodo(id, done) {
    await supabase.from('personal_todos').update({ done: !done, updated_at: new Date().toISOString() }).eq('id', id);
    setPersonalTodos(prev => prev.map(t => t.id === id ? { ...t, done: !done } : t));
  }
  async function deleteTodo(id) {
    await supabase.from('personal_todos').delete().eq('id', id);
    setPersonalTodos(prev => prev.filter(t => t.id !== id));
  }

  function generateInviteMailto(user) {
    const subject = encodeURIComponent(`Your WigleyHub Portal Access — ${usersClient?.name || ''}`);
    const inviteUrl = `${window.location.origin}/portal/signup?email=${encodeURIComponent(user.email)}&client=${encodeURIComponent(usersClient?.id || '')}`;
    const body = encodeURIComponent(`Hi ${user.name.split(' ')[0]},\n\nYou've been invited to access the WigleyHub client portal for ${usersClient?.name || 'your account'}.\n\nClick the link below to set up your account:\n${inviteUrl}\n\nIf you have any questions, please don't hesitate to reach out.\n\nBest regards,\n${staffName}\nNational Tax Resource Group`);
    return `mailto:${user.email}?subject=${subject}&body=${body}`;
  }

  async function loadUsersForClient(cl) {
    if (!cl) { setPortalUsers([]); return; }
    const { data } = await supabase.from('client_portal_users').select('*, client_access_properties(property_id)').eq('client_id', cl.id).order('name');
    setPortalUsers(data || []);
    loadClientData(cl.id, cl.name);
  }

  function formatSize(bytes) { if (!bytes) return '—'; if (bytes < 1024*1024) return (bytes/1024).toFixed(1)+' KB'; return (bytes/(1024*1024)).toFixed(1)+' MB'; }
  function formatDate(dt) { if (!dt) return '—'; const d=new Date(dt); const diff=new Date()-d; if (diff<86400000) return 'Today'; if (diff<172800000) return 'Yesterday'; return d.toLocaleDateString('en-US',{month:'short',day:'numeric'}); }
  function fileIcon(name) { const ext=name?.split('.').pop()?.toLowerCase(); if (ext==='pdf') return '📄'; if (['xlsx','xls','csv'].includes(ext)) return '📊'; if (['docx','doc'].includes(ext)) return '📝'; return '📎'; }
  function deadlineBadge(deadline) {
    if (!deadline) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const d = new Date(deadline); d.setHours(0,0,0,0);
    const days = Math.ceil((d - today) / 86400000);
    const label = d.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
    if (days < 0) return <div style={{ display:'inline-flex', flexDirection:'column', gap:'1px' }}><span style={{ background:'rgba(248,113,113,0.12)', color:'#f87171', border:'1px solid rgba(248,113,113,0.3)', borderRadius:'20px', padding:'2px 8px', fontSize:'11px', fontWeight:'600' }}>⚠ Expired</span><span style={{ fontSize:'10px', color:'#f87171', paddingLeft:'4px' }}>{label}</span></div>;
    if (days <= 30) return <div style={{ display:'inline-flex', flexDirection:'column', gap:'1px' }}><span style={{ background:'rgba(251,191,36,0.12)', color:'#fbbf24', border:'1px solid rgba(251,191,36,0.3)', borderRadius:'20px', padding:'2px 8px', fontSize:'11px', fontWeight:'600' }}>🔥 Urgent · {days}d</span><span style={{ fontSize:'10px', color:'#fbbf24', paddingLeft:'4px' }}>{label}</span></div>;
    return <div style={{ display:'inline-flex', flexDirection:'column', gap:'1px' }}><span style={{ background:'rgba(52,211,153,0.12)', color:'#34d399', border:'1px solid rgba(52,211,153,0.3)', borderRadius:'20px', padding:'2px 8px', fontSize:'11px', fontWeight:'600' }}>✓ Active</span><span style={{ fontSize:'10px', color:'#34d399', paddingLeft:'4px' }}>{label}</span></div>;
  }

  const filteredProps = properties.filter(p => { if (!searchQ) return true; const q=searchQ.toLowerCase(); return p.name.toLowerCase().includes(q)||(p.location_code&&p.location_code.toLowerCase().includes(q))||(p.city&&p.city.toLowerCase().includes(q)); });
  // ⛔ first_year IS IN HERE BECAUSE SQL HAS ALWAYS PASSED IT. Every RPC gates on
  // is_represented(pr.first_year, pr.retired_year, pr.reactivated_year, year) — this map fed the JS mirror
  // of that predicate while carrying only the two retirement columns, so the client and the server
  // disagreed about the property half of the cascade on every surface. retirement.js already states the
  // rule the RPCs implement ("a parcel cannot be ours before its building is"; representationStart takes
  // the LATER of the two) — the field was simply never handed over. Renaming the map would be honest but
  // touches nine call sites for no behavioural gain; the comment carries it instead.
  const propRetireById = new Map((allProperties || []).map(p => [p.id, { first_year: p.first_year, retired_year: p.retired_year, reactivated_year: p.reactivated_year }]));
  // ⛔ THE CLIENTS PAGE COUNTED ALL TIME WHILE PROPERTIES COUNTED ACTIVE-ONLY, so one client read 25
  // parcels / 3 properties on one screen and 35 / 4 on the other. Same predicate as PropertiesGrid.js:90-95,
  // built on the same shared representedInYear primitive — that filter is a local const inside the grid and
  // is not exported, so this is the closest reuse available without moving it, which this task does not ask
  // for. Keep the two in step: a change to the grid's rule belongs here too.
  //   parcel live  = the parcel, judged against ITS property's retirement
  //   property live = the property itself, AND (no parcels OR at least one live parcel)
  const clientCountParcelLive = (p) => representedInYear(p, propRetireById.get(p.property_id), currentValueYear);
  const clientCountPropLive = (p) => representedInYear({ first_year: p.first_year, retired_year: p.retired_year, reactivated_year: p.reactivated_year }, null, currentValueYear)
    && (!parcels.some(par => par.property_id === p.id) || parcels.some(par => par.property_id === p.id && clientCountParcelLive(par)));
  const filteredParcels = parcels.filter(p => { const q=parcelSearch.toLowerCase(); const ms=!q||(p.parcel_name&&p.parcel_name.toLowerCase().includes(q))||(p.client_name&&p.client_name.toLowerCase().includes(q))||(p.assessor&&p.assessor.toLowerCase().includes(q))||(p.address&&p.address.toLowerCase().includes(q))||(p.master_property_id&&String(p.master_property_id).includes(q))||(p.account_number&&p.account_number.toLowerCase().includes(q)); const mc=!parcelClientFilter||p.client_name===parcelClientFilter; const mst=!parcelStateFilter||p.state===parcelStateFilter; const mr=showRetired||representedInYear(p, propRetireById.get(p.property_id), currentValueYear); return ms&&mc&&mst&&mr; });
  // ⛔ THE PARCELS PAGE PRINTED A DB-WIDE COUNT ABOVE A YEAR-GATED TABLE. filteredParcels gates on
  // representedInYear (the `mr` clause above), but the heading read "{parcelCount} parcels across all
  // clients" from a head:true COUNT over the whole table, and the second line read "Showing 3 of 3,795"
  // — which asserts that search hid the other 3,792 when the YEAR hid most of them. Not merely
  // ambiguous about being year-scoped, like the Properties page was: a number that contradicts its own
  // rows, and a comparison that names the wrong cause.
  //
  // Same split as PropertiesGrid: what is SHOWN is post-every-filter, while "in other years" is the year
  // alone — a search box has no bearing on where the rest of the portfolio is. showRetired is honoured
  // here exactly as the table honours it, so with it on the other-years clause correctly disappears.
  const parcelsInYear = parcels.filter(p => showRetired || representedInYear(p, propRetireById.get(p.property_id), currentValueYear));
  const parcelsOtherYears = parcels.length - parcelsInYear.length;
  // The earliest configured year holding any parcel, for the empty state. Same predicate as the table, so
  // the year offered cannot turn out to be empty too.
  const firstYearWithParcels = (parcels.length && !parcelsInYear.length)
    ? (yearRange.find(y => parcels.some(p => representedInYear(p, propRetireById.get(p.property_id), y))) || null)
    : null;
  const parcelStates = [...new Set(parcels.map(p=>p.state).filter(Boolean))].sort();
  const parcelClients = [...new Set(parcels.map(p=>p.client_name).filter(Boolean))].sort();
  const filteredClients = allClients.filter(c => { if (!showRetiredClients && c.status === 'Retired') return false; if (!clientSearch) return true; const q=clientSearch.toLowerCase(); if (c.name.toLowerCase().includes(q)) return true; const cp=parcels.filter(p=>p.client_name===c.name); return cp.some(p=>p.master_property_id&&String(p.master_property_id).startsWith(q.replace('#',''))); });
  // ⛔ TWO LISTS, DELIBERATELY. The drill listed retired parcels at EVERY year while PropertiesGrid:90 and
  // filteredParcels both gate on representedInYear — invisible until 70d366a made the year selectable, and
  // then simply wrong: pick 2021 and a parcel retired in 2022 still showed as if it were ours.
  //
  // The TABLE is gated (same predicate, same showRetired escape as the other two surfaces). The NOTES are
  // NOT: a retired parcel's notes are still that property's history, and hiding them would lose the record
  // of why it was retired at exactly the moment someone is looking for it. Per Philip: gate the table, not
  // the notes.
  const drillPropertyParcelsAll = drillProperty ? parcels.filter(p=>p.master_property_id===drillProperty.master_property_id) : [];
  // ⛔ PRO-RATA (Philip 2026-10-28): the table also lists the parcels of the client's OTHER properties that have a share in
  // this one in the year (utils/prorata), tagged with the share. Only the table: the counts, documents and notes below
  // stay this property's own parcels', which is where those records live.
  const drillSplits = drillProperty ? splitsForYear(prorataRows, parcels, currentValueYear) : new Map();
  const drillShareOf = (p) => (drillSplits.has(p.id) ? drillSplits.get(p.id).get(drillProperty.id) ?? 0 : null);
  const drillPropertyParcels = [...drillPropertyParcelsAll,
    ...parcels.filter(p => p.master_property_id !== drillProperty?.master_property_id && drillSplits.get(p.id)?.has(drillProperty.id))]
    .filter(p => showRetired || representedInYear(p, propRetireById.get(p.property_id), currentValueYear));
  // ⛔ THE EARLIEST YEAR A PARCEL IS ACTUALLY REPRESENTED, so an empty year can say where the data is
  // rather than only that it is not here. No query: parcels is loaded with select('*') so every row
  // already carries first_year/retired_year/reactivated_year, and propRetireById is already built for the
  // retirement filters. One pass over yearRange per render.
  //
  // ⛔ representedInYear, NOT yearState. The question is "where is the WORK", not "where may data go" —
  // pointing someone at a baseline year as though their parcels lived there would be a wrong answer
  // wearing a helpful shape. null when no year in the configured range qualifies; the message degrades
  // rather than printing undefined.
  const drillFirstRepYear = drillProperty
    ? (yearRange.find(y => drillPropertyParcelsAll.some(p =>
        representedInYear(p, propRetireById.get(p.property_id), y))) || null)
    : null;
  // What a year option on the property page says. ⛔ THE PROPERTY'S OWN STATE OUTRANKS ITS PARCELS: if the
  // building was not ours that year, that is the fact and no parcel can contradict it. Only when the
  // property IS ours do the parcels decide — because they are what a person acts on. The old control
  // reported the property ALONE while being read as "can I enter data here", and on prop-77396 the two
  // disagreed by a year, so the selector showed the half that was not the one being acted on.
  const drillYearOptionChip = (y) => {
    const prop = drillProperty
      ? { first_year: drillProperty.first_year, retired_year: drillProperty.retired_year, reactivated_year: drillProperty.reactivated_year }
      : null;
    const pc = yearChip(null, prop, y);
    if (pc.tag) return pc;                              // property closed or baseline — that is the answer
    if (!drillPropertyParcelsAll.length) return pc;      // no parcels at all; nothing to add
    const states = drillPropertyParcelsAll.map(p => yearState(p, propRetireById.get(p.property_id), y));
    if (states.includes('represented')) return { state: 'represented', tag: null, title: '' };
    if (states.includes('baseline')) return { state: 'baseline', tag: '·baseline',
      title: `Baseline year — no parcel here was represented in ${y}. Values and taxes can be entered as the client's figures from before we took this on.` };
    return { state: 'closed', tag: '·no parcels',
      title: `This property was ours in ${y}, but none of its parcels were — their representation starts later.` };
  };
  const drillClientParcels = drillClient ? parcels.filter(p=> {
    if (p.client_name === drillClient.name) return true;
    // Also try matching via allProperties client_id
    const matchingProp = allProperties.find(ap => ap.client_id === drillClient.id && ap.master_property_id === p.master_property_id);
    return !!matchingProp;
  }) : [];
  const drillClientMasterIds = [...new Set(drillClientParcels.map(p=>p.master_property_id).filter(Boolean))];
  const drillClientPropsFromDB = drillClient ? allProperties.filter(p=>drillClientMasterIds.includes(p.master_property_id)) : [];
  const drillClientPropsFromParcels = drillClientMasterIds.filter(mid=>!drillClientPropsFromDB.some(p=>p.master_property_id===mid)).map(mid=>{
    const sample=drillClientParcels.find(p=>p.master_property_id===mid);
    const propRecord=allProperties.find(p=>p.master_property_id===mid);
    const nm=propRecord?propRecord.name:sample?sample.parcel_name||('Property #'+mid):('Property #'+mid);
    return{id:String(mid),name:nm,master_property_id:mid,
      city:propRecord?propRecord.city||'':sample?sample.city||'':'',
      state:propRecord?propRecord.state||'':sample?sample.state||'':'',
      client_id:null,
      consultant_firm:propRecord?propRecord.consultant_firm||null:null,
      client_manager:propRecord?propRecord.client_manager||null:null,
      location_code:propRecord?propRecord.location_code||null:null,
      client_group:propRecord?propRecord.client_group||null:null};
  });
  // ⛔ THIRD SOURCE: properties linked DIRECTLY by client_id. The two sources above both start from the
  // client's PARCELS (master_property_id), so a client with no parcel rows saw "No properties found" no
  // matter how correctly properties.client_id was set — which is how prop-77396 was invisible under
  // RecNation. This only ADDS: anything the parcel-derived paths already produced is kept, and the union is
  // deduplicated by property id so a property reachable BOTH ways (the normal case — LaSalle's three) is
  // listed once.
  const drillClientPropsFromClientId = drillClient ? (allProperties || []).filter(p => p.client_id === drillClient.id) : [];
  const drillClientProperties=(() => {
    const seen = new Set();
    return [...drillClientPropsFromDB, ...drillClientPropsFromParcels, ...drillClientPropsFromClientId]
      .filter(p => { const k = p.id ?? `mpid:${p.master_property_id}`; if (seen.has(k)) return false; seen.add(k); return true; })
      .sort((a,b)=>a.name.localeCompare(b.name));
  })();
  // R2-S2/S3: All Contacts view rows — search filter THEN name sort (roleless contacts still included).
  const displayContacts = (() => {
    const q = contactSearch.trim().toLowerCase();
    const filtered = q
      ? clientContacts.filter(ct => {
          const name = `${ct.first_name || ''} ${ct.last_name || ''}`.toLowerCase();
          return name.includes(q) || (ct.title || '').toLowerCase().includes(q) || (ct.email || '').toLowerCase().includes(q) || (ct.phone || '').toLowerCase().includes(q);
        })
      : clientContacts;
    return [...filtered].sort((a, b) => {
      const an = `${a.first_name || ''} ${a.last_name || ''}`.trim();
      const bn = `${b.first_name || ''} ${b.last_name || ''}`.trim();
      return contactSortDir === 'asc' ? an.localeCompare(bn) : bn.localeCompare(an);
    });
  })();
  // R3: Property View rows — each property with its parcels' State and Assessor(s), then the search filter, then the
  // chosen column's sort. State and Assessor come from the property's parcels (the same parcels the Parcels count
  // counts); a property whose parcels have more than one assessor carries them all (shown as "Multiple", names on hover).
  const displayProperties = (() => {
    const rows = drillClientProperties.map(p => {
      const pp = parcels.filter(par => par.master_property_id === p.master_property_id);
      const distinct = (vals) => [...new Set(vals.map(v => String(v || '').trim()).filter(Boolean))].sort((x, y) => x.localeCompare(y));
      const states = distinct(pp.map(par => par.state));
      return { ...p, _parcelCount: pp.length, _states: states.length ? states : distinct([p.state]), _assessors: distinct(pp.map(par => par.assessor)) };
    });
    const q = propertySearch.trim().toLowerCase();
    const filtered = q
      ? rows.filter(p =>
          (p.name || '').toLowerCase().includes(q)
          || String(p.master_property_id || '').toLowerCase().includes(q)
          || (p.location_code || '').toLowerCase().includes(q)
          || (p.consultant_firm || '').toLowerCase().includes(q)
          || p._states.some(v => v.toLowerCase().includes(q))
          || p._assessors.some(v => v.toLowerCase().includes(q)))
      : rows;
    const sortVal = {
      name: p => p.name || '', mpid: p => Number(p.master_property_id) || 0, location: p => p.location_code || '',
      state: p => p._states.join(', '), assessor: p => p._assessors.join(', '), parcels: p => p._parcelCount,
      consultant: p => p.consultant_firm || '',
    }[propertySort.key];
    const dir = propertySort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const x = sortVal(a), y = sortVal(b);
      const cmp = typeof x === 'number' ? x - y : x.localeCompare(y);
      return (cmp || (a.name || '').localeCompare(b.name || '')) * dir;
    });
  })();

  const isDark = darkMode;

  // ── Primary 8-item navigation (per build whiteboard) ──────────────
  // Each item is a top-level page. Utilities and NTRG are section
  // containers: clicking them opens a bubble screen AND they expose a
  // submenu of children in the dropdown. `children` items with a
  // `soon` tag are not yet built (M4/M5) and render disabled.
  const navItems = [
    { id:'dashboard',   icon:'⊞', label:'Dashboard' },
    { id:'calendar',    icon:'📅', label:'Calendar' },
    { id:'clients',     icon:'🏦', label:'Clients' },
    { id:'consultants', icon:'🏛', label:'Consultants' },
    { id:'intake',      icon:'📥', label:'Document Intake' },
    { id:'documents',   icon:'🗂', label:'Documents' },
    { id:'properties',  icon:'🏢', label:'Properties' },
    { id:'utilities',   icon:'🧰', label:'Utilities', children:[
      { id:'users',      icon:'👥', label:'User Management' },
      { id:'assessors_collectors', icon:'🏛', label:'Assessors & Collectors' },
      { id:'tablemgmt',  icon:'🗃', label:'Table Management' },
      { id:'deliveries', icon:'📤', label:'Client Deliveries' },
      { id:'open_questions', icon:'❓', label:'Open Questions' },
      { id:'tax_savings_packet', icon:'💲', label:'Tax Savings Packet' },
      { id:'templates',  icon:'📑', label:'Templates', soon:'M4' },
      { id:'__moretools', divider:'More Tools' },
      { id:'parcels',    icon:'📋', label:'Parcels' },
      { id:'exchange',   icon:'📁', label:'File Exchange' },
      { id:'scanner',    icon:'🔍', label:'Document Scanner' },
      { id:'extractor',  icon:'📊', label:'Notice Extractor' },
      { id:'audit',      icon:'🧾', label:'Notice Audit' },
      { id:'tasks',      icon:'✅', label:'Task Engine' },
      { id:'activity',   icon:'📋', label:'Activity' },
      { id:'admin',      icon:'⚙️', label:'Admin Console' },
    ]},
    { id:'ntrg',        icon:'🏢', label:'NTRG', children:[
      { id:'ntrg_pto',        icon:'🌴', label:'PTO', soon:'M5' },
      { id:'ntrg_empdocs',    icon:'📄', label:'Employee Documents', soon:'M5' },
      { id:'ntrg_handbook',   icon:'📘', label:'Handbook', soon:'M5' },
      { id:'ntrg_commission',  icon:'💰', label:'Commission Schedule', soon:'M5' },
      { id:'ntrg_empinfo',    icon:'👤', label:'Employee Information' },
      { id:'ntrg_orgchart',   icon:'🗂', label:'Organizational Chart' },
    ]},
  ];

  // Flat list of every navigable child id → used to resolve the current
  // page's label in the dropdown button and to drive the section bubbles.
  const utilitiesChildren = navItems.find(n=>n.id==='utilities').children;
  const ntrgChildren       = navItems.find(n=>n.id==='ntrg').children;
  // Back-compat: some render code referenced adminNavItems for the
  // dashboard Utilities tile grid. Keep it pointing at the real,
  // already-built tools so the dashboard body is unchanged.
  const adminNavItems = [
    { id:'intake',    icon:'📥', label:'Document Intake' },
    { id:'exchange',  icon:'📁', label:'File Exchange' },
    { id:'scanner',   icon:'🔍', label:'Document Scanner' },
    { id:'users',     icon:'👥', label:'Manage Users' },
    { id:'tasks',     icon:'✅', label:'Task Engine' },
    { id:'extractor', icon:'📊', label:'Notice Extractor' },
    { id:'audit',     icon:'🧾', label:'Notice Audit' },
    { id:'documents', icon:'🗂', label:'Documents' },
    { id:'feedback',  icon:'💡', label:'Feedback' },
    { id:'admin',     icon:'⚙️', label:'Admin Console' },
  ];

  // ── Colours ──────────────────────────────────────────────
  const c = isDark ? {
    bg:       'var(--ink)',
    sidebar:  'linear-gradient(180deg,#1C1C1E,#1C1C1E)',
    sidebarBorder: '#374060',
    card:     'rgba(47,56,84,0.6)',
    cardBorder:'#374060',
    topbar:   'rgba(26,34,56,0.96)',
    text:     '#e8eaf2',
    muted:    'var(--slate)',
    subtext:  '#a8b0cc',
    input:    'var(--ink)',
    inputBorder:'#434C68',
    gold:     'var(--red)',
    goldDim:  'var(--red-dark)',
    activeNav:'rgba(180,0,0,0.1)',
    rowHover: 'rgba(255,255,255,0.03)',
    statCard: 'rgba(26,34,56,0.6)',
    th:       'rgba(26,34,56,0.5)',
    border:   '#374060',
  } : {
    bg:       'var(--bg)',
    sidebar:  'linear-gradient(180deg,#1C1C1E,#1C1C1E)',
    sidebarBorder: '#374060',
    card:     '#ffffff',
    cardBorder:'var(--line)',
    topbar:   'rgba(255,255,255,0.96)',
    text:     'var(--ink)',
    muted:    'var(--slate)',
    subtext:  '#4a5068',
    input:    '#ffffff',
    inputBorder:'#c8ccd8',
    gold:     'var(--red-dark)',
    goldDim:  'var(--red)',
    activeNav:'rgba(180,0,0,0.15)',
    rowHover: 'rgba(0,0,0,0.02)',
    statCard: '#ffffff',
    th:       'var(--line-soft)',
    border:   'var(--line)',
  };

  const s = {
    wrap:      { display:'flex', flexDirection:'column', minHeight:'100vh', background:c.bg, fontFamily:"'Public Sans',system-ui,sans-serif", transition:'background 0.2s' },
    main:      { flex:1, display:'flex', flexDirection:'column', minHeight:'100vh' },
    topbar:    { height:'54px', background:c.topbar, backdropFilter:'blur(12px)', borderBottom:`1px solid ${c.cardBorder}`, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 28px', position:'sticky', top:0, zIndex:10 },
    page:      { flex:1, padding:'24px 32px' },
    card:      { background:c.card, border:`1px solid ${c.cardBorder}`, borderRadius:'12px', overflow:'hidden', marginBottom:'18px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
    cardHeader:{ padding:'14px 18px', borderBottom:`1px solid ${c.cardBorder}`, display:'flex', justifyContent:'space-between', alignItems:'center' },
    cardTitle: { fontSize:'14px', fontWeight:'700', color:c.text },
    table:     { width:'100%', borderCollapse:'collapse' },
    th:        { padding:'9px 16px', textAlign:'left', fontSize:'11px', fontWeight:'700', color:c.muted, letterSpacing:'0.06em', textTransform:'uppercase', background:c.th },
    td:        { padding:'11px 16px', borderBottom:`1px solid ${isDark ? 'rgba(67,76,104,0.3)' : '#eef0f6'}`, fontSize:'14px', fontWeight:'500', color:c.text, verticalAlign:'middle' },
    input:     { width:'100%', padding:'9px 12px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'8px', color:c.text, fontSize:'14px', outline:'none', boxSizing:'border-box', fontFamily:"'Public Sans',system-ui,sans-serif" },
    select:    { padding:'8px 12px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'8px', color:c.text, fontSize:'14px', outline:'none', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btn:       { padding:'7px 14px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'7px', color: 'white', fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btnBack:   { padding:'6px 14px', background:'transparent', border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.subtext, fontSize:'13px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif", marginBottom:'16px', display:'inline-block' },
    btnSm:     { padding:'4px 10px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'5px', color: 'white', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    badge:     (color) => ({ background:`rgba(${color},0.12)`, color:`rgb(${color})`, borderRadius:'20px', padding:'3px 9px', fontSize:'12px', fontWeight:'600' }),
    statCard:  { background:c.statCard, border:`1px solid ${c.cardBorder}`, borderRadius:'12px', padding:'20px 24px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
  };

  // ── Column config: Properties page ──
  // Lifted out of the IIFE so useColumnPrefs (a hook) can take it as input.
  // The future column-manager UI just toggles `visible` and reorders these.
  // Keep `key` values stable so saved layouts in user_column_prefs keep
  // resolving across releases.
  const PROPERTY_COLUMNS = [
    // Flat option-A-revised columns. Each descriptor carries `type` (search/sort
    // behavior) and `value(p,r,ctx)` (raw scalar for filter/sort) alongside the
    // mockup-styled `render`. ctx = { clientName, clientObj } supplied by the grid.
    { key:'client',            label:'Client',            visible:true, align:'left',   type:'enum',
      value:(p, r, ctx) => ctx?.clientName || '',
      render:(p, r, ctx) => (
        <div style={{ display:'flex', alignItems:'flex-start', gap:'8px', fontWeight:'600', fontSize:'13px', color:'var(--charcoal)', whiteSpace:'normal', lineHeight:'1.3', wordBreak:'break-word' }}>
          <span>{ctx?.clientName || <span style={{ color:'#C4C4C9' }}>—</span>}</span>
        </div>
      ),
    },
    { key:'cm', label:'CM(s)', visible:true, align:'left', type:'text',
      value:(p) => roleNames(clientAssignments, ntrgStaff, p.client_id, 'CM'),
      render:(p) => { const t = roleNames(clientAssignments, ntrgStaff, p.client_id, 'CM'); return <span style={{ fontSize:'12px', color: t ? c.text : c.muted }}>{t || '—'}</span>; } },
    { key:'cs', label:'CS', visible:true, align:'left', type:'text',
      value:(p) => roleNames(clientAssignments, ntrgStaff, p.client_id, 'CS'),
      render:(p) => { const t = roleNames(clientAssignments, ntrgStaff, p.client_id, 'CS'); return <span style={{ fontSize:'12px', color: t ? c.text : c.muted }}>{t || '—'}</span>; } },
    { key:'ts', label:'TS', visible:true, align:'left', type:'text',
      value:(p) => tsText(clientAssignments, ntrgStaff, p.client_id),
      render:(p) => { const rows = rolesForClient(clientAssignments, p.client_id, 'TS'); return rows.length ? <TsAssignmentDisplay rows={rows} staffName={staffNameById} darkMode={darkMode} /> : <span style={{ fontSize:'12px', color:c.muted }}>—</span>; } },
    { key:'name',              label:'Property',          visible:true, align:'left',   type:'text',
      value:(p) => p.name || '',
      render:(p) => {
        const loc = [p.city, p.state].filter(Boolean).join(', ');
        return (
          <div>
            {/* ⛔ TWO LINES, NEVER "1..." (Philip 2026-10-27): the name wraps; past two lines it ends in … with the full name on hover. */}
            <div title={p.name} style={{ fontWeight:'700', color:'var(--ink)', fontSize:'14px', lineHeight:'1.3', whiteSpace:'normal', wordBreak:'break-word',
              display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{p.name}</div>
            {loc && <div style={{ fontSize:'12px', color:'var(--muted)', marginTop:'2px' }}>{loc}</div>}
          </div>
        );
      },
    },
    { key:'loc_code',          label:'Loc Code',          visible:true, align:'center', type:'text',
      value:(p) => p.location_code || '',
      render:(p) => p.location_code
        ? <span style={{ fontVariantNumeric:'tabular-nums', color:'var(--slate)', fontWeight:'500' }}>{p.location_code}</span>
        : <span style={{ color:'#C4C4C9' }}>—</span> },
    { key:'master_id',         label:'Master ID',         visible:true, align:'center', type:'text',
      value:(p) => p.master_property_id != null ? String(p.master_property_id) : '',
      render:(p) => p.master_property_id != null
        ? <span style={{ fontVariantNumeric:'tabular-nums', color:'var(--slate)', fontWeight:'500' }}>{`#${p.master_property_id}`}</span>
        : <span style={{ color:'#C4C4C9' }}>—</span> },
    { key:'parcels',           label:'Parcels',           visible:true, align:'center', type:'num',
      value:(p, r) => r.count,
      render:(p, r) => r.count > 0
        ? <span style={{ fontVariantNumeric:'tabular-nums', fontWeight:'600', color:'var(--charcoal)' }}>{r.count}</span>
        : <span style={{ color:'#C4C4C9' }}>—</span> },
    { key:'value_status',      label:'Value Status',      visible:true, align:'center', type:'enum',
      value:(p, r) => r.valueStatus,
      render:(p, r) => <span style={{ fontSize:'13px', fontWeight:'600', color: r.valueStatus === 'No Status' ? 'var(--muted)' : 'var(--charcoal)', whiteSpace:'nowrap' }}>{r.valueStatus}</span> },
    { key:'assessed',          label:'Market Value',      visible:true, align:'right',  type:'money',
      value:(p, r) => r.assessed,
      render:(p, r) => <span style={{ fontVariantNumeric:'tabular-nums', fontWeight:'700', color:'var(--ink)' }}>{fmtMoney(r.assessed)}</span> },
    { key:'taxable_value',     label:'Taxable Value',     visible:true, align:'right',  type:'money',
      value:(p, r) => r.taxableSum,
      render:(p, r) => <span style={{ fontVariantNumeric:'tabular-nums', fontWeight:'600', color:'var(--charcoal)' }}>{fmtMoney(r.taxableSum)}</span> },
    { key:'state',             label:'State',             visible:true, align:'center', type:'enum',
      value:(p, r) => r.state,
      render:(p, r) => <span style={{ fontWeight:'600', color:'var(--slate)', fontSize:'12px' }}>{r.state}</span> },
    { key:'earliest_deadline', label:'Earliest Deadline', visible:true, align:'center', type:'text',
      value:() => '',
      render:() => <span style={{ color:'#C4C4C9' }}>—</span> },
  ];
  const { columns: resolvedPropertyColumns, save: savePropertyColumns } = useColumnPrefs(
    'properties',
    PROPERTY_COLUMNS,
    staffUser?.id ? String(staffUser.id) : null
  );

  // ── Column config: Parcels page ──
  // 11 historical columns + Parcel Type (new, default hidden — was tooltip-only
  // before, so don't change anyone's current view). stopRowClick: true on RE/PP
  // mirrors the original td-level onClick={e.stopPropagation()} so clicking
  // anywhere in that cell does not open ParcelModal.
  const PARCELS_COLUMNS = [
    { key:'parcel_name',     label:'Parcel / Property',  visible:true,  align:'left',
      render:(p) => (
        <div>
          <div style={{ fontWeight:'600', color:'var(--red)', fontSize:'12px', wordBreak:'break-word' }}>{p.parcel_name || '—'}</div>
          {p.address && <div style={{ fontSize:'11px', color:c.muted, marginTop:'1px', wordBreak:'break-word' }}>{p.address}{p.city ? `, ${p.city}` : ''}</div>}
        </div>
      ),
    },
    { key:'status',          label:'Status',             visible:true,  align:'left',
      render:(p) => p.parcel_status
        ? <span style={{ fontSize:'12px', color:c.text }}>{p.parcel_status}</span>
        : <span style={{ color:c.muted, fontSize:'11px' }}>—</span>
    },
    { key:'account_number',  label:'Account #',          visible:true,  align:'left',
      render:(p) => <span style={{ fontFamily:'monospace', fontSize:'11px', color:c.subtext }}>{p.account_number || '—'}</span>
    },
    { key:'client',          label:'Client',             visible:true,  align:'left',
      render:(p) => <div style={{ whiteSpace:'normal', wordBreak:'break-word', fontSize:'12px', color:c.subtext }}>{p.client_name}</div>
    },
    { key:'master_id',       label:'Master ID',          visible:true,  align:'left',
      render:(p) => <span style={{ fontFamily:'monospace', fontSize:'11px', color:'var(--red)' }}>{p.master_property_id ? `#${p.master_property_id}` : '—'}</span>
    },
    { key:'re_pp',           label:'RE/PP',              visible:true,  align:'left', stopRowClick:true,
      render:(p) => (
        <div style={{ position:'relative', display:'inline-block' }}
          onMouseEnter={e => { const t = e.currentTarget.querySelector('.type-tip'); if (t) t.style.opacity = '1'; }}
          onMouseLeave={e => { const t = e.currentTarget.querySelector('.type-tip'); if (t) t.style.opacity = '0'; }}>
          <span style={{ fontSize: '14px', color: 'var(--charcoal)' }}>{p.re_pp === 'Real Estate' ? 'RE' : 'PP'}</span>
          {parcelTypeOf(p) && (
            <div className="type-tip" style={{ opacity:0, transition:'opacity 0.15s', position:'absolute', bottom:'calc(100% + 6px)', left:'50%', transform:'translateX(-50%)', background:'var(--ink)', color:'#e8eaf2', fontSize:'11px', fontWeight:'600', padding:'5px 10px', borderRadius:'7px', whiteSpace:'nowrap', zIndex:9999, pointerEvents:'none', boxShadow:'0 4px 12px rgba(0,0,0,0.4)' }}>{parcelTypeOf(p)}</div>
          )}
        </div>
      ),
    },
    { key:'assessor',        label:'Assessor',           visible:true,  align:'left',
      render:(p) => <div style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'140px', fontSize:'12px', color:c.subtext }}>{p.assessor || '—'}</div>
    },
    { key:'state',           label:'State',              visible:true,  align:'left',
      render:(p) => <span style={{ color:c.subtext, fontSize:'12px' }}>{p.state || '—'}</span>
    },
    { key:'appeal_deadline', label:'Appeal Deadline',    visible:true,  align:'left',
      render:(p) => deadlineBadge(parseLocalDate(p.appeal_deadline)) || blankYearCell(p)
    },
    { key:'services',        label:'Services',           visible:true,  align:'left',
      render:(p) => <div style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'160px', fontSize:'12px', color:c.muted }}>{p.services || blankYearCell(p)}</div>
    },
    { key:'admin',           label:'Admin',              visible:true,  align:'left',
      render:(p) => <span style={{ fontSize:'12px', color:c.subtext }}>{p.admin_assigned ? p.admin_assigned.split(',')[0] : '—'}</span>
    },
    { key:'parcel_type',     label:'Parcel Type',        visible:false, align:'left',
      render:(p) => <span style={{ fontSize:'12px', color:c.subtext }}>{parcelTypeOf(p) || '—'}</span>
    },
  ];
  const { columns: resolvedParcelsColumns, save: saveParcelsColumns } = useColumnPrefs(
    'parcels',
    PARCELS_COLUMNS,
    staffUser?.id ? String(staffUser.id) : null
  );

  // Property-drill parcels table. Same column-config shape as PARCELS_COLUMNS above, persisted
  // per staff under its own table_key. locked:true = the picker can't hide it — Assessor ID is the
  // only cell that identifies the row, so hiding it would leave an unreadable table.
  // CM/CS are keyed on the CLIENT, so they are identical on every row of one property by
  // construction; they stay here (hidable) rather than moving out, pending Philip's call.
  const PROPERTY_PARCELS_COLUMNS = [
    // nowrap: account numbers carry hyphens ('8940-436-171') and spaces
    // ('4343 019 027 21 000 73'), both of which the browser treats as break opportunities and
    // splits across two lines. No fixed width — nowrap makes the column's min-content width the
    // full string, so it sizes to the longest value present and the wrapper's overflowX:auto
    // handles the rest. A fixed width would fit 18 East and clip LaSalle.
    { key:'account_number',  label:'Assessor ID',     visible:true, align:'left', locked:true, nowrap:true,
      render:(p) => {
        const pct = drillShareOf(p);
        return <>
          <span style={{ fontFamily:'monospace', fontSize:'11px', fontWeight:'600', color:'var(--red)' }}>{p.account_number||'—'}</span>
          {pct != null && <span title={`Pro-rata: ${pct}% of this parcel counts toward this property in ${currentValueYear}`}
            style={{ marginLeft:'6px', fontSize:'10px', fontWeight:'700', color:'var(--slate)', border:'1px solid var(--line)', borderRadius:'var(--radius-pill)', padding:'0 6px' }}>Shared · {pct}%</span>}
        </>;
      }
    },
    { key:'re_pp',           label:'RE/PP',           visible:true, align:'left', stopRowClick:true,
      render:(p) => (
        <div style={{ position:'relative', display:'inline-block' }}
          onMouseEnter={e => { const t=e.currentTarget.querySelector('.type-tip'); if(t) t.style.opacity='1'; }}
          onMouseLeave={e => { const t=e.currentTarget.querySelector('.type-tip'); if(t) t.style.opacity='0'; }}>
          <span style={{ fontSize:'14px', color:'var(--charcoal)' }}>{p.re_pp==='Real Estate'?'RE':'PP'}</span>
          {parcelTypeOf(p) && <div className="type-tip" style={{ opacity:0, transition:'opacity 0.15s', position:'absolute', bottom:'calc(100% + 6px)', left:'50%', transform:'translateX(-50%)', background:'var(--ink)', color:'#e8eaf2', fontSize:'11px', fontWeight:'600', padding:'5px 10px', borderRadius:'7px', whiteSpace:'nowrap', zIndex:9999, pointerEvents:'none', boxShadow:'0 4px 12px rgba(0,0,0,0.4)' }}>{parcelTypeOf(p)}</div>}
        </div>
      ),
    },
    { key:'parcel_type',     label:'Type',            visible:true, align:'left',
      render:(p) => <span style={{ fontSize:'12px', color:c.subtext }}>{parcelTypeOf(p)||'—'}</span>
    },
    { key:'assessor',        label:'Assessor',        visible:true, align:'left',
      render:(p) => <span style={{ fontSize:'12px', color:c.subtext }}>{p.assessor||'—'}</span>
    },
    // Address only — the city used to be appended here, but parcels.address already carries the
    // full one-line address from the SigerTax import, so the append re-stated it and forced a wrap.
    { key:'address',         label:'Address',         visible:true, align:'left',
      render:(p) => <span style={{ fontSize:'12px', color:c.muted }}>{p.address||'—'}</span>
    },
    { key:'appeal_deadline', label:'Appeal Deadline', visible:true, align:'left',
      // parseLocalDate, not the bare string: deadlineBadge does new Date(x) internally, which parses
      // 'YYYY-MM-DD' as UTC midnight and renders a day EARLY west of Greenwich. Matches PARCELS_COLUMNS.
      render:(p) => deadlineBadge(parseLocalDate(p.appeal_deadline)) || blankYearCell(p)
    },
    { key:'services',        label:'Services',        visible:true, align:'left',
      render:(p) => <span style={{ fontSize:'12px', color:c.muted }}>{fmtServices(p.services) || blankYearCell(p)}</span>
    },
    { key:'cm',              label:'CM(s)',           visible:true, align:'left',
      render:() => { const t = cmDisplay(drillProperty?.client_id); return <span style={{ fontSize:'12px', color: t ? c.text : c.muted }}>{t || '—'}</span>; }
    },
    { key:'cs',              label:'CS',              visible:true, align:'left',
      render:() => { const t = csDisplay(drillProperty?.client_id); return <span style={{ fontSize:'12px', color: t ? c.text : c.muted }}>{t || '—'}</span>; }
    },
    { key:'ts',              label:'TS',              visible:true, align:'left',
      render:(p) => <ParcelTsDisplay assignments={clientAssignments} clientId={drillProperty?.client_id} parcel={p} staffName={staffNameById} darkMode={darkMode} />
    },
  ];
  const { columns: resolvedPropParcelCols, save: savePropParcelCols } = useColumnPrefs(
    'property_parcels',
    PROPERTY_PARCELS_COLUMNS,
    staffUser?.id ? String(staffUser.id) : null
  );

  async function generateUploadToken(clientId, clientName, forProperty = false) {
    setGeneratingToken(true);
    const expires = new Date();
    expires.setDate(expires.getDate() + 60);
    // Deactivate existing tokens at the same scope
    if (forProperty && drillProperty) {
      await supabase.from('upload_tokens').update({ active: false }).eq('property_id', drillProperty.id).eq('active', true);
    } else {
      await supabase.from('upload_tokens').update({ active: false }).eq('client_id', clientId).eq('active', true);
    }
    const { data, error } = await supabase.from('upload_tokens').insert({
      client_id: clientId,
      client_name: clientName,
      property_id: forProperty ? (drillProperty?.id || null) : null,
      property_name: forProperty ? (drillProperty?.name || null) : null,
      created_by: staffUser?.name || 'NTRG Staff',
      expires_at: expires.toISOString(),
      active: true,
    }).select().single();
    if (!error && data) {
      const url = `${window.location.origin}/upload/${data.token}`;
      setNewTokenUrl(url);
      navigateTo({ modal: 'genToken' });
      if (forProperty) setPropertyTokens(prev => [data, ...prev]);
      else setUploadTokens(prev => [data, ...prev]);
    }
    setGeneratingToken(false);
  }

  // ── Notifications (bell) — unread for the current staffer. Global (every page). Light 60s poll + refresh on action.
  const loadNotifications = useCallback(async () => {
    if (!staffUser?.id) return;
    const { data, error } = await supabase.from('notifications').select('*')
      .eq('recipient_id', staffUser.id).is('read_at', null)
      .order('created_at', { ascending: false }).limit(20);
    if (error) { console.warn('[notifications] load failed:', error.message); return; }
    setMyNotifications(data || []);
  }, [staffUser?.id]);
  useEffect(() => { loadNotifications(); const t = setInterval(loadNotifications, 60000); return () => clearInterval(t); }, [loadNotifications]);
  async function handleNotificationClick(n) {
    const { error } = await supabase.from('notifications').update({ read_at: new Date().toISOString() }).eq('id', n.id);
    if (error) console.warn('[notifications] mark-read failed:', error.message);   // RLS-off, but surface if it ever no-ops
    setMyNotifications(prev => prev.filter(x => x.id !== n.id));
    if (n.entity_type === 'task' && n.entity_id) {   // deep-link: fetch the full task, then open it in Task Engine
      const { data: task } = await supabase.from('tasks').select('*').eq('id', n.entity_id).is('deleted_at', null).maybeSingle();
      if (task) { setDrillTask(task); navigateTo({ page: 'tasks' }); }
    }
  }
  async function markAllNotificationsRead() {
    if (!staffUser?.id) return;
    const { error } = await supabase.from('notifications').update({ read_at: new Date().toISOString() }).eq('recipient_id', staffUser.id).is('read_at', null);
    if (error) { console.warn('[notifications] mark-all failed:', error.message); return; }
    setMyNotifications([]);
  }

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)' }}>
      <Header
        user={user}
        staffUser={staffUser}
        activePage={activePage}
        onActivePageChange={handleNavChange}
        onSignOut={onSignOut}
        notifications={myNotifications}
        onNotificationClick={handleNotificationClick}
        onMarkAllRead={markAllNotificationsRead}
      />
      <main className="page-container" style={(activePage === 'properties' || activePage === 'assessors_collectors' || activePage === 'taxbill_worklist' || activePage === 'taxbill_approvals' || activePage === 'taxbill_transmittals') ? { maxWidth: '1440px' } : undefined}>
        <div>

          {/* ── DASHBOARD ── */}
          {activePage==='dashboard' && (() => {
            const card = { background:'#FFFFFF', border:'1px solid #E6E6E9', borderRadius:'14px', boxShadow:'0 1px 2px rgba(20,20,30,.04), 0 4px 16px rgba(20,20,30,.05)', padding:'18px 20px' };
            const sectionLabel = { fontFamily:"'Archivo',sans-serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.13em', textTransform:'uppercase', color:'#8A8A8E', marginBottom:'12px' };
            const h3 = { fontFamily:"'Archivo',sans-serif", fontSize:'1rem', fontWeight:700, color:'#1C1C1E', marginBottom:'14px', display:'flex', alignItems:'center', gap:'9px' };
            const tag = { fontFamily:"'Public Sans',sans-serif", fontSize:'.78rem', fontWeight:800, background:'#B40000', color:'#fff', padding:'2px 11px', borderRadius:'999px' };
            const addBtn = { fontSize:'.78rem', color:'#B40000', fontWeight:700, cursor:'pointer', background:'#ffffff', border:'1px solid rgba(180,0,0,0.25)', borderRadius:'8px', padding:'4px 10px', fontFamily:"'Public Sans',sans-serif" };
            const row = { display:'flex', alignItems:'flex-start', gap:'11px', padding:'10px 0', borderBottom:'1px solid #F0F0F2' };
            const chk = { width:'18px', height:'18px', border:'2px solid #C9C9CE', borderRadius:'5px', flexShrink:0, marginTop:'1px', cursor:'pointer' };
            const tTitle = { fontSize:'.9rem', fontWeight:600, color:'#303030' };
            const tMeta = { fontSize:'.76rem', color:'#8A8A8E', marginTop:'2px' };
            const duePill = { fontSize:'.72rem', fontWeight:700, padding:'3px 9px', borderRadius:'999px', whiteSpace:'nowrap', flexShrink:0 };
            const dueSoon = { ...duePill, background:'#FBEAEA', color:'#9C0000' };
            const dueLater = { ...duePill, background:'#EFF1F3', color:'#606060' };
            const today = new Date(); today.setHours(0,0,0,0);
            const fmt = (d) => d.toLocaleDateString('en-US', { month:'short', day:'numeric' });
            return (
              <div style={{ maxWidth:'1180px', margin:'0 auto' }}>
                <div style={sectionLabel}>Your Tasks</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'30px' }}>

                  {/* Assigned to You */}
                  <div style={card}>
                    {/* Header toggles BOTH cards. Title, count and the action button stay visible collapsed;
                        the action stopsPropagation so "Add a task" doesn't also toggle. */}
                    <h3 style={{ ...h3, justifyContent:'space-between', cursor:'pointer' }} onClick={toggleDashTasks}
                        title={dashTasksCollapsed ? 'Show tasks' : 'Hide tasks'}>
                      <span style={{ display:'flex', alignItems:'center', gap:'9px' }}>
                        <span style={{ fontSize:'11px', color:'#8A8A8E' }}>{dashTasksCollapsed ? '▸' : '▾'}</span>
                        Assigned to You <span style={tag}>{myTasks.length}</span>
                      </span>
                      <span style={addBtn} onClick={(e) => { e.stopPropagation(); setDrillTask(null); navigateTo({ page:'tasks' }); }}>Add a task</span>
                    </h3>
                    {!dashTasksCollapsed && (myTasks.length === 0 ? (
                      <div style={{ ...tMeta, padding:'10px 0' }}>No tasks assigned to you.</div>
                    ) : myTasks.map((task, i) => {
                      const due = parseLocalDate(task.due_date);
                      const soon = due && due <= today;
                      const label = !due ? null : (due.getTime() === today.getTime() ? 'Due today' : soon ? 'Due ' + fmt(due) : fmt(due));
                      const meta = [task.client_name, task.property_name].filter(Boolean).join(' · ');
                      return (
                        <div key={task.id} style={{ ...row, borderBottom: i === myTasks.length - 1 ? 'none' : row.borderBottom, cursor:'pointer' }}
                             onClick={() => { setDrillTask(task); navigateTo({ page:'tasks' }); }}>
                          <div style={chk} />
                          <div style={{ flex:1 }}>
                            <div style={tTitle}>{task.title}</div>
                            {meta && <div style={tMeta}>{meta}</div>}
                          </div>
                          {label && <span style={soon ? dueSoon : dueLater}>{label}</span>}
                        </div>
                      );
                    }))}
                  </div>

                  {/* Personal To-Dos */}
                  <div style={card}>
                    <h3 style={{ ...h3, justifyContent:'space-between', cursor:'pointer' }} onClick={toggleDashTasks}
                        title={dashTasksCollapsed ? 'Show to-dos' : 'Hide to-dos'}>
                      <span style={{ display:'flex', alignItems:'center', gap:'9px' }}>
                        <span style={{ fontSize:'11px', color:'#8A8A8E' }}>{dashTasksCollapsed ? '▸' : '▾'}</span>
                        Personal To-Dos <span style={tag}>{personalTodos.length}</span>
                      </span>
                      <span style={addBtn} onClick={(e) => { e.stopPropagation(); const t = window.prompt('New to-do:'); if (t) addTodo(t); }}>Add a to-do</span>
                    </h3>
                    {!dashTasksCollapsed && personalTodos.length === 0 && (
                      <div style={{ ...tMeta, padding:'10px 0' }}>No personal to-dos yet.</div>
                    )}
                    {!dashTasksCollapsed && personalTodos.map((todo, i) => (
                      <div key={todo.id} style={{ ...row, borderBottom: i === personalTodos.length - 1 ? 'none' : row.borderBottom }}>
                        <div style={{ ...chk, background: todo.done ? '#B40000' : 'transparent', borderColor: todo.done ? '#B40000' : '#C9C9CE', display:'flex', alignItems:'center', justifyContent:'center' }}
                             onClick={() => toggleTodo(todo.id, todo.done)}>
                          {todo.done && <span style={{ color:'#fff', fontSize:'12px', lineHeight:1 }}>{'✓'}</span>}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ ...tTitle, textDecoration: todo.done ? 'line-through' : 'none', color: todo.done ? '#8A8A8E' : '#303030' }}>{todo.title}</div>
                        </div>
                        <span onClick={() => deleteTodo(todo.id)} title="Delete"
                              style={{ cursor:'pointer', color:'#C9C9CE', fontSize:'14px', lineHeight:1, padding:'2px 4px' }}>{'✕'}</span>
                      </div>
                    ))}
                  </div>

                </div>
                {(() => {
                  const grid = { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'14px', maxWidth:'1180px', margin:'0 auto' };
                  const bubble = { background:'#FFFFFF', border:'1.5px solid #E6E6E9', borderRadius:'14px', minHeight:'92px', padding:'14px 12px', position:'relative', display:'flex', alignItems:'flex-start', justifyContent:'center', textAlign:'center', cursor:'pointer', transition:'.15s', boxShadow:'0 1px 2px rgba(20,20,30,.03)' };
                  const bubbleSoon = { ...bubble, border:'1.5px dashed #D0D0D5', background:'#FAFAFB', cursor:'default' };
                  const stackCell = { display:'flex', flexDirection:'row', gap:'10px', minHeight:'88px' };
                  const bName = { fontFamily:"'Archivo',sans-serif", fontWeight:700, fontSize:'.92rem', color:'#1C1C1E', lineHeight:1.15, position:'absolute', top:'36%', left:0, right:0, transform:'translateY(-50%)', padding:'0 12px' };
                  const bNameSoon = { ...bName, color:'#A8A8AE' };
                  const soonTag = { position:'absolute', left:'50%', top:'72%', transform:'translate(-50%,-50%)', fontSize:'.58rem', fontWeight:700, letterSpacing:'.08em', color:'#B8B8BE', textTransform:'uppercase' };
                  const hoverIn = (e) => { e.currentTarget.style.borderColor = '#B40000'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(180,0,0,.14)'; e.currentTarget.style.transform = 'translateY(-1px)'; };
                  const hoverOut = (e) => { e.currentTarget.style.borderColor = '#E6E6E9'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(20,20,30,.03)'; e.currentTarget.style.transform = 'none'; };
                  const live = (name, onClick) => (
                    <div style={bubble} onClick={onClick} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                      <div style={bName}>{name}</div>
                    </div>
                  );
                  // Same round red/white badge as the top-of-page counters (tag), centered under the title at the
                  // stub bubbles' "Coming soon" vertical spot.
                  const countCenter = { ...tag, position:'absolute', left:'50%', top:'72%', transform:'translate(-50%,-50%)' };
                  const liveCount = (name, count, onClick) => (
                    <div style={bubble} onClick={onClick} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                      <div style={bName}>{name}</div>
                      {count > 0 && <span style={countCenter}>{count}</span>}
                    </div>
                  );
                  const soon = (name, stk) => (
                    <div style={stk ? { ...bubbleSoon, flex:1, minHeight:'88px', padding:'14px 8px' } : bubbleSoon}>
                      <div style={stk ? { ...bNameSoon, fontSize:'.8rem' } : bNameSoon}>{name}</div>
                      <span style={soonTag}>Coming soon</span>
                    </div>
                  );
                  return (
                    <>
                      <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.13em', textTransform:'uppercase', color:'#8A8A8E', marginBottom:'12px' }}>Your Workflow</div>
                      <div style={grid}>
                        {soon('Authorizations')}
                        {soon('Info Request')}
                        <div style={stackCell}>{soon('BPP Compliance', true)}{soon('RE Compliance', true)}</div>
                        <div style={stackCell}>{soon('BPP Notices', true)}{soon('RE Notices', true)}</div>
                        {soon('Under Review')}
                        {soon('Open Appeals')}
                        {soon('Results')}
                        {soon('CMAP')}
                        {soon('Accounting')}
                        {live('Live Reporting', () => navigateTo({ page:'reporting' }))}
                        {liveCount('Tax Bill Processing', tsBillCount, () => navigateTo({ page:'taxbill_worklist' }))}
                        {liveCount('Tax Bill Approvals', csBillCount, () => navigateTo({ page:'taxbill_approvals' }))}
                        {liveCount('Tax Bill Transmittals', transmitBillCount, () => navigateTo({ page:'taxbill_transmittals' }))}
                        {soon('Refunds')}
                        {soon('Budget')}
                        {soon('PAR')}
                        {live('Document Intake', () => navigateTo({ page:'intake' }))}
                        {live('Clients', goToClientsList)}
                        {soon('Proposals')}
                      </div>
                    </>
                  );
                })()}
                {/* Greeting toast -> Stage 2c. */}
              </div>
            );
          })()}

          {/* CLIENTS L1 */}
          {activePage==='clients' && !drillClient && (
            <div style={{ width:'100%' }}>
              <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'6px' }}>Clients</div>
              <div style={{ fontSize:'13px', color:c.muted, marginBottom:'22px' }}>{allClients.length} clients in WigleyHub</div>
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.cardTitle}>All Clients</div>
                  <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                    <div style={{ fontSize:'12px', color:c.muted }}>{filteredClients.length} of {allClients.length}</div>
                    <button onClick={() => {
                      const rows = filteredClients.map(cl => {
                        const clientProps = allProperties.filter(p=>p.client_id===cl.id).filter(clientCountPropLive);
                        const mids = [...new Set(clientProps.map(p=>p.master_property_id).filter(Boolean))];
                        const cp = parcels.filter(p=>mids.includes(p.master_property_id)).filter(clientCountParcelLive);
                        return { name: cl.name, manager: cmDisplay(cl.id), specialist: csDisplay(cl.id), tax: tsDisplayText(cl.id), properties: mids.length, parcels: cp.length };
                      });
                      const d = new Date();
                      const stamp = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                      exportToCsv(`clients-${stamp}.csv`, [
                        { key:'name', label:'Client Name' },
                        { key:'manager', label:'Client Manager(s)' },
                        { key:'specialist', label:'Client Specialist(s)' },
                        { key:'tax', label:'Tax Specialist(s)' },
                        { key:'properties', label:'Properties' },
                        { key:'parcels', label:'Parcels' },
                      ], rows);
                    }} style={{ padding:'6px 12px', background:'transparent', border:`1px solid ${c.border}`, borderRadius:'8px', color:c.subtext, fontSize:'12px', fontWeight:'600', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif", whiteSpace:'nowrap' }}>Export CSV</button>
                  </div>
                </div>
                <div style={{ padding:'12px 12px 0', display:'flex', alignItems:'center', gap:'10px' }}>
                  <input style={{ ...s.input, flex:1 }} placeholder="Search clients by name or master property ID..." value={clientSearch} onChange={e=>setClientSearch(e.target.value)} />
                  <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:c.subtext, cursor:'pointer', whiteSpace:'nowrap', padding:'0 8px' }}>
                    <input type="checkbox" checked={showRetiredClients} onChange={e=>setShowRetiredClients(e.target.checked)} style={{ cursor:'pointer' }} />
                    Show retired
                  </label>
                </div>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>Client Name</th><th style={s.th}>Client Manager(s)</th><th style={s.th}>Client Specialist(s)</th><th style={s.th}>Tax Specialist(s)</th><th style={{ ...s.th, textAlign:'center' }}>Properties<div style={{ fontSize:'9px', fontWeight:'600', color:c.muted, textTransform:'none', letterSpacing:0 }}>{currentValueYear}</div></th><th style={{ ...s.th, textAlign:'center' }}>Parcels<div style={{ fontSize:'9px', fontWeight:'600', color:c.muted, textTransform:'none', letterSpacing:0 }}>{currentValueYear}</div></th></tr></thead>
                  <tbody>
                    {filteredClients.map(cl => {
                      const clientProps = allProperties.filter(p=>p.client_id===cl.id).filter(clientCountPropLive);
const mids=[...new Set(clientProps.map(p=>p.master_property_id).filter(Boolean))];
const cp=parcels.filter(p=>mids.includes(p.master_property_id)).filter(clientCountParcelLive);
                      return (
                        <tr key={cl.id} style={{ cursor:'pointer', opacity: cl.status === 'Retired' ? 0.6 : 1 }} onClick={()=>navigateTo({ drillClient:cl })}
                          onMouseEnter={e=>e.currentTarget.style.background=c.rowHover}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <td style={s.td}><div style={{ fontWeight:'600', color:'var(--red)' }}>{cl.name}</div></td>
                          <td style={s.td}><span style={{ color: cmDisplay(cl.id) ? c.text : c.muted, fontSize:'12px' }}>{cmDisplay(cl.id) || '—'}</span></td>
                          <td style={s.td}><span style={{ color: csDisplay(cl.id) ? c.text : c.muted, fontSize:'12px' }}>{csDisplay(cl.id) || '—'}</span></td>
                          <td style={s.td}><TsAssignmentDisplay rows={asgRows(cl.id,'TS')} staffName={staffNameById} darkMode={darkMode} /></td>
                          <td style={{ ...s.td, textAlign:'center' }}><span style={{ color:'var(--charcoal)' }}>{mids.length}</span></td>
                          <td style={{ ...s.td, textAlign:'center' }}><span style={{ color:'var(--charcoal)' }}>{cp.length}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CLIENTS L2 */}
          {activePage==='clients' && drillClient && !drillProperty && !drillContact && (
            <div style={{ width:'100%' }}>
              <button style={s.btnBack} onClick={()=>navigateTo({ drillClient:null })}>← Back to Clients</button>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div>
                  <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'4px' }}>{drillClient.name}</div>
                  {/* client_since_year is the stated year (migration 2026-10-08). Blank shows NOTHING — never a year
                      derived from contract_start_date or a placeholder. */}
                  <div style={{ fontSize:'13px', color:c.muted, marginBottom:'22px', minHeight:'1px' }}>{drillClient.client_since_year ? `Client since ${drillClient.client_since_year}` : ''}</div>
                </div>
                <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={() => navigateTo({ modal: 'editClient' })}
                  style={{ padding:'8px 14px', background:'transparent', border:`1px solid ${c.border}`, borderRadius:'8px', color:c.subtext, fontSize:'12px', fontWeight:'600', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif", whiteSpace:'nowrap' }}>
                  Edit Client
                </button>
                <button onClick={async () => {
                  const retiring = drillClient.status !== 'Retired';
                  const ok = window.confirm(retiring ? `Retire ${drillClient.name}? They'll be hidden from the client list.` : `Unretire ${drillClient.name}?`);
                  if (!ok) return;
                  // ⛔ 'Active', NOT 'active'. This wrote lowercase, and it is the ONLY writer in the
                  // codebase that did — creation, bulk import and the consultant equivalent all write
                  // 'Active'. A client that was retired and then unretired came back with a status no
                  // other value matched, which painted it red in Admin Console for the rest of its life.
                  const newStatus = retiring ? 'Retired' : 'Active';
                  const { error } = await supabase.from('clients').update({ status: newStatus }).eq('id', drillClient.id);
                  if (error) { window.alert('Update failed: ' + error.message); return; }
                  setAllClients(prev => prev.map(x => x.id === drillClient.id ? { ...x, status: newStatus } : x));
                  setClients(prev => prev.map(x => x.id === drillClient.id ? { ...x, status: newStatus } : x));
                  navigateTo({ drillClient: retiring ? null : { ...drillClient, status: newStatus } });
                }} style={{ padding:'8px 14px', background:'transparent', border:`1px solid ${c.border}`, borderRadius:'8px', color:c.subtext, fontSize:'12px', fontWeight:'600', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif", whiteSpace:'nowrap' }}>
                  {drillClient.status === 'Retired' ? 'Unretire Client' : 'Retire Client'}
                </button>
                </div>
              </div>

              {/* Client Contacts */}
              <div style={{ ...s.card, marginBottom:'16px' }}>
                <div style={s.cardHeader}>
                  <div style={s.cardTitle}>👤 Client Contacts</div>
                  <button style={s.btn} onClick={() => { setContactModal({ contact: null, prefillPropertyId: null }); navigateTo({ modal: 'contact' }); }}>+ Add Contact</button>
                </div>
                <div style={{ display:'flex', gap:'24px', padding:'14px 18px' }}>
                <div style={{ flex:1, display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gridTemplateRows:'repeat(2, auto)', gridAutoFlow:'column', gap:'14px 24px' }}>
                  {(() => {
                    const hdr = { fontSize:'10px', fontWeight:'700', letterSpacing:'.08em', textTransform:'uppercase', color:'#303030', marginBottom:'6px' };
                    const naStyle = { fontSize:'12px', color:c.muted, fontStyle:'italic' };
                    const primary = clientContacts.find(c => (c.client_contact_roles || []).some(r => r.role === 'primary'));
                    return (
                      <>
                        <div>
                          <div style={hdr}>Primary Contact</div>
                          {primary ? (
                            <div onClick={() => { setContactModal({ contact: primary, prefillPropertyId: null }); navigateTo({ modal: 'contact' }); }} style={{ fontSize:'13px', color:c.text, cursor:'pointer' }}>
                              {primary.first_name} {primary.last_name}
                              {primary.title && <span style={{ color:c.muted }}> · {primary.title}</span>}
                              <span style={{ color:c.muted }}> · {primary.email}</span>
                              {primary.phone && <span style={{ color:c.muted }}> · {formatPhone(primary.phone)}</span>}
                            </div>
                          ) : <div style={naStyle}>Not assigned</div>}
                        </div>
                        <div>
                          <div style={hdr}>Client Manager(s)</div>
                          <div style={{ fontSize:'13px', color: cmDisplay(drillClient.id) ? c.text : c.muted, fontStyle: cmDisplay(drillClient.id) ? 'normal' : 'italic' }}>
                            {cmDisplay(drillClient.id) || 'Not assigned'}
                          </div>
                        </div>
                        <div>
                          <div style={hdr}>Client Specialist</div>
                          <div style={{ fontSize:'13px', color: csDisplay(drillClient.id) ? c.text : c.muted, fontStyle: csDisplay(drillClient.id) ? 'normal' : 'italic' }}>
                            {csDisplay(drillClient.id) || 'Not assigned'}
                          </div>
                        </div>
                        <div>
                          <div style={hdr}>Tax Specialist</div>
                          <div style={{ fontSize:'13px' }}>
                            {asgRows(drillClient.id,'TS').length
                              ? <TsAssignmentDisplay rows={asgRows(drillClient.id,'TS')} staffName={staffNameById} darkMode={darkMode} />
                              : <span style={naStyle}>Not assigned</span>}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div style={{ width:'220px', borderLeft:`1px solid ${c.cardBorder}`, paddingLeft:'18px' }}>
                  <div style={{ fontSize:'10px', fontWeight:'700', letterSpacing:'.08em', textTransform:'uppercase', color:'#303030', marginBottom:'6px' }}>Address</div>
                  {(() => {
                    const { address_line1, address_line2, city, state, zip } = drillClient;
                    const cityLine = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                    if (!(address_line1 || address_line2 || city || state || zip)) return <div style={{ fontSize:'12px', color:c.muted, fontStyle:'italic' }}>—</div>;
                    return (
                      <div style={{ fontSize:'12px', color:c.text, lineHeight:1.5 }}>
                        {address_line1 && <div>{address_line1}</div>}
                        {address_line2 && <div>{address_line2}</div>}
                        {cityLine && <div>{cityLine}</div>}
                      </div>
                    );
                  })()}
                </div>
                </div>
              </div>

              {/* Detail tabs (Property View / All Contacts View) */}
              <div style={{ display:'flex', gap:'8px', marginBottom:'16px', borderBottom:`1px solid ${c.cardBorder}` }}>
                {[{ key:'properties', label:'Property View' }, { key:'contacts', label:'All Contacts View' }].map(tab => {
                  const on = clientDetailTab === tab.key;
                  return (
                    <button key={tab.key} type="button" onClick={() => setClientDetailTab(tab.key)}
                      style={{ padding:'8px 16px', background:'transparent', border:'none', borderBottom:`2px solid ${on ? 'var(--red)' : 'transparent'}`, color: on ? 'var(--red)' : c.muted, fontSize:'13px', fontWeight: on ? '700' : '600', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" }}>
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              {clientDetailTab === 'contacts' ? (
                <div style={{ ...s.card, marginBottom:'16px' }}>
                  <div style={{ padding:'12px 12px 0' }}>
                    <input style={{ ...s.input }} placeholder="Search contacts by name, title, email, or phone..." value={contactSearch} onChange={e=>setContactSearch(e.target.value)} />
                  </div>
                  <table style={s.table}>
                    <thead><tr>
                      <th style={{ ...s.th, cursor:'pointer', userSelect:'none' }} onClick={() => setContactSortDir(d => d === 'asc' ? 'desc' : 'asc')}>Name {contactSortDir === 'asc' ? '▲' : '▼'}</th>
                      <th style={s.th}>Title</th>
                      <th style={s.th}>Email</th>
                      <th style={s.th}>Phone</th>
                      <th style={s.th}>Roles</th>
                    </tr></thead>
                    <tbody>
                      {displayContacts.length === 0 ? (
                        <tr><td colSpan="5" style={{ ...s.td, textAlign:'center', color:c.muted }}>{clientContacts.length === 0 ? 'No contacts for this client yet.' : 'No contacts match your search.'}</td></tr>
                      ) : displayContacts.map(ct => (
                        <tr key={ct.id}
                          onMouseEnter={e=>e.currentTarget.style.background=c.rowHover}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <td style={{ ...s.td, cursor:'pointer' }} onClick={() => navigateTo({ drillProperty:null, drillContact:ct })}>
                            <span style={{ fontWeight:'600', color:'var(--red)' }}>{ct.first_name} {ct.last_name}</span>
                          </td>
                          <td style={{ ...s.td, color:c.subtext }}>{ct.title || '—'}</td>
                          <td style={{ ...s.td, color:c.subtext }}>{ct.email || '—'}</td>
                          <td style={{ ...s.td, color:c.subtext }}>{ct.phone ? formatPhone(ct.phone) : '—'}</td>
                          <td style={s.td}>
                            {(() => {
                              // One contact can hold several roles at one property AND roles across several
                              // properties, so the set is flattened and DEDUPLICATED by role — the column
                              // answers "what does this person do for this client", not "where".
                              const roles = [...new Set((ct.contact_properties || []).map(r => r.role).filter(Boolean))];
                              return roles.length === 0
                                ? <span style={{ color:c.muted }}>—</span>
                                : (
                                  <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                                    {roles.map(role => (
                                      <span key={role} title={contactRoleLabel(role)} style={s.badge('96,165,250')}>{contactRoleLabel(role)}</span>
                                    ))}
                                  </div>
                                );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ ...s.card, marginBottom:'16px' }}>
                  <div style={{ padding:'12px 12px 0' }}>
                    <input style={{ ...s.input }} placeholder="Search properties by name, MPID, location ID, state, assessor, or consultant..." value={propertySearch} onChange={e=>setPropertySearch(e.target.value)} />
                  </div>
                  <table style={s.table}>
                    <thead><tr>
                      {[['name','Property'], ['mpid','MPID','center'], ['location','Location ID','center'], ['state','State','center'],
                        ['assessor','Assessor'], ['parcels','Parcels','center'], ['consultant','Consultant']].map(([key, label, align]) => (
                        <th key={key} style={{ ...s.th, cursor:'pointer', userSelect:'none', ...(align ? { textAlign: align } : {}) }}
                          onClick={() => setPropertySort(cur => ({ key, dir: cur.key === key && cur.dir === 'asc' ? 'desc' : 'asc' }))}>
                          {label}{propertySort.key === key ? (propertySort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                        </th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {displayProperties.length===0
                        ? <tr><td colSpan="7" style={{ ...s.td, textAlign:'center', color:c.muted }}>{drillClientProperties.length===0 ? 'No properties found' : 'No properties match your search.'}</td></tr>
                        : displayProperties.map(p=>(
                          <tr key={p.id} style={{ cursor:'pointer' }} onClick={()=>navigateTo({ drillProperty:p })}
                            onMouseEnter={e=>e.currentTarget.style.background=c.rowHover}
                            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                            <td style={s.td}><div style={{ fontWeight:'600', color:'var(--red)' }}>{p.name}</div></td>
                            <td style={{ ...s.td, fontFamily:'monospace', fontSize:'11px', color:c.subtext, textAlign:'center' }}>{p.master_property_id?`#${p.master_property_id}`:'—'}</td>
                            <td style={{ ...s.td, fontSize:'12px', color:c.subtext, textAlign:'center' }}>{p.location_code||'—'}</td>
                            <td style={{ ...s.td, fontSize:'12px', color:c.subtext, textAlign:'center' }}>{p._states.join(', ')||'—'}</td>
                            <td style={{ ...s.td, fontSize:'12px', color:c.subtext }}>
                              {p._assessors.length > 1
                                ? <span title={p._assessors.join('\n')} style={{ borderBottom:`1px dotted ${c.subtext}`, cursor:'help' }}>Multiple ({p._assessors.length})</span>
                                : (p._assessors[0] || '—')}
                            </td>
                            <td style={{ ...s.td, textAlign:'center' }}><span style={{ color:'var(--charcoal)' }}>{p._parcelCount}</span></td>
                            <td style={{ ...s.td, fontSize:'12px', color:c.subtext }}>{p.consultant_firm||'—'}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              )}

          {/* UPLOAD LINKS */}
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.cardTitle}>🔗 Secure Upload Links</div>
                  <button style={s.btn} onClick={()=>generateUploadToken(drillClient.id, drillClient.name)} disabled={generatingToken}>
                    {generatingToken ? '⏳ Generating...' : '+ Generate Upload Link'}
                  </button>
                </div>
                <div style={{ padding:'14px' }}>
                  {uploadTokens.length === 0 ? (
                    <div style={{ fontSize:'13px', color:c.muted, textAlign:'center', padding:'16px 0' }}>No upload links yet — generate one to send to this client</div>
                  ) : (() => {
                    const isLive = t => t.active && new Date(t.expires_at) >= new Date();
                    const liveTokens = uploadTokens.filter(isLive);
                    const inactiveTokens = uploadTokens.filter(t => !isLive(t));
                    const renderToken = t => {
                      const expired = new Date(t.expires_at) < new Date();
                      const url = `${window.location.origin}/upload/${t.token}`;
                      return (
                        <div key={t.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 12px', borderRadius:'8px', border:`1px solid ${c.cardBorder}`, marginBottom:'8px', background: !t.active||expired ? (isDark?'rgba(0,0,0,0.1)':'#f9f9f9') : 'transparent' }}>
                          <div style={{ width:'8px', height:'8px', borderRadius:'50%', flexShrink:0, background: expired||!t.active ? '#f87171' : '#34d399' }}/>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:'12px', color:c.text, fontFamily:'monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{url}</div>
                            <div style={{ fontSize:'11px', color:c.muted, marginTop:'2px' }}>
                              Created {new Date(t.created_at).toLocaleDateString()} · Expires {new Date(t.expires_at).toLocaleDateString()} · {t.files_received||0} file{t.files_received!==1?'s':''} received{!t.active&&!expired?' · Deactivated':''}{expired?' · Expired':''}
                            </div>
                          </div>
                          <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
                            <button style={{ ...s.btnSm, background:'rgba(96,165,250,0.15)', color:'#60a5fa', border:'1px solid rgba(96,165,250,0.3)' }} onClick={()=>navigator.clipboard.writeText(url)}>📋 Copy</button>
                            {(expired||!t.active) && <button style={s.btnSm} onClick={()=>generateUploadToken(drillClient.id, drillClient.name)}>↺ Regenerate</button>}
                          </div>
                        </div>
                      );
                    };
                    return (
                      <>
                        {liveTokens.map(renderToken)}
                        {liveTokens.length === 0 && (
                          <div style={{ fontSize:'13px', color:c.muted, textAlign:'center', padding:'8px 0' }}>No active upload links</div>
                        )}
                        {inactiveTokens.length > 0 && (
                          <>
                            <div onClick={() => setShowExpiredLinks(o => !o)} style={{ cursor:'pointer', userSelect:'none', fontSize:'12px', color:c.muted, padding:'8px 2px 2px', display:'flex', alignItems:'center', gap:'6px' }}>
                              <span>{showExpiredLinks ? '▾' : '▸'}</span>
                              <span>{showExpiredLinks ? 'Hide' : 'Show'} {inactiveTokens.length} inactive link{inactiveTokens.length!==1?'s':''}</span>
                            </div>
                            {showExpiredLinks && inactiveTokens.map(renderToken)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Legal Entity Names */}
              <div style={{ ...s.card, marginBottom:'16px' }}>
                <div style={{ ...s.cardHeader, cursor:'pointer' }} onClick={() => setLegalEntitiesOpen(o => !o)}>
                  <div style={s.cardTitle}>🏢 Legal Entity Names ({(drillClient.legal_entity_names || '').split(',').map(e => e.trim()).filter(Boolean).length}) {legalEntitiesOpen ? '▾' : '▸'}</div>
                  <div style={{ fontSize:'11px', color:c.muted }}>Used to match record cards and notices to this client</div>
                </div>
                {legalEntitiesOpen && (
                  <div style={{ padding:'14px 18px' }}>
                    <LegalEntityEditor clientId={drillClient.id} isDark={isDark} c={c} s={s} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CLIENT DOCUMENTS — docs filed to the CLIENT with no property (contracts, onboarding, operations).
              Every other documents surface scopes by property, so this is the only place they surface. */}
          {activePage==='clients' && drillClient && !drillProperty && !drillContact && (
            <div style={{ width:'100%' }}>
              <ClientDocuments clientId={drillClient.id} clientName={drillClient.name} c={c} s={s} />
            </div>
          )}

          {/* CLIENT NOTES — client notes + rolled-up property/parcel notes (client bubble view) */}
          {activePage==='clients' && drillClient && !drillProperty && !drillContact && (() => {
            const clientProps = allProperties.filter(p => p.client_id === drillClient.id);
            const clientParcels = parcels.filter(p => p.client_id === drillClient.id || p.client_name === drillClient.name);
            return (
              <div style={{ width:'100%' }}>
                <div style={s.card}>
                  <div style={{ padding:'14px 18px' }}>
                    <EntityNotes
                      level="client"
                      entityId={drillClient.id}
                      viewingYear={currentValueYear}
                      rollup={{ propertyIds: clientProps.map(p => p.id), parcelIds: clientParcels.map(p => p.id) }}
                      selfLabel={drillClient.name}
                      rollupLabels={{
                        ...Object.fromEntries(clientProps.map(p => [`property:${p.id}`, p.name || p.id])),
                        ...Object.fromEntries(clientParcels.map(p => [`parcel:${p.id}`, p.account_number || p.parcel_name || p.id])),
                      }}
                      staff={{ id: staffUser?.id, name: staffName }}
                      years={yearRange}
                      isDark={isDark}
                    />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* CLIENTS L3-contact — contact detail (R4a, display only) */}
          {activePage==='clients' && drillClient && drillContact && !drillProperty && (
            <div style={{ width:'100%' }}>
              <button style={s.btnBack} onClick={()=>navigateTo({ drillContact:null })}>← Back to {drillClient.name}</button>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'4px' }}>
                <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text }}>{drillContact.first_name} {drillContact.last_name}</div>
                <button onClick={() => { setContactModal({ contact: drillContact, prefillPropertyId: null }); navigateTo({ modal: 'contact' }); }} style={{ padding:'6px 12px', background:'transparent', border:'1px solid var(--line)', borderRadius:'8px', color:'var(--slate)', fontSize:'12px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif", fontWeight:'600' }}>✏️ Edit</button>
              </div>
              <div style={{ fontSize:'13px', color:c.muted, marginBottom:'22px' }}>{drillContact.title || 'No title'}</div>
              <div style={{ ...s.card, marginBottom:'16px' }}>
                <div style={{ padding:'14px 18px' }}>
                  <div style={{ fontSize:'13px', color:c.text, marginBottom:'10px' }}>
                    {drillContact.email || '—'}{drillContact.phone ? <span style={{ color:c.muted }}> · {formatPhone(drillContact.phone)}</span> : null}
                  </div>
                  {(drillContact.client_contact_roles || []).length === 0
                    ? <span style={{ color:c.muted, fontSize:'12px' }}>No roles assigned</span>
                    : (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                        {(drillContact.client_contact_roles || []).map(r => (
                          <span key={r.role} title={roleLabel(r.role)} style={s.badge('96,165,250')}>{roleAbbr(r.role)}</span>
                        ))}
                      </div>
                    )}
                </div>
              </div>
              <div style={s.card}>
                <div style={s.cardHeader}><div style={s.cardTitle}>Associated Properties</div></div>
                {contactProperties.length === 0
                  ? <div style={{ padding:'20px 18px', fontSize:'13px', color:c.muted, fontStyle:'italic' }}>Not assigned to any properties</div>
                  : (
                    <table style={s.table}>
                      <thead><tr>
                        <th style={s.th}>Property</th>
                        <th style={{ ...s.th, textAlign:'center' }}>MPID</th>
                        <th style={{ ...s.th, textAlign:'center' }}>Location ID</th>
                        <th style={{ ...s.th, textAlign:'center' }}>Parcels</th>
                        <th style={s.th}>Consultant</th>
                        <th style={s.th}>Roles</th>
                      </tr></thead>
                      <tbody>
                        {contactProperties.map(p=>{const pp=parcels.filter(par=>par.master_property_id===p.master_property_id);return(
                          <tr key={p.id} style={{ cursor:'pointer' }} onClick={()=>navigateTo({ drillProperty:p })}
                            onMouseEnter={e=>e.currentTarget.style.background=c.rowHover}
                            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                            <td style={s.td}><div style={{ fontWeight:'600', color:'var(--red)' }}>{p.name}</div></td>
                            <td style={{ ...s.td, fontFamily:'monospace', fontSize:'11px', color:c.subtext, textAlign:'center' }}>{p.master_property_id?`#${p.master_property_id}`:'—'}</td>
                            <td style={{ ...s.td, fontSize:'12px', color:c.subtext, textAlign:'center' }}>{p.location_code||'—'}</td>
                            <td style={{ ...s.td, textAlign:'center' }}><span style={{ color:'var(--charcoal)' }}>{pp.length}</span></td>
                            <td style={{ ...s.td, fontSize:'12px', color:c.subtext }}>{p.consultant_firm||'—'}</td>
                            <td style={s.td}>
                              <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                                {p.roles.map(r => {
                                  const label = contactRoleLabel(r.role) + (r.scope && r.scope !== 'ALL' ? ` (${r.scope})` : '');
                                  return <span key={`${r.role}|${r.scope}`} title={label} style={s.badge('96,165,250')}>{label}</span>;
                                })}
                              </div>
                            </td>
                          </tr>
                        );})}
                      </tbody>
                    </table>
                  )}
              </div>
            </div>
          )}

          {/* CLIENTS L3 */}
          {activePage==='clients' && drillClient && drillProperty && (
            <div style={{ width:'100%' }}>
              <button style={s.btnBack} onClick={()=>navigateTo({ drillProperty:null })}>← Back to {drillContact ? `${drillContact.first_name} ${drillContact.last_name}` : drillClient.name}</button>
              {modal === 'propDocs' && (
                <DocumentsBubble
                  property={drillProperty}
                  parcel={null}
                  isDark={isDark}
                  onClose={() => navigateTo({ modal: null })}
                />
              )}
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'4px' }}>
                <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text }}>{drillProperty.name}</div>
                <button onClick={() => navigateTo({ modal: modal === 'propDocs' ? null : 'propDocs' })}
                  style={{ padding:'6px 12px', background: modal === 'propDocs' ? 'rgba(96,165,250,0.15)' : 'transparent', border:`1px solid ${modal === 'propDocs' ? '#60a5fa' : 'var(--line)'}`, borderRadius:'8px', color: modal === 'propDocs' ? '#60a5fa' : 'var(--slate)', fontSize:'12px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif", fontWeight:'600' }}>
                  📂 Documents
                </button>
                {/* ⛔ THE SAME YEAR THE PARCEL MODAL USES — one sessionStorage key, not a page-local state.
                    Choosing here moves the modal's opening year, the retirement gating on the Parcels list and
                    PropertiesGrid, the per-year appeal status/deadline/services, and the Clients-page counts. */}
                <ValueYearSelect years={yearRange} value={currentValueYear} onChange={setValueYear}
                  optionChip={drillYearOptionChip} s={s} c={c} />
              </div>
              <div style={{ fontSize:'13px', color:c.muted, marginBottom:'22px' }}>
                {drillProperty.city&&drillProperty.state?`${drillProperty.city}, ${drillProperty.state}`:''}{drillProperty.master_property_id?` · #${drillProperty.master_property_id}`:''}
                {/* ⛔ A BARE "2 parcels" ON A YEAR-SCOPED SCREEN DOES NOT SAY IT IS YEAR-SCOPED, and that is
                    what made "0 parcels" read as data loss rather than as a filter. The ambiguity exists at
                    every count; zero only made it visible. So the year is always named, and the
                    other-years clause appears only when the two numbers differ. */}
                {' · '}{drillPropertyParcels.length} parcel{drillPropertyParcels.length === 1 ? '' : 's'} in {currentValueYear}
                {drillPropertyParcelsAll.length !== drillPropertyParcels.length
                  && ` · ${drillPropertyParcelsAll.length - drillPropertyParcels.length} in other years`}
              </div>
              {/* ⛔ SAY WHEN THE SELECTED YEAR IS NOT ONE WE HELD THIS PROPERTY. Mirrors ParcelModal:2752, with one
                  difference: NO reactivation instruction. A parcel has a Parcel Status control to set; a property's
                  retired_year is set by hand and there is nothing on this screen to click. Offering an action that
                  does not exist is the "resolve that bill first" trap — the message states the fact and stops.
                  retirementLabel(null, prop) is safe: retirement.js:49 optional-chains the parcel and falls through
                  to the property, which is also where its "Property retired" wording comes from. */}
              {(() => {
                const pr = { retired_year: drillProperty.retired_year, reactivated_year: drillProperty.reactivated_year, first_year: drillProperty.first_year };
                if (representedInYear(null, pr, currentValueYear)) return null;
                // ⛔ TWO REASONS, TWO SENTENCES — the lesson already paid for at ParcelModal:2735-2742. A property
                // BEFORE its first_year is not retired, so retirementLabel() returns '' and a single sentence reads
                // "Not represented in 2021 — . Viewing history only." A dangling em-dash with no reason is what
                // turned a correctly-disabled control into an hour of "it's dead and there's no error". Name the
                // actual cause; there is no control on this screen to point at either way.
                const y = String(currentValueYear);
                const first = pr.first_year;
                const beforeFirst = first != null && first !== '' && y < String(first);
                const label = retirementLabel(null, pr);
                return (
                  <div style={{ margin: '0 0 18px', padding: '9px 13px', fontSize: '12px', fontWeight: '600',
                    color: isDark ? '#fbbf24' : '#8A5B00', background: isDark ? 'rgba(251,191,36,0.10)' : 'rgba(251,191,36,0.12)',
                    border: '1px solid rgba(251,191,36,0.55)', borderRadius: '8px' }}>
                    {beforeFirst
                      ? <>⚠ Not ours in {currentValueYear} — this property has been with us since {first}. Viewing history only.</>
                      : label
                        ? <>⚠ Not represented in {currentValueYear} — {label}. Viewing history only.</>
                        : <>⚠ Not represented in {currentValueYear}. Viewing history only.</>}
                  </div>
                );
              })()}
              {/* Property Details — the property's own info, above its parcels. Reads the full row (propFull) with drillProperty as fallback. */}
              {(() => {
                const pd = propFull || drillProperty;
                const money = (v) => (v == null || v === '') ? null : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                const rows = [
                  ['Client', drillClient?.name],
                  ['Address', pd.address],
                  ['City / State / Zip', [pd.city, pd.state, pd.postal_code].filter(Boolean).join(', ')],
                  ['County', pd.county],
                  ['MPID', pd.master_property_id],
                  ['Location ID', pd.location_code],
                  ['Purchase Date', pd.purchase_date],
                  ['Purchase Amount', money(pd.purchase_amount)],
                  ['Purchase Type', pd.purchase_type],
                ];
                return (
                  <div style={s.card}>
                    <div style={s.cardHeader}><div style={s.cardTitle}>Property Details</div>
                      <button onClick={() => { setEditProperty(pd); navigateTo({ modal: 'editProperty' }); }} style={s.btn}>Edit</button>
                    </div>
                    <div style={{ padding:'16px 18px', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:'14px 24px' }}>
                      {rows.map(([label, val]) => (
                        <div key={label}>
                          <div style={{ fontSize:'11px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px' }}>{label}</div>
                          <div style={{ fontSize:'13px', color:c.text }}>{(val != null && val !== '') ? val : '—'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* Property Contacts — read-only resolved list; opens the one ContactModal to add/edit. Between Property Details and Parcels. */}
              <div style={s.card}>
                <div style={{ padding:'14px 18px' }}>
                  <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'15px', fontWeight:700, color:c.text, marginBottom:'10px' }}>Property Contacts</div>
                  <PropertyContactsCard propertyId={drillProperty.id} clientId={drillProperty.client_id} refreshKey={contactRefresh} isDark={isDark}
                    onOpenModal={payload => { setContactModal(payload); navigateTo({ modal: 'contact' }); }} />
                </div>
              </div>
              <div style={s.card}>
                <div style={s.cardHeader}><div style={s.cardTitle}>Parcels</div>
                  <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                  <button onClick={() => { setCreateParcelFor(drillProperty); navigateTo({ modal: 'createParcel' }); }} style={s.btn}>+ Add Parcel</button>
                  <div style={{ position:'relative' }}>
                    <button onClick={() => navigateTo({ modal: modal === 'colsPropParcels' ? null : 'colsPropParcels' })} disabled={!staffUser?.id}
                      title={staffUser?.id ? 'Show, hide, and reorder columns' : 'Sign-in required to save column layout'}
                      style={{ padding:'8px 14px', background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--line-soft)', border:`1px solid ${c.border}`, borderRadius:'8px', color:c.text, fontSize:'12px', fontWeight:'600', cursor: staffUser?.id ? 'pointer' : 'not-allowed', opacity: staffUser?.id ? 1 : 0.5 }}>
                      ⊞ Columns ▾
                    </button>
                    {modal === 'colsPropParcels' && (
                      <ColumnPicker
                        columns={resolvedPropParcelCols}
                        onSave={async (newCols) => { await savePropParcelCols(newCols); navigateTo({ modal: null }); }}
                        onClose={() => navigateTo({ modal: null })}
                        c={c}
                        s={s}
                        isDark={isDark}
                        disabled={!staffUser?.id}
                      />
                    )}
                  </div>
                  </div>
                </div>
                {(() => {
                  // locked columns render regardless of a saved visible:false (hand-edited prefs row).
                  const propParcelCols = resolvedPropParcelCols.filter(col => col.locked || col.visible);
                  return (
                <ScrollX style={{ overflowX:'auto' }}>
                  <table style={s.table}>
                    <thead><tr>
                      {propParcelCols.map(col => <th key={col.key} style={{ ...s.th, textAlign: col.align }}>{col.label}</th>)}
                    </tr></thead>
                    <tbody>
                      {drillPropertyParcels.length===0
                        ? <tr><td colSpan={propParcelCols.length} style={{ ...s.td, textAlign:'center', color:c.muted, padding:'18px 16px' }}>{(() => {
                            // ⛔ "No parcels found" IS ACCURATE AND USELESS — the same dead-end shape as
                            // "resolve that bill first": true, and it leaves someone guessing a year to find
                            // their own data while the property looks empty. Say where they are, and make the
                            // year a CONTROL rather than an instruction to go operate one three inches away.
                            if (!drillPropertyParcelsAll.length) return 'No parcels on this property yet.';
                            const n = drillPropertyParcelsAll.length;
                            return (<>
                              <div style={{ fontWeight:600, color:c.text }}>No parcels represented in {currentValueYear}.</div>
                              <div style={{ marginTop:'4px' }}>
                                This property has {n} parcel{n === 1 ? '' : 's'}
                                {drillFirstRepYear
                                  ? <>, represented from{' '}
                                      <button type="button" onClick={() => setValueYear(drillFirstRepYear)}
                                        style={{ background:'none', border:'none', padding:0, font:'inherit', color:'var(--red)', fontWeight:700, cursor:'pointer', textDecoration:'underline' }}>
                                        {drillFirstRepYear}
                                      </button>{' '}— switch to see {n === 1 ? 'it' : 'them'}.</>
                                  : ' represented in other years.'}
                              </div>
                            </>);
                          })()}</td></tr>
                        : drillPropertyParcels.map(p=>(
                          <tr key={p.id} style={{ cursor:'pointer' }} onClick={()=>navigateTo({ drillParcel:p })} onMouseEnter={e=>e.currentTarget.style.background=c.rowHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                            {propParcelCols.map(col => (
                              <td key={col.key}
                                style={{ ...s.td, textAlign: col.align, whiteSpace: col.nowrap ? 'nowrap' : undefined }}
                                onClick={col.stopRowClick ? (e => e.stopPropagation()) : undefined}>
                                {col.render(p)}
                              </td>
                            ))}
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                  {/* ⛔ ONCE PER TABLE, NOT PER ROW. Shown only when a visible row has no parcel_year_data for the
                      selected year, so it disappears as the back-years are loaded. "Not recorded" is deliberate:
                      the gap is in what we hold, not in what the app can show. */}
                  {anyYearDataMissing(drillPropertyParcels) && (
                    <div style={{ padding: '8px 14px', fontSize: '11px', color: c.muted, borderTop: `1px solid ${c.cardBorder}` }}>
                      Some parcels have no data recorded for {currentValueYear} — their Appeal Deadline and Services show “—”. Earlier years are still being loaded.
                    </div>
                  )}
                </ScrollX>
                  );
                })()}
              </div>
              {(
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <div style={s.cardTitle}>⚡ Open Tasks for this Property</div>
                    <span style={s.badge('251,191,36')}>{propertyTasks.length} open</span>
                  </div>
                  <table style={s.table}>
                    <thead><tr>
                      <th style={s.th}>Task</th>
                      <th style={s.th}>Priority</th>
                      <th style={s.th}>Assigned To</th>
                      <th style={s.th}>Due Date</th>
                      <th style={s.th}>Status</th>
                    </tr></thead>
                    <tbody>
                      {propertyTasks.length === 0 ? (
                        <tr><td colSpan="5" style={{ ...s.td, textAlign:'center', color:c.muted }}>No open tasks for this property</td></tr>
                      ) : propertyTasks.map(t => {
                        const overdue = t.due_date && new Date(t.due_date) < new Date();
                        const days = t.due_date ? Math.ceil((new Date(t.due_date)-new Date())/86400000) : null;
                        return (
                          <tr key={t.id} style={{ cursor:'pointer' }}
                            onClick={() => { setDrillTask(t); navigateTo({ page:'tasks' }); }}
                            onMouseEnter={e=>e.currentTarget.style.background=c.rowHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                            <td style={s.td}><div style={{ fontWeight:'600', color:c.text }}>{t.title}</div></td>
                            <td style={s.td}><span style={s.badge(t.priority==='Urgent'?'248,113,113':t.priority==='High'?'251,191,36':'52,211,153')}>{t.priority}</span></td>
                            <td style={{ ...s.td, fontSize:'12px', color:c.subtext }}>{t.assigned_to_name||'—'}</td>
                            <td style={{ ...s.td, fontSize:'12px', color:overdue?'#f87171':days<=3?'#fbbf24':c.subtext, fontWeight:overdue?'700':'400' }}>
                              {t.due_date ? (overdue && days < 0?`⚠ ${Math.abs(days)}d overdue`:days===0?'⚠ Due Today':days===1?'Tomorrow':`${days}d`) : '—'}
                            </td>
                            <td style={s.td}><span style={s.badge(t.status==='Open'?'96,165,250':t.status==='In Progress'?'251,191,36':'180,0,0')}>{t.status}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* PROPERTY SECURE UPLOAD LINKS */}
          {activePage==='clients' && drillClient && drillProperty && (
            <div style={{ width:'100%' }}>
              <div style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.cardTitle}>🔗 Secure Upload Links</div>
                  <button style={s.btn} onClick={() => generateUploadToken(drillClient.id, drillClient.name, true)} disabled={generatingToken}>
                    {generatingToken ? '⏳ Generating...' : '+ Generate Upload Link'}
                  </button>
                </div>
                <div style={{ padding:'14px' }}>
                  <div style={{ fontSize:'12px', color:c.muted, marginBottom:'10px' }}>Links generated here are scoped to <strong style={{ color:c.text }}>{drillProperty.name}</strong> — uploaded files will appear in this property's file section.</div>
                  {propertyTokens.length === 0 ? (
                    <div style={{ fontSize:'13px', color:c.muted, textAlign:'center', padding:'16px 0' }}>No upload links for this property yet</div>
                  ) : propertyTokens.map(t => {
                    const expired = new Date(t.expires_at) < new Date();
                    const url = `${window.location.origin}/upload/${t.token}`;
                    return (
                      <div key={t.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 12px', borderRadius:'8px', border:`1px solid ${c.border}`, marginBottom:'8px', background: !t.active||expired ? (isDark?'rgba(0,0,0,0.1)':'#f9f9f9') : 'transparent' }}>
                        <div style={{ width:'8px', height:'8px', borderRadius:'50%', flexShrink:0, background: expired||!t.active ? '#f87171' : '#34d399' }}/>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:'12px', color:c.text, fontFamily:'monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{url}</div>
                          <div style={{ fontSize:'11px', color:c.muted, marginTop:'2px' }}>
                            Created {new Date(t.created_at).toLocaleDateString()} · Expires {new Date(t.expires_at).toLocaleDateString()} · {t.files_received||0} file{t.files_received!==1?'s':''} received{!t.active&&!expired?' · Deactivated':''}{expired?' · Expired':''}
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
                          <button style={{ ...s.btnSm, background:'rgba(96,165,250,0.15)', color:'#60a5fa', border:'1px solid rgba(96,165,250,0.3)' }} onClick={() => navigator.clipboard.writeText(url)}>📋 Copy</button>
                          {(expired||!t.active) && <button style={s.btnSm} onClick={() => generateUploadToken(drillClient.id, drillClient.name, true)}>↺ Regenerate</button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* PROPERTY DOCUMENTS — shown when drillProperty is set */}
          {activePage==='clients' && drillClient && drillProperty && (
            <div style={{ width:'100%' }}>
              <div style={s.card}>
                <div style={s.cardHeader}>
                  {/* Title toggles; the count and Refresh stay reachable while collapsed. */}
                  <div style={{ ...s.cardTitle, cursor:'pointer', userSelect:'none' }} onClick={toggleFiledDocs}
                    title={filedDocsCollapsed ? 'Show filed documents' : 'Hide filed documents'}>
                    <span style={{ display:'inline-block', width:'12px', color:c.muted }}>{filedDocsCollapsed ? '▸' : '▾'}</span> 📁 Filed Documents
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <span style={s.badge('96,165,250')}>{propertyFiles.length} file{propertyFiles.length!==1?'s':''}</span>
                    <button onClick={() => loadPropertyFiles(drillProperty)} disabled={propertyFilesRefreshing}
                      style={{ ...s.btnSm, background:'transparent', color: propertyFilesRefreshing ? c.muted : 'var(--red)', border:`1px solid ${propertyFilesRefreshing ? c.border : 'rgba(180,0,0,0.3)'}` }}>
                      {propertyFilesRefreshing ? '⏳' : '↻ Refresh'}
                    </button>
                  </div>
                </div>
                {filedDocsCollapsed ? null : propertyFiles.length === 0 ? (
                  <div style={{ padding:'24px', textAlign:'center', fontSize:'13px', color:c.muted }}>No documents filed for this property yet</div>
                ) : (() => {
                  // Group files by group_id, ungrouped files stand alone
                  const groups = {};
                  const ungrouped = [];
                  propertyFiles.forEach(f => {
                    if (f.group_id) {
                      if (!groups[f.group_id]) groups[f.group_id] = [];
                      groups[f.group_id].push(f);
                    } else ungrouped.push(f);
                  });
                  // Client-side sort over the rows ALREADY LOADED. Applied WITHIN each Tax Bill Package and within
                  // the ungrouped list — a package is a unit and stays contiguous, so sorting can never scatter a
                  // bill and its transmittal apart. Default (created_at desc) reproduces the previous order exactly.
                  const sortVal = (f, key) => {
                    if (key === 'created_at') return f.created_at || '';
                    if (key === 'file_name') return (f.file_name?.split('/').pop() || f.file_name || '').toLowerCase();
                    if (key === 'folder_name') return `${f.folder_name || ''} ${f.year_subfolder || ''}`.toLowerCase();
                    return (f[key] || '').toString().toLowerCase();
                  };
                  const cmp = (a, b) => {
                    const av = sortVal(a, filedDocsSort.key), bv = sortVal(b, filedDocsSort.key);
                    if (av === bv) return 0;
                    return (av < bv ? -1 : 1) * (filedDocsSort.dir === 'asc' ? 1 : -1);
                  };
                  Object.values(groups).forEach(g => g.sort(cmp));
                  ungrouped.sort(cmp);
                  const SortTh = ({ label, k, center }) => (
                    <th style={{ ...s.th, ...(center ? { textAlign:'center' } : {}), cursor:'pointer', userSelect:'none' }}
                      title={`Sort by ${label}`}
                      onClick={() => setFiledDocsSort(prev => ({ key: k, dir: prev.key === k && prev.dir === 'desc' ? 'asc' : 'desc' }))}>
                      {label}{filedDocsSort.key === k ? <span style={{ color:'var(--red)' }}>{filedDocsSort.dir === 'desc' ? ' ▾' : ' ▴'}</span> : ''}
                    </th>
                  );
                  const formatDateTime = (ts) => {
                    if (!ts) return '—';
                    const d = new Date(ts);
                    return d.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) + ' · ' +
                      d.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',hour12:true});
                  };
                  const FileRow = ({f, grouped}) => (
                    <tr key={f.id}
                      style={{ background: grouped ? (isDark?'rgba(180,0,0,0.04)':'rgba(180,0,0,0.03)') : 'transparent' }}
                      onMouseEnter={e=>e.currentTarget.style.opacity='0.85'}
                      onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
                      <td style={s.td}>
                        {grouped && <span style={{ display:'inline-block', width:'3px', height:'16px', background:'var(--red)', borderRadius:'2px', marginRight:'8px', verticalAlign:'middle' }}/>}
                        <span style={{ fontWeight:'600', fontSize:'12px' }}>{f.file_name?.split('/').pop()||f.file_name}</span>
                      </td>
                      <td style={{ ...s.td, fontSize:'12px', color:c.subtext }}>
                        {f.type_key==='tax_bill_transmittal' ? <span style={{ color:'var(--red)' }}>📋 {vocab?.type(f.type_key)?.name || f.file_type}</span> : (vocab?.type(f.type_key)?.name || f.file_type || '—')}
                      </td>
                      <td style={{ ...s.td, fontSize:'12px', color:c.muted, fontFamily:'monospace' }}>{f.folder_key ? (vocab?.folder(f.folder_key)?.name || f.folder_name) : '—'}{f.year_subfolder?` / ${f.year_subfolder}`:''}</td>
                      <td style={{ ...s.td, fontSize:'12px', color:c.subtext }}>{f.uploaded_by_name||'—'}</td>
                      <td style={{ ...s.td, fontSize:'12px', color:c.muted }}>{formatDateTime(f.created_at)}</td>
                      <td style={{ ...s.td, textAlign:'center' }}>
                        <div style={{ display:'flex', gap:'6px', justifyContent:'center' }}>
                          <button style={{ ...s.btnSm, background:'rgba(96,165,250,0.15)', color:'#60a5fa', border:'1px solid rgba(96,165,250,0.3)' }} onClick={async(e)=>{
                            e.stopPropagation();
                            try{
                            const u=await fileUrl(f.storage_path,'preview');
                            if(f.file_name?.endsWith('.html')){const resp=await fetch(u);const html=await resp.text();setPreviewFile({html,name:f.file_name,type:'html'});}else{setPreviewFile({url:u,name:f.file_name,type:'pdf'});}
                            }catch{ alert('Could not preview the file.'); }
                          }}>👁 Preview</button>
                          <button style={{ ...s.btnSm, background:'rgba(52,211,153,0.12)', color:'#34d399', border:'1px solid rgba(52,211,153,0.3)' }} onClick={async(e)=>{
                            e.stopPropagation();
                            try{
                            const u=await fileUrl(f.storage_path,'open');
                            window.open(u, '_blank', 'noopener,noreferrer');
                            }catch{ alert('Could not open the file.'); }
                          }}>↗ Open</button>
                          <button style={s.btnSm} onClick={async()=>{
                            try{
                            const u=await fileUrl(f.storage_path,'download');
                            const a=document.createElement('a');a.href=u;a.download=f.file_name;a.click();
                            }catch{ alert('Could not download the file.'); }
                          }}>⬇</button>
                          <button style={{ ...s.btnSm, background:'rgba(248,113,113,0.12)', color:'#f87171', border:'1px solid rgba(248,113,113,0.3)' }} onClick={async()=>{
                            if(!window.confirm(`Delete "${f.file_name?.split('/').pop()||f.file_name}"?\n\nThis will permanently remove the file record. This cannot be undone.`)) return;
                            const { error: delErr } = await fileDelete(f.id);
                            if (delErr) { alert('Could not delete the file: ' + delErr.message); return; }
                            setPropertyFiles(prev => prev.filter(x => x.id !== f.id));
                          }}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  );
                  return (
                    <table style={s.table}>
                      <thead><tr>
                        <SortTh label="File" k="file_name" />
                        <SortTh label="Type" k="file_type" />
                        <SortTh label="Folder" k="folder_name" />
                        <SortTh label="Filed By" k="uploaded_by_name" />
                        <SortTh label="Date & Time" k="created_at" />
                        <th style={{ ...s.th, textAlign:'center' }}>Action</th>
                      </tr></thead>
                      <tbody>
                        {Object.values(groups).map((grp,gi) => (
                          <React.Fragment key={gi}>
                            <tr><td colSpan="6" style={{ padding:'6px 14px 2px', background:isDark?'rgba(180,0,0,0.08)':'rgba(180,0,0,0.06)', borderTop:`1px solid rgba(180,0,0,0.2)` }}>
                              <span style={{ fontSize:'11px', color:'var(--red)', fontFamily:'monospace', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.07em' }}>📎 Tax Bill Package · {grp[0]?.year_subfolder||''}</span>
                            </td></tr>
                            {grp.map(f => <FileRow key={f.id} f={f} grouped={true} />)}
                          </React.Fragment>
                        ))}
                        {ungrouped.map(f => <FileRow key={f.id} f={f} grouped={false} />)}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </div>
          )}

          {/* PROPERTY NOTES — last section on the property screen; property notes + rolled-up parcel notes */}
          {activePage==='clients' && drillClient && drillProperty && (
            <div style={{ width:'100%' }}>
              <div style={s.card}>
                <div style={{ padding:'14px 18px' }}>
                  <EntityNotes
                    level="property"
                    entityId={drillProperty.id}
                    viewingYear={currentValueYear}
                    rollup={{ parcelIds: drillPropertyParcelsAll.map(p => p.id) }}
                    selfLabel={drillProperty.name}
                    rollupLabels={Object.fromEntries(drillPropertyParcelsAll.map(p => [`parcel:${p.id}`, p.account_number || p.parcel_name || p.id]))}
                    staff={{ id: staffUser?.id, name: staffName }}
                    years={yearRange}
                    isDark={isDark}
                  />
                </div>
              </div>
            </div>
          )}

          {/* FILE EXCHANGE */}
          {activePage==='exchange' && (() => {
            // ── Feature flag — flip to true when ready to send real emails ──
            const NOTIFY_EMAIL_ENABLED = false;


            const canUpload = exchClient && exchLevel && (exchLevel === 'general' || selectedProp);
            const exchFiles = files.filter(f => exchClient && f.client_id === exchClient.id);
            const filteredRecipients = portalUsers.filter(u => !exchRecipientSearch || u.name?.toLowerCase().includes(exchRecipientSearch.toLowerCase()) || u.email?.toLowerCase().includes(exchRecipientSearch.toLowerCase()));

            const btnToggle = (active) => ({
              flex: 1, padding: '14px 20px', borderRadius: '10px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif",
              fontWeight: '700', fontSize: '14px', textAlign: 'center', transition: 'all 0.15s',
              background: active ? 'linear-gradient(135deg,#B40000,#9C0000)' : (isDark ? 'rgba(255,255,255,0.03)' : '#f8f9fc'),
              color: active ? 'var(--ink)' : c.subtext,
              border: active ? 'none' : `1px solid ${c.border}`,
            });

            return (
            <div style={{ width:'100%' }}>
              <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'6px' }}>File Exchange</div>
              <div style={{ fontSize:'13px', color:c.muted, marginBottom:'22px' }}>Send files to clients or view files received from them.</div>

              {/* Step 1 — Select Client */}
              <div style={s.card}>
                <div style={s.cardHeader}><div style={s.cardTitle}>1 · Select Client</div></div>
                <div style={{ padding:'16px' }}>
                  <select style={{ ...s.select, width:'100%' }} value={exchClient?.id || ''} onChange={e => {
                    const cl = allClients.find(c2 => c2.id === e.target.value);
                    setExchClient(cl || null);
                    setExchLevel(null);
                    setSelectedProp(null);
                    setExchNotify(false);
                    setExchRecipients([]);
                    if (cl) {
                      loadClientData(cl.id, cl.name);   // loads properties, portal users AND setFiles() — do not re-read file_exchange here
                    } else { setFiles([]); setProperties([]); }
                  }}>
                    <option value="">— Select Client —</option>
                    {allClients.map(cl => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Step 2 — Property Specific or General */}
              {exchClient && (
                <div style={s.card}>
                  <div style={s.cardHeader}><div style={s.cardTitle}>2 · File Level</div></div>
                  <div style={{ padding:'16px' }}>
                    <div style={{ display:'flex', gap:'12px', marginBottom: exchLevel ? '16px' : '0' }}>
                      <div style={btnToggle(exchLevel === 'property')} onClick={() => { setExchLevel('property'); setSelectedProp(null); }}>
                        <div style={{ fontSize:'22px', marginBottom:'6px' }}>🏢</div>
                        <div>Property Specific</div>
                        <div style={{ fontSize:'11px', fontWeight:'400', marginTop:'4px', opacity:0.8 }}>File belongs to a specific property</div>
                      </div>
                      <div style={btnToggle(exchLevel === 'general')} onClick={() => { setExchLevel('general'); setSelectedProp(null); }}>
                        <div style={{ fontSize:'22px', marginBottom:'6px' }}>📁</div>
                        <div>General / Client Level</div>
                        <div style={{ fontSize:'11px', fontWeight:'400', marginTop:'4px', opacity:0.8 }}>Authorization, budget, correspondence, etc.</div>
                      </div>
                    </div>

                    {/* Property search — only if property specific */}
                    {exchLevel === 'property' && (
                      <div style={{ borderTop:`1px solid ${c.border}`, paddingTop:'14px' }}>
                        <div style={{ fontSize:'11px', color:c.muted, fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'8px' }}>
                          Select Property {selectedProp && <span style={{ color:'#34d399', fontWeight:'400' }}>✓ {selectedProp.name}</span>}
                        </div>
                        <input style={{ ...s.input, marginBottom:'10px' }} placeholder="Search by name, city, or ID..." value={searchQ} onChange={e => setSearchQ(e.target.value)} />
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'8px', maxHeight:'180px', overflowY:'auto' }}>
                          {filteredProps.map(p => (
                            <div key={p.id} onClick={() => setSelectedProp(p)}
                              style={{ padding:'10px 12px', borderRadius:'8px', cursor:'pointer', border: selectedProp?.id===p.id ? `1px solid #B40000` : `1px solid ${c.border}`, background: selectedProp?.id===p.id ? 'rgba(180,0,0,0.08)' : 'transparent' }}>
                              <div style={{ fontSize:'12px', fontWeight:'700', color:c.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.name}</div>
                              <div style={{ fontSize:'11px', color:c.muted }}>{p.city}{p.state?`, ${p.state}`:''}</div>
                              {p.master_property_id && <div style={{ fontSize:'11px', color:c.subtext, fontFamily:'monospace' }}>#{p.master_property_id}</div>}
                            </div>
                          ))}
                          {filteredProps.length === 0 && <div style={{ gridColumn:'1/-1', padding:'12px', textAlign:'center', fontSize:'12px', color:c.muted }}>{searchQ ? 'No properties match' : 'Type to search properties'}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3 — Upload */}
              {exchClient && exchLevel && (
                <div style={{ ...s.card, opacity: canUpload ? 1 : 0.55 }}>
                  <div style={s.cardHeader}>
                    <div style={s.cardTitle}>3 · Upload File</div>
                    <div style={{ fontSize:'12px', color: canUpload ? 'var(--red)' : c.muted }}>
                      {canUpload
                        ? exchLevel === 'property'
                          ? `→ ${exchClient.name} · ${selectedProp.name}`
                          : `→ ${exchClient.name} (client level)`
                        : 'Select a property above to continue'}
                    </div>
                  </div>
                  <div style={{ padding:'14px 16px' }}>
                    {/* File type */}
                    <div style={{ marginBottom:'12px' }}>
                      <div style={{ fontSize:'11px', color:c.muted, fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px' }}>File Type</div>
                      <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
                        <select id="file-type-select" style={{ ...s.select, flex:1 }} value={exchFileType} onChange={e => setExchFileType(e.target.value)}>
                          <option value="">— Select type —</option>
                          <DocTypeOptions vocab={vocab} current={exchFileType} />
                        </select>
                        {vocab?.type(exchFileType) && <span style={{ fontSize:'11px', color:'var(--red)', fontFamily:'monospace', whiteSpace:'nowrap' }}>→ {vocab.folder(vocab.type(exchFileType).folder_key)?.name}</span>}
                      </div>
                    </div>

                    {/* Notify toggle */}
                    <div style={{ marginBottom:'12px', padding:'12px 14px', background:isDark?'rgba(26,34,56,0.5)':'#f8f9fc', borderRadius:'8px', border:`1px solid ${c.border}` }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: exchNotify ? '12px' : '0' }}>
                        <div>
                          <div style={{ fontSize:'13px', fontWeight:'600', color:c.text }}>Notify Client?</div>
                          <div style={{ fontSize:'11px', color:c.muted }}>Send an email to the client when this file is uploaded</div>
                        </div>
                        <div style={{ display:'flex', gap:'8px' }}>
                          <button onClick={() => setExchNotify(false)} style={{ ...s.btnSm, background: !exchNotify ? 'rgba(52,211,153,0.15)' : 'transparent', color: !exchNotify ? '#34d399' : c.muted, border: !exchNotify ? '1px solid rgba(52,211,153,0.3)' : `1px solid ${c.border}` }}>Upload Silently</button>
                          <button onClick={() => setExchNotify(true)} style={{ ...s.btnSm, background: exchNotify ? 'rgba(180,0,0,0.15)' : 'transparent', color: exchNotify ? 'var(--red)' : c.muted, border: exchNotify ? '1px solid rgba(180,0,0,0.3)' : `1px solid ${c.border}` }}>Notify Client ✉</button>
                        </div>
                      </div>

                      {/* Recipient picker */}
                      {exchNotify && (
                        <div style={{ borderTop:`1px solid ${c.border}`, paddingTop:'10px' }}>
                          {NOTIFY_EMAIL_ENABLED ? (
                            <>
                              <div style={{ fontSize:'11px', color:c.muted, fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px' }}>Select Recipients</div>
                              <input style={{ ...s.input, marginBottom:'8px' }} placeholder="Search by name or email..." value={exchRecipientSearch} onChange={e => setExchRecipientSearch(e.target.value)} />
                              {filteredRecipients.map(u => {
                                const selected = exchRecipients.some(r => r.id === u.id);
                                return (
                                  <div key={u.id} onClick={() => setExchRecipients(prev => selected ? prev.filter(r=>r.id!==u.id) : [...prev, u])}
                                    style={{ display:'flex', alignItems:'center', gap:'10px', padding:'7px 10px', borderRadius:'6px', cursor:'pointer', marginBottom:'4px', background: selected ? 'rgba(180,0,0,0.1)' : 'transparent', border: selected ? '1px solid rgba(180,0,0,0.3)' : `1px solid transparent` }}>
                                    <div style={{ width:'8px', height:'8px', borderRadius:'50%', background: selected ? 'var(--red)' : c.border, flexShrink:0 }} />
                                    <div style={{ flex:1 }}>
                                      <div style={{ fontSize:'12px', fontWeight:'600', color:c.text }}>{u.name}</div>
                                      <div style={{ fontSize:'11px', color:c.muted }}>{u.email}</div>
                                    </div>
                                    {selected && <span style={{ fontSize:'11px', color:'var(--red)' }}>✓ Selected</span>}
                                  </div>
                                );
                              })}
                              {portalUsers.length === 0 && <div style={{ fontSize:'12px', color:c.muted }}>No portal users for this client yet.</div>}
                            </>
                          ) : (
                            <div style={{ fontSize:'12px', color:'#fbbf24', display:'flex', alignItems:'center', gap:'8px' }}>
                              <span>⏳</span>
                              <span>Email notifications coming soon — upload will proceed silently for now.</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Drop zone */}
                    <input ref={fileInputRef} type="file" style={{ display:'none' }} onChange={e=>e.target.files[0]&&canUpload&&sendExchangeFile(e.target.files[0])} />
                    <div onClick={() => canUpload && !uploading && fileInputRef.current?.click()}
                      style={{ border:`2px dashed ${canUpload ? c.inputBorder : c.border}`, borderRadius:'10px', padding:'28px', textAlign:'center', cursor: canUpload && !uploading ? 'pointer' : 'default' }}
                      onMouseEnter={e => { if(canUpload) e.currentTarget.style.borderColor='var(--red)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = canUpload ? c.inputBorder : c.border; }}>
                      {uploading
                        ? <div><div style={{ fontSize:'13px', color:c.text, marginBottom:'10px' }}>Uploading...</div><div style={{ background:c.th, borderRadius:'4px', height:'6px', overflow:'hidden', maxWidth:'280px', margin:'0 auto' }}><div style={{ height:'100%', background:'linear-gradient(90deg,#9C0000,#B40000)', borderRadius:'4px', width:`${uploadProgress}%`, transition:'width 0.2s' }}></div></div></div>
                        : <><div style={{ fontSize:'32px', marginBottom:'10px' }}>📄</div><div style={{ fontSize:'14px', fontWeight:'600', color: canUpload ? c.text : c.muted, marginBottom:'4px' }}>Drop file here or click to browse</div><div style={{ fontSize:'12px', color:c.muted }}>PDF, Excel, Word — any format</div></>}
                    </div>
                  </div>
                </div>
              )}

              {/* Sent files */}
              {exchClient && (
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <div style={s.cardTitle}>📤 Sent to Client</div>
                    <div style={{ fontSize:'12px', color:c.muted }}>{exchFiles.filter(f=>f.direction==='ntrg_to_client').length} files · {exchClient.name}</div>
                  </div>
                  <table style={s.table}><thead><tr><th style={s.th}>File</th><th style={s.th}>Type</th><th style={s.th}>Property</th><th style={s.th}>Date</th><th style={{ ...s.th, textAlign:'center' }}>Actions</th></tr></thead>
                  <tbody>{exchFiles.filter(f=>f.direction==='ntrg_to_client').length===0
                    ? <tr><td colSpan="5" style={{ ...s.td, textAlign:'center', color:c.muted }}>No files sent yet</td></tr>
                    : exchFiles.filter(f=>f.direction==='ntrg_to_client').map(f=>(
                      <tr key={f.id}>
                        <td style={s.td}><span style={{ fontSize:'15px', marginRight:'8px' }}>{fileIcon(f.file_name)}</span><span style={{ fontSize:'12px' }}>{f.file_name}</span></td>
                        <td style={{ ...s.td, fontSize:'12px', color:c.subtext }}>{f.file_type}</td>
                        <td style={{ ...s.td, fontSize:'12px', color:c.muted }}>{f.property_id ? (allProperties.find(p=>p.id===f.property_id)?.name||'—') : <span style={{ color:'#34d399', fontSize:'11px' }}>Client level</span>}</td>
                        <td style={{ ...s.td, fontSize:'12px', color:c.muted }}>{formatDate(f.created_at)}</td>
                        <td style={{ ...s.td, textAlign:'center' }}><div style={{ display:'flex', gap:'5px', justifyContent:'center' }}>
                          <button style={{...s.btnSm,background:'rgba(96,165,250,0.15)',color:'#60a5fa',border:'1px solid rgba(96,165,250,0.3)'}} onClick={async(e)=>{ e.stopPropagation(); try{ const u=await fileUrl(f.storage_path,'preview'); setPreviewFile({url:u,name:f.file_name,type:'pdf'}); }catch{ alert('Could not preview the file.'); } }}>👁</button>
                          <button style={{...s.btnSm,background:'rgba(52,211,153,0.12)',color:'#34d399',border:'1px solid rgba(52,211,153,0.3)'}} onClick={async(e)=>{ e.stopPropagation(); try{ const u=await fileUrl(f.storage_path,'open'); window.open(u,'_blank','noopener,noreferrer'); }catch{ alert('Could not open the file.'); } }}>↗</button>
                          <button style={s.btnSm} onClick={async()=>{ try{ const u=await fileUrl(f.storage_path,'download'); const a=document.createElement('a');a.href=u;a.download=f.file_name;a.click(); }catch{ alert('Could not download the file.'); } }}>⬇</button>
                          <button style={{...s.btnSm,background:'rgba(248,113,113,0.12)',color:'#f87171',border:'1px solid rgba(248,113,113,0.3)'}} onClick={async()=>{ if(!window.confirm(`Delete "${f.file_name}"?\nThis cannot be undone.`)) return; const { error: delErr } = await fileDelete(f.id); if(delErr){ alert('Could not delete: '+delErr.message); return; } setFiles(prev=>prev.filter(x=>x.id!==f.id)); }}>🗑</button>
                        </div></td>
                      </tr>
                    ))}</tbody></table>
                </div>
              )}

              {/* Received files */}
              {exchClient && (
                <div style={s.card}>
                  <div style={s.cardHeader}>
                    <div style={s.cardTitle}>📥 Received from Client</div>
                    <div style={{ fontSize:'12px', color:c.muted }}>{exchFiles.filter(f=>f.direction==='client_to_ntrg').length} files · {exchClient.name}</div>
                  </div>
                  {/* Filing modal for received files */}
                  {filingFile && (
                    <div style={{ margin:'0 0 12px', padding:'14px 16px', background:isDark?'rgba(180,0,0,0.06)':'rgba(180,0,0,0.05)', border:'1px solid rgba(180,0,0,0.25)', borderRadius:'10px' }}>
                      <div style={{ fontSize:'13px', fontWeight:'700', color:c.text, marginBottom:'10px' }}>📂 File: <span style={{ fontWeight:'400', color:c.subtext }}>{filingFile.file_name}</span></div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                        <div>
                          <div style={{ fontSize:'11px', color:c.muted, fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'5px' }}>File Type</div>
                          <select style={{ ...s.select, width:'100%' }} value={filingType} onChange={e => setFilingType(e.target.value)}>
                            <option value="">— Select type —</option>
                            <DocTypeOptions vocab={vocab} current={filingType} />
                          </select>
                        </div>
                        <div>
                          <div style={{ fontSize:'11px', color:c.muted, fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'5px' }}>Property (optional)</div>
                          <select style={{ ...s.select, width:'100%' }} value={filingProp?.id || ''} onChange={e => setFilingProp(properties.find(p=>p.id===e.target.value)||null)}>
                            <option value="">— Client level —</option>
                            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:'8px' }}>
                        <button style={s.btn} onClick={async () => {
                          // Folder and type from the folder table — keys and stored names both written.
                          const t = vocab?.type(filingType);
                          if (!t) { alert('Pick a file type first.'); return; }
                          const fo = vocab.folder(t.folder_key);
                          const docFields = { file_type: t.legacy_name, type_key: t.key, folder_name: fo.legacy_name, folder_key: fo.key };
                          await supabase.from('file_exchange').update({ ...docFields, property_id: filingProp?.id || filingFile.property_id, filed_by: staffName, filed_at: new Date().toISOString() }).eq('id', filingFile.id);
                          setFiles(prev => prev.map(x => x.id === filingFile.id ? { ...x, ...docFields, property_id: filingProp?.id || x.property_id } : x));
                          setFilingFile(null); setFilingType(''); setFilingProp(null);
                        }}>✓ Save Filing</button>
                        <button style={s.btnBack} onClick={() => { setFilingFile(null); setFilingType(''); setFilingProp(null); }}>Cancel</button>
                      </div>
                    </div>
                  )}
                  <table style={s.table}><thead><tr><th style={s.th}>File</th><th style={s.th}>Type</th><th style={s.th}>Property</th><th style={s.th}>Uploaded By</th><th style={s.th}>Date</th><th style={{ ...s.th, textAlign:'center' }}>Actions</th></tr></thead>
                  <tbody>{exchFiles.filter(f=>f.direction==='client_to_ntrg').length===0
                    ? <tr><td colSpan="6" style={{ ...s.td, textAlign:'center', color:c.muted }}>No files received yet</td></tr>
                    : exchFiles.filter(f=>f.direction==='client_to_ntrg').map(f=>(
                      <tr key={f.id} style={{ background: (!f.type_key || f.type_key === 'other') ? (isDark?'rgba(251,191,36,0.04)':'rgba(251,191,36,0.04)') : 'transparent' }}>
                        <td style={s.td}>
                          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                            <span style={{ fontSize:'15px' }}>{fileIcon(f.file_name)}</span>
                            <span style={{ fontSize:'12px' }}>{f.file_name}</span>
                            {(!f.type_key || f.type_key === 'other') && <span style={{ fontSize:'11px', background:'rgba(251,191,36,0.15)', color:'#fbbf24', border:'1px solid rgba(251,191,36,0.3)', borderRadius:'10px', padding:'1px 6px', whiteSpace:'nowrap' }}>Needs filing</span>}
                          </div>
                        </td>
                        <td style={{ ...s.td, fontSize:'12px', color: (!f.type_key||f.type_key==='other') ? '#fbbf24' : c.subtext }}>{vocab?.type(f.type_key)?.name || f.file_type || '—'}</td>
                        <td style={{ ...s.td, fontSize:'12px', color:c.muted }}>{f.property_id ? (allProperties.find(p=>p.id===f.property_id)?.name||'—') : <span style={{ color:'#34d399', fontSize:'11px' }}>Client level</span>}</td>
                        <td style={{ ...s.td, fontSize:'12px', color:c.subtext }}>{f.uploaded_by_name}</td>
                        <td style={{ ...s.td, fontSize:'12px', color:c.muted }}>{formatDate(f.created_at)}</td>
                        <td style={{ ...s.td, textAlign:'center' }}><div style={{ display:'flex', gap:'5px', justifyContent:'center' }}>
                          {(!f.type_key || f.type_key === 'other') && <button style={{...s.btnSm,background:'rgba(251,191,36,0.12)',color:'#fbbf24',border:'1px solid rgba(251,191,36,0.3)'}} onClick={() => { setFilingFile(f); setFilingType(f.type_key && f.type_key !== 'other' ? f.type_key : ''); setFilingProp(null); }}>📂 File</button>}
                          <button style={{...s.btnSm,background:'rgba(96,165,250,0.15)',color:'#60a5fa',border:'1px solid rgba(96,165,250,0.3)'}} onClick={async(e)=>{ e.stopPropagation(); try{ const u=await fileUrl(f.storage_path,'preview'); setPreviewFile({url:u,name:f.file_name,type:'pdf'}); }catch{ alert('Could not preview the file.'); } }}>👁</button>
                          <button style={{...s.btnSm,background:'rgba(52,211,153,0.12)',color:'#34d399',border:'1px solid rgba(52,211,153,0.3)'}} onClick={async(e)=>{ e.stopPropagation(); try{ const u=await fileUrl(f.storage_path,'open'); window.open(u,'_blank','noopener,noreferrer'); }catch{ alert('Could not open the file.'); } }}>↗</button>
                          <button style={s.btnSm} onClick={async()=>{ try{ const u=await fileUrl(f.storage_path,'download'); const a=document.createElement('a');a.href=u;a.download=f.file_name;a.click(); }catch{ alert('Could not download the file.'); } }}>⬇</button>
                          <button style={{...s.btnSm,background:'rgba(248,113,113,0.12)',color:'#f87171',border:'1px solid rgba(248,113,113,0.3)'}} onClick={async()=>{ if(!window.confirm(`Delete "${f.file_name}"?\nThis cannot be undone.`)) return; const { error: delErr } = await fileDelete(f.id); if(delErr){ alert('Could not delete: '+delErr.message); return; } setFiles(prev=>prev.filter(x=>x.id!==f.id)); }}>🗑</button>
                        </div></td>
                      </tr>
                    ))}</tbody></table>
                </div>
              )}

            </div>
            );
          })()}

          {/* MANAGE USERS */}
          {activePage==='users' && (() => {
            const activeUsers = portalUsers.filter(u => u.status !== 'archived');
            const archivedUsers = portalUsers.filter(u => u.status === 'archived');
            const filteredUsers = (showArchived ? archivedUsers : activeUsers).filter(u =>
              !userSearch || u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase()) || u.title?.toLowerCase().includes(userSearch.toLowerCase())
            );
            const clientProperties = properties;

            return (
            <div style={{ width:'100%' }}>
              <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'6px' }}>Manage Users</div>
              <div style={{ fontSize:'13px', color:c.muted, marginBottom:'20px' }}>Add and manage client portal users, assign property access, and send invitations.</div>

              {/* Client selector */}
              <div style={s.card}>
                <div style={s.cardHeader}><div style={s.cardTitle}>Select Client</div></div>
                <div style={{ padding:'14px 16px' }}>
                  <select style={{ ...s.select, width:'100%', maxWidth:'420px' }} value={usersClient?.id || ''} onChange={e => {
                    const cl = allClients.find(c2 => c2.id === e.target.value);
                    setUsersClient(cl || null);
                    setManageAccessProp(null); setManageAccessUser(null); setShowAddUser(false); setUserSearch('');
                    loadUsersForClient(cl);
                  }}>
                    <option value="">— Select a client to manage users —</option>
                    {allClients.map(cl => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
                  </select>
                </div>
              </div>

              {usersClient && (<>

                {/* Stats bar */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', margin:'16px 0' }}>
                  {[
                    { label:'Active Users', value: activeUsers.length, color:'52,211,153' },
                    { label:'Pending Invite', value: activeUsers.filter(u=>u.status==='pending').length, color:'251,191,36' },
                    { label:'Properties', value: clientProperties.length, color:'180,0,0' },
                    { label:'Archived', value: archivedUsers.length, color:'167,139,250' },
                  ].map(st => (
                    <div key={st.label} style={{ ...s.statCard, padding:'14px 16px' }}>
                      <div style={{ fontSize:'11px', color:c.muted, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'4px' }}>{st.label}</div>
                      <div style={{ fontSize:'24px', fontWeight:'700', color:`rgb(${st.color})`, fontFamily:"'Archivo',sans-serif" }}>{st.value}</div>
                    </div>
                  ))}
                </div>

                {/* View toggle + actions */}
                <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px', flexWrap:'wrap' }}>
                  <div style={{ display:'flex', background:isDark?'rgba(26,34,56,0.6)':'var(--bg)', borderRadius:'8px', padding:'3px', gap:'2px' }}>
                    {[['users','👤 By User'],['properties','🏢 By Property']].map(([v,label]) => (
                      <button key={v} onClick={() => { setUsersView(v); setManageAccessProp(null); setManageAccessUser(null); }}
                        style={{ padding:'6px 14px', borderRadius:'6px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:'600', fontFamily:"'Public Sans',system-ui,sans-serif", background: usersView===v ? (isDark?'#2F3854':'#fff') : 'transparent', color: usersView===v ? c.text : c.muted, boxShadow: usersView===v ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <input style={{ ...s.input, flex:1, maxWidth:'280px' }} placeholder="Search users..." value={userSearch} onChange={e=>setUserSearch(e.target.value)} />
                  <div style={{ marginLeft:'auto', display:'flex', gap:'8px' }}>
                    <button onClick={() => setShowArchived(!showArchived)} style={{ ...s.btnGhost, fontSize:'12px', color: showArchived ? '#a78bfa' : c.muted }}>
                      {showArchived ? '← Active Users' : `🗄 Archived (${archivedUsers.length})`}
                    </button>
                    {!showArchived && <button onClick={() => setShowAddUser(!showAddUser)} style={s.btn}>+ Add User</button>}
                  </div>
                </div>

                {/* Add user form */}
                {showAddUser && (
                  <div style={{ ...s.card, marginBottom:'14px', border:'1px solid rgba(180,0,0,0.3)', background:isDark?'rgba(180,0,0,0.04)':'rgba(180,0,0,0.03)' }}>
                    <div style={s.cardHeader}><div style={s.cardTitle}>➕ Add New User — {usersClient.name}</div></div>
                    <div style={{ padding:'16px', display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'12px' }}>
                      <div>
                        <div style={{ fontSize:'11px', color:c.muted, fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'5px' }}>Full Name *</div>
                        <input style={s.input} placeholder="Jane Smith" value={newUser.name} onChange={e=>setNewUser(p=>({...p,name:e.target.value}))} />
                      </div>
                      <div>
                        <div style={{ fontSize:'11px', color:c.muted, fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'5px' }}>Email *</div>
                        <input style={s.input} placeholder="jane@company.com" type="email" value={newUser.email} onChange={e=>setNewUser(p=>({...p,email:e.target.value}))} />
                      </div>
                      <div>
                        <div style={{ fontSize:'11px', color:c.muted, fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'5px' }}>Title</div>
                        <input style={s.input} placeholder="Asset Manager" value={newUser.title} onChange={e=>setNewUser(p=>({...p,title:e.target.value}))} />
                      </div>
                    </div>
                    <div style={{ padding:'0 16px 16px', display:'flex', alignItems:'center', gap:'16px' }}>
                      <label style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer', fontSize:'13px', color:c.text }}>
                        <input type="checkbox" checked={newUser.is_primary} onChange={e=>setNewUser(p=>({...p,is_primary:e.target.checked}))} style={{ accentColor:'var(--red)', width:'16px', height:'16px' }} />
                        Primary Contact (gets admin role in client portal)
                      </label>
                      <div style={{ marginLeft:'auto', display:'flex', gap:'8px' }}>
                        <button onClick={() => { setShowAddUser(false); setNewUser({name:'',email:'',title:'',role:'user',is_primary:false}); }} style={s.btnGhost}>Cancel</button>
                        <button onClick={addPortalUser} disabled={!newUser.name||!newUser.email||addingUser} style={{ ...s.btn, opacity:(!newUser.name||!newUser.email)?0.5:1 }}>
                          {addingUser ? 'Adding...' : '✓ Add User'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── BY USER VIEW ── */}
                {usersView === 'users' && (
                  <div style={s.card}>
                    <div style={s.cardHeader}>
                      <div style={s.cardTitle}>{showArchived ? '🗄 Archived Users' : '👤 Portal Users'} — {usersClient.name}</div>
                      <div style={{ fontSize:'12px', color:c.muted }}>{filteredUsers.length} user{filteredUsers.length!==1?'s':''}</div>
                    </div>
                    {filteredUsers.length === 0 ? (
                      <div style={{ padding:'32px', textAlign:'center', fontSize:'13px', color:c.muted }}>
                        {showArchived ? 'No archived users' : 'No users yet — add one above'}
                      </div>
                    ) : filteredUsers.map(u => {
                      const assignedProps = clientProperties.filter(p => (u.client_access_properties||[]).some(a=>a.property_id===p.id));
                      const isExpanded = manageAccessUser?.id === u.id;
                      const isPending = u.status === 'pending';
                      const isArchived = u.status === 'archived';
                      return (
                        <React.Fragment key={u.id}>
                          <div style={{ padding:'14px 18px', borderBottom:`1px solid ${c.border}`, display:'flex', alignItems:'center', gap:'14px', background: isExpanded ? (isDark?'rgba(180,0,0,0.05)':'rgba(180,0,0,0.03)') : 'transparent' }}>
                            {/* Avatar */}
                            <div style={{ width:'40px', height:'40px', borderRadius:'50%', background:`linear-gradient(135deg,#B40000,#9C0000)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', fontWeight:'700', color:'var(--ink)', flexShrink:0 }}>
                              {u.name?.charAt(0) || '?'}
                            </div>
                            {/* Info */}
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                                <div style={{ fontWeight:'700', fontSize:'13px', color:c.text }}>{u.name}</div>
                                {u.is_primary && <span style={{ fontSize:'11px', background:'rgba(180,0,0,0.15)', color:'var(--red)', border:'1px solid rgba(180,0,0,0.3)', borderRadius:'10px', padding:'1px 7px' }}>Primary</span>}
                                {isPending && <span style={{ fontSize:'11px', background:'rgba(251,191,36,0.12)', color:'#fbbf24', border:'1px solid rgba(251,191,36,0.3)', borderRadius:'10px', padding:'1px 7px' }}>⏳ Invite Pending</span>}
                                {isArchived && <span style={{ fontSize:'11px', background:'rgba(167,139,250,0.12)', color:'#a78bfa', border:'1px solid rgba(167,139,250,0.3)', borderRadius:'10px', padding:'1px 7px' }}>Archived</span>}
                              </div>
                              <div style={{ fontSize:'12px', color:c.muted, marginTop:'2px' }}>{u.title ? `${u.title} · ` : ''}{u.email}</div>
                              {!isArchived && <div style={{ fontSize:'11px', color: assignedProps.length === 0 ? '#fbbf24' : '#34d399', marginTop:'3px' }}>
                                {assignedProps.length === 0 ? '⚠ No properties assigned' : `✓ ${assignedProps.length} propert${assignedProps.length===1?'y':'ies'} assigned`}
                              </div>}
                            </div>
                            {/* Actions */}
                            <div style={{ display:'flex', gap:'6px', flexShrink:0, flexWrap:'wrap', justifyContent:'flex-end' }}>
                              {!isArchived && (
                                <>
                                  <a href={generateInviteMailto(u)} style={{ ...s.btnSm, background:'rgba(180,0,0,0.12)', color:'var(--red)', border:'1px solid rgba(180,0,0,0.3)', textDecoration:'none', display:'inline-block' }}>✉ Send Invite</a>
                                  <button onClick={() => setManageAccessUser(isExpanded ? null : u)} style={{ ...s.btnSm, background: isExpanded ? 'rgba(180,0,0,0.15)' : 'transparent', color: isExpanded ? 'var(--red)' : c.subtext, border:`1px solid ${isExpanded ? 'var(--red)' : c.border}` }}>
                                    {isExpanded ? 'Close ✕' : '🏢 Assign Properties'}
                                  </button>
                                  <button onClick={() => archivePortalUser(u.id)} style={{ ...s.btnSm, background:'rgba(248,113,113,0.08)', color:'#f87171', border:'1px solid rgba(248,113,113,0.2)' }}>Archive</button>
                                </>
                              )}
                              {isArchived && (
                                <button onClick={() => reactivatePortalUser(u.id)} style={{ ...s.btnSm, background:'rgba(52,211,153,0.1)', color:'#34d399', border:'1px solid rgba(52,211,153,0.2)' }}>↺ Reactivate</button>
                              )}
                            </div>
                          </div>
                          {/* Property assignment panel */}
                          {isExpanded && (
                            <div style={{ padding:'16px 20px', background:isDark?'rgba(26,34,56,0.5)':'#f8f9fc', borderBottom:`1px solid ${c.border}` }}>
                              <div style={{ fontSize:'11px', color:'var(--red)', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'12px' }}>Property Access — {u.name}</div>
                              {clientProperties.length === 0 ? (
                                <div style={{ fontSize:'12px', color:c.muted }}>No properties found for this client.</div>
                              ) : (
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:'8px' }}>
                                  {clientProperties.map(p => {
                                    const assigned = (u.client_access_properties||[]).some(a=>a.property_id===p.id);
                                    return (
                                      <div key={p.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'8px', background: assigned?'rgba(52,211,153,0.06)':isDark?'rgba(0,0,0,0.2)':'#fff', border: assigned?'1px solid rgba(52,211,153,0.25)':`1px solid ${c.border}` }}>
                                        <div style={{ flex:1, minWidth:0 }}>
                                          <div style={{ fontSize:'12px', fontWeight:'600', color:c.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.name}</div>
                                          <div style={{ fontSize:'11px', color:c.muted }}>{p.city}{p.state?`, ${p.state}`:''}</div>
                                        </div>
                                        <button onClick={async () => {
                                          await togglePropertyAssignment(u.id, p.id, assigned);
                                          setManageAccessUser(prev => prev ? { ...prev, client_access_properties: assigned ? (prev.client_access_properties||[]).filter(a=>a.property_id!==p.id) : [...(prev.client_access_properties||[]),{property_id:p.id}] } : null);
                                        }} style={{ padding:'4px 10px', borderRadius:'6px', border:'none', cursor:'pointer', fontSize:'11px', fontWeight:'700', fontFamily:"'Public Sans',system-ui,sans-serif", background:assigned?'rgba(248,113,113,0.15)':'linear-gradient(135deg,#B40000,#9C0000)', color:assigned?'#f87171':'var(--ink)', whiteSpace:'nowrap', flexShrink:0 }}>
                                          {assigned ? '✕ Remove' : '+ Add'}
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}

                {/* ── BY PROPERTY VIEW ── */}
                {usersView === 'properties' && (
                  <div style={s.card}>
                    <div style={s.cardHeader}>
                      <div style={s.cardTitle}>🏢 Properties — {usersClient.name}</div>
                      <div style={{ fontSize:'12px', color:c.muted }}>{clientProperties.length} propert{clientProperties.length!==1?'ies':'y'}</div>
                    </div>
                    {clientProperties.length === 0 ? (
                      <div style={{ padding:'32px', textAlign:'center', fontSize:'13px', color:c.muted }}>No properties found for this client</div>
                    ) : clientProperties.map(p => {
                      const assignedUsers = activeUsers.filter(u=>(u.client_access_properties||[]).some(a=>a.property_id===p.id));
                      const isExpanded = manageAccessProp?.id === p.id;
                      return (
                        <React.Fragment key={p.id}>
                          <div style={{ padding:'14px 18px', borderBottom:`1px solid ${c.border}`, display:'flex', alignItems:'center', gap:'14px', background: isExpanded?(isDark?'rgba(180,0,0,0.05)':'rgba(180,0,0,0.03)'):'transparent' }}>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontWeight:'700', fontSize:'13px', color:c.text }}>{p.name}</div>
                              <div style={{ fontSize:'12px', color:c.muted, marginTop:'2px' }}>{p.city}{p.state?`, ${p.state}`:''}{p.master_property_id?` · #${p.master_property_id}`:''}</div>
                            </div>
                            <div style={{ flex:1 }}>
                              {assignedUsers.length === 0
                                ? <span style={{ ...s.badge('251,191,36'), fontSize:'11px' }}>No users assigned</span>
                                : <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>{assignedUsers.map(u=><span key={u.id} title={u.email} style={{ ...s.badge('52,211,153'), fontSize:'11px' }}>{u.name}</span>)}</div>}
                            </div>
                            <button onClick={() => setManageAccessProp(isExpanded?null:p)} style={{ ...s.btnSm, background:isExpanded?'rgba(180,0,0,0.15)':'transparent', color:isExpanded?'var(--red)':c.subtext, border:`1px solid ${isExpanded?'var(--red)':c.border}`, flexShrink:0 }}>
                              {isExpanded ? 'Close ✕' : 'Manage Access'}
                            </button>
                          </div>
                          {isExpanded && (
                            <div style={{ padding:'16px 20px', background:isDark?'rgba(26,34,56,0.5)':'#f8f9fc', borderBottom:`1px solid ${c.border}` }}>
                              <div style={{ fontSize:'11px', color:'var(--red)', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'12px' }}>User Access — {p.name}</div>
                              {activeUsers.length === 0 ? (
                                <div style={{ fontSize:'12px', color:c.muted }}>No active users for this client. Add users first.</div>
                              ) : (
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:'8px' }}>
                                  {activeUsers.map(u => {
                                    const assigned = (u.client_access_properties||[]).some(a=>a.property_id===p.id);
                                    return (
                                      <div key={u.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'8px', background:assigned?'rgba(52,211,153,0.06)':isDark?'rgba(0,0,0,0.2)':'#fff', border:assigned?'1px solid rgba(52,211,153,0.25)':`1px solid ${c.border}` }}>
                                        <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:'linear-gradient(135deg,#B40000,#9C0000)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:'700', color: 'white', flexShrink:0 }}>{u.name?.charAt(0)||'?'}</div>
                                        <div style={{ flex:1, minWidth:0 }}>
                                          <div style={{ fontSize:'12px', fontWeight:'600', color:c.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.name}</div>
                                          <div style={{ fontSize:'11px', color:c.muted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.title || u.email}</div>
                                        </div>
                                        <button onClick={async () => {
                                          await togglePropertyAssignment(u.id, p.id, assigned);
                                          setPortalUsers(prev => prev.map(pu => pu.id===u.id ? { ...pu, client_access_properties: assigned ? (pu.client_access_properties||[]).filter(a=>a.property_id!==p.id) : [...(pu.client_access_properties||[]),{property_id:p.id}] } : pu));
                                        }} style={{ padding:'4px 10px', borderRadius:'6px', border:'none', cursor:'pointer', fontSize:'11px', fontWeight:'700', fontFamily:"'Public Sans',system-ui,sans-serif", background:assigned?'rgba(248,113,113,0.15)':'linear-gradient(135deg,#B40000,#9C0000)', color:assigned?'#f87171':'var(--ink)', whiteSpace:'nowrap', flexShrink:0 }}>
                                          {assigned?'✕ Remove':'+ Add'}
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}

              </>)}
            </div>
            );
          })()}

          {/* PROPERTIES */}
          {activePage==='properties' && (
            <PropertiesGrid
              c={c}
              s={s}
              isDark={isDark}
              searchQ={searchQ}
              setSearchQ={setSearchQ}
              selectedClient={selectedClient}
              setSelectedClient={setSelectedClient}
              showRetired={showRetired}
              setShowRetired={setShowRetired}
              currentValueYear={currentValueYear}
              setValueYear={setValueYear}
              yearRange={yearRange}
              propertiesLoaded={propertiesLoaded}
              expandedProps={expandedProps}
              setExpandedProps={setExpandedProps}
              allProperties={allProperties}
              parcels={parcels}
              valueByParcelId={yearValues}
              prorataRows={prorataRows}
              allClients={allClients}
              resolvedPropertyColumns={resolvedPropertyColumns}
              savePropertyColumns={savePropertyColumns}
              columnPickerOpen={modal === 'colsProps'}
              setColumnPickerOpen={(v) => navigateTo({ modal: v ? 'colsProps' : null })}
              staffUser={staffUser}
              addPropertyMsg={addPropertyMsg}
              setAddPropertyOpen={() => navigateTo({ modal: 'addProperty' })}
              navigateTo={navigateTo}
            />
          )}

          {/* PARCELS */}
          {activePage==='parcels' && (() => {
            // ⛔ CLIENT AND PARCEL / PROPERTY ARE PINNED ON THE LEFT, AND SO SHOWN FIRST (Philip 2026-10-27): fixed widths
            // (sticky cells need known offsets), opaque backgrounds, the rest scrolling under them.
            const PIN_W = { client: 160, parcel_name: 200 };
            const visibleParcelCols = resolvedParcelsColumns.filter(col => col.visible);
            const parcelCols = [...Object.keys(PIN_W).map(k => visibleParcelCols.find(col => col.key === k)).filter(Boolean),
              ...visibleParcelCols.filter(col => !(col.key in PIN_W))];
            const pinned = parcelCols.filter(col => col.key in PIN_W);
            const pinAt = (col, header) => {
              const i = pinned.indexOf(col);
              if (i < 0) return {};
              const w = PIN_W[col.key];
              const left = pinned.slice(0, i).reduce((sum, p2) => sum + PIN_W[p2.key], 0);
              return { width: `${w}px`, minWidth: `${w}px`, maxWidth: `${w}px`,
                ...pinCell(left, { header, last: i === pinned.length - 1, background: header ? `linear-gradient(${c.th}, ${c.th}), ${c.card}` : c.card }) };
            };
            return (
              <div style={{ width:'100%' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'6px' }}>
                  <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text }}>Parcels</div>
                  {/* Naming a year the reader cannot change is only half the fix — the count would state a
                      filter and offer no way to operate it. No optionChip: this is a list of parcels across
                      every client, so "is this year baseline" has no single answer here either. */}
                  <ValueYearSelect years={yearRange} value={currentValueYear} onChange={setValueYear} s={s} c={c} />
                </div>
                <div style={{ fontSize:'13px', color:c.muted, marginBottom:'22px' }}>
                  {filteredParcels.length.toLocaleString()} parcel{filteredParcels.length === 1 ? '' : 's'} in {currentValueYear}
                  {parcelsOtherYears > 0 && ` · ${parcelsOtherYears.toLocaleString()} in other years`}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 220px 160px auto auto', gap:'10px', marginBottom:'16px', alignItems:'center' }}>
                  <input style={s.input} placeholder="Search by parcel name, account #, client, assessor, address, master ID..." value={parcelSearch} onChange={e=>setParcelSearch(e.target.value)} />
                  <select style={{ ...s.select, width:'100%' }} value={parcelClientFilter} onChange={e=>setParcelClientFilter(e.target.value)}><option value="">All Clients</option>{parcelClients.map(cl=><option key={cl} value={cl}>{cl}</option>)}</select>
                  <select style={{ ...s.select, width:'100%' }} value={parcelStateFilter} onChange={e=>setParcelStateFilter(e.target.value)}><option value="">All States</option>{parcelStates.map(st=><option key={st} value={st}>{st}</option>)}</select>
                  <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:c.subtext, cursor:'pointer', whiteSpace:'nowrap', padding:'0 8px' }}>
                    <input type="checkbox" checked={showRetired} onChange={e=>setShowRetired(e.target.checked)} style={{ cursor:'pointer' }} />
                    Show retired
                  </label>
                  <div style={{ position:'relative' }}>
                    <button onClick={() => navigateTo({ modal: modal === 'colsParcels' ? null : 'colsParcels' })} disabled={!staffUser?.id}
                      title={staffUser?.id ? 'Show, hide, and reorder columns' : 'Sign-in required to save column layout'}
                      style={{ padding:'8px 14px', background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--line-soft)', border:`1px solid ${c.border}`, borderRadius:'8px', color:c.text, fontSize:'12px', fontWeight:'600', cursor: staffUser?.id ? 'pointer' : 'default', fontFamily:"'Public Sans',system-ui,sans-serif", whiteSpace:'nowrap', opacity: staffUser?.id ? 1 : 0.5 }}>
                      ⊞ Columns ▾
                    </button>
                    {modal === 'colsParcels' && (
                      <ColumnPicker
                        columns={resolvedParcelsColumns}
                        onSave={async (newCols) => { await saveParcelsColumns(newCols); navigateTo({ modal: null }); }}
                        onClose={() => navigateTo({ modal: null })}
                        c={c}
                        s={s}
                        isDark={isDark}
                        disabled={!staffUser?.id}
                      />
                    )}
                  </div>
                </div>
                {/* Compared against the IN-YEAR population, not the table total: "3 of 3,795" invited the
                    reader to widen their search for parcels that were never behind it. */}
                <div style={{ fontSize:'12px', color:c.muted, marginBottom:'10px' }}>Showing {filteredParcels.length.toLocaleString()} of {parcelsInYear.length.toLocaleString()} parcels in {currentValueYear}</div>
                <div style={s.card}>
                  <ScrollX>
                    <table style={{ ...s.table, ...PINNED_TABLE, minWidth:'1400px' }}>
                      <thead>
                        <tr>
                          {parcelCols.map(col => <th key={col.key} style={{ ...s.th, textAlign: col.align, ...pinAt(col, true) }}>{col.label}</th>)}
                        </tr>
                      </thead>
                      <tbody key={redrawKey(filteredParcels.slice(0, 200).map(p => p.id))}>
                        {filteredParcels.slice(0, 200).map(p => (
                          <tr key={p.id} style={{ cursor:'pointer', opacity: representedInYear(p, propRetireById.get(p.property_id), currentValueYear) ? 1 : 0.6 }}
                            onClick={() => navigateTo({ drillParcel:p })}
                            onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            {parcelCols.map(col => (
                              <td key={col.key}
                                style={{ ...s.td, textAlign: col.align, ...pinAt(col, false) }}
                                onClick={col.stopRowClick ? (e => e.stopPropagation()) : undefined}>
                                {col.render(p)}
                              </td>
                            ))}
                          </tr>
                        ))}
                        {filteredParcels.length > 200 && <tr><td colSpan={parcelCols.length} style={{ ...s.td, textAlign:'center', color:c.muted, fontStyle:'italic' }}>Showing first 200 — use filters to narrow down</td></tr>}
                        {/* ⛔ "No parcels match your search" NAMED THE WRONG CONTROL. When the year is what
                            emptied the table, no amount of editing the search box helps — the same dead end as
                            "resolve that bill first". Search is only blamed when a search is actually on. */}
                        {filteredParcels.length === 0 && <tr><td colSpan={parcelCols.length} style={{ ...s.td, textAlign:'center', color:c.muted, padding:'18px 16px' }}>{(() => {
                          const searching = !!parcelSearch || !!parcelClientFilter || !!parcelStateFilter;
                          if (!parcels.length) return 'No parcels yet.';
                          if (parcelsInYear.length && searching) return 'No parcels match your search.';
                          if (!parcelsInYear.length) return (<>
                            <div style={{ fontWeight:600, color:c.text }}>No parcels represented in {currentValueYear}.</div>
                            <div style={{ marginTop:'4px' }}>
                              {parcels.length.toLocaleString()} parcel{parcels.length === 1 ? '' : 's'} on file
                              {firstYearWithParcels
                                ? <>, represented from{' '}
                                    <button type="button" onClick={() => setValueYear(firstYearWithParcels)}
                                      style={{ background:'none', border:'none', padding:0, font:'inherit', color:'var(--red)', fontWeight:700, cursor:'pointer', textDecoration:'underline' }}>
                                      {firstYearWithParcels}
                                    </button>{' '}— switch to see {parcels.length === 1 ? 'it' : 'them'}.</>
                                : ' represented in other years.'}
                            </div>
                          </>);
                          return 'No parcels match your search.';
                        })()}</td></tr>}
                      </tbody>
                    </table>
                    {/* ⛔ ONCE PER TABLE, NOT PER ROW. Shown only when a visible row has no parcel_year_data for the
                        selected year, so it disappears as the back-years are loaded. "Not recorded" is deliberate:
                        the gap is in what we hold, not in what the app can show. */}
                    {anyYearDataMissing(filteredParcels) && (
                      <div style={{ padding: '8px 14px', fontSize: '11px', color: c.muted, borderTop: `1px solid ${c.cardBorder}` }}>
                        Some parcels have no data recorded for {currentValueYear} — their Appeal Deadline and Services show “—”. Earlier years are still being loaded.
                      </div>
                    )}
                  </ScrollX>
                </div>
              </div>
            );
          })()}

          {/* DOCUMENT SCANNER */}
          {activePage==='scanner' && <div style={{ margin:'-24px -32px' }}><ErrorBoundary isDark={isDark} label="The scanner hit an error" resetLabel="← Back to dashboard" onReset={() => navigateTo({ page:'dashboard' })}><DocumentScanner darkMode={darkMode} staffUser={staffUser} allProperties={allProperties} allClients={allClients} onDrillParcel={(p) => navigateTo({ drillParcel:p })} /></ErrorBoundary></div>}
          {activePage==='taxbill_worklist' && (modal === 'taxbillScan'
            ? <div style={{ margin:'-24px -32px' }}><ErrorBoundary isDark={isDark} label="The scanner hit an error" resetLabel="← Back to the worklist" onReset={() => { setTaxbillScan(null); navigateTo({ modal: null }); }}><DocumentScanner darkMode={darkMode} staffUser={staffUser} allProperties={allProperties} allClients={allClients} onDrillParcel={(p) => navigateTo({ drillParcel:p })}
                taxbillLaunch={taxbillScan} onScanComplete={() => { setTaxbillScan(null); navigateTo({ modal: null }); reloadBillCounts(); }} /></ErrorBoundary></div>
            : <TaxBillWorklist darkMode={darkMode} staffUser={staffUser}
                onScanRow={(r, yr) => { setTaxbillScan({ mode:'row', parcel_id:r.parcel_id, property_id:r.property_id, collector_id:r.collector_id, installment_number:r.installment_number, tax_year:yr }); navigateTo({ modal: 'taxbillScan' }); }}
                onScanNew={() => { setTaxbillScan({ mode:'match' }); navigateTo({ modal: 'taxbillScan' }); }}
                onOpenParcel={(r, yr) => openParcelDeepLink(r.parcel_id, 'taxes', yr, r.collector_id && r.installment_number != null ? { collectorId: r.collector_id, installmentNumber: r.installment_number } : null)}
                refreshKey={worklistRefresh}
                onAssignCollector={openParcelForCollectors} />)}
          {activePage==='taxbill_approvals' && <TaxBillApprovals darkMode={darkMode} staffUser={staffUser} onMutated={() => reloadBillCounts()} />}
          {activePage==='taxbill_transmittals' && <TaxBillTransmittals darkMode={darkMode} staffUser={staffUser} />}
          {activePage==='intake' && (
  <DocumentIntake
    darkMode={darkMode}
    staffUser={staffUser}
    allProperties={allProperties}
    allClients={allClients}
    onDrillParcel={(p) => navigateTo({ drillParcel:p })}
  />
)}

          {/* ADMIN CONSOLE */}
          {activePage==='admin' && <AdminPanel darkMode={darkMode} staffUser={staffUser} />}
          {activePage==='deliveries' && <ClientDeliveries darkMode={darkMode} staffUser={staffUser} />}
          {/* ── ONBOARDING — the single front door for creating records ───────────────────────────
              Cards are ordered by DATA DEPENDENCY, not by importance: a property needs a client and a
              parcel needs a property, so creating them out of order is impossible and the page says so
              on each card rather than letting someone find out by trying. Consultant is last despite
              needing no parent — it is not part of the client → property → parcel chain, and breaking
              the chain to slot it in would make the order look arbitrary.
              ⛔ EVERY CARD OPENS AN EXISTING MODAL. Nothing here re-implements a form. */}
          {activePage==='onboarding' && (() => {
            const card = { background:c.card, border:`1px solid ${c.cardBorder}`, borderRadius:'12px', padding:'18px 20px', display:'flex', flexDirection:'column', gap:'8px' };
            const step = { fontSize:'11px', fontWeight:'800', color:'var(--red)', letterSpacing:'0.08em' };
            const h    = { fontFamily:"'Archivo',sans-serif", fontSize:'17px', fontWeight:'700', color:c.text };
            const sub  = { fontSize:'12px', color:c.muted, lineHeight:'1.45' };
            const need = { fontSize:'11px', fontWeight:'700', color:c.subtext, background:isDark?'rgba(148,163,184,0.12)':'rgba(100,116,139,0.08)', borderRadius:'6px', padding:'3px 8px', alignSelf:'flex-start' };
            // Properties belonging to the client picked on the parcel card. Same client_id link the
            // Property View union uses — not the parcel-derived path, which would hide a brand-new
            // property that has no parcels yet, i.e. exactly the one being added to.
            const onbProps = onbParcelClientId ? (allProperties||[]).filter(pr => pr.client_id === onbParcelClientId).sort((a,b)=>(a.name||'').localeCompare(b.name||'')) : [];
            const onbChosen = onbProps.find(pr => pr.id === onbParcelPropertyId) || null;
            return (
              <div style={{ width:'100%', maxWidth:'900px' }}>
                <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'6px' }}>Onboarding</div>
                <div style={{ fontSize:'13px', color:c.muted, marginBottom:'22px' }}>Create a client, its properties, and their parcels. Each step needs the one above it.</div>
                <div style={{ display:'grid', gap:'14px' }}>

                  <div style={card}>
                    <div style={step}>STEP 1</div>
                    <div style={h}>New Client</div>
                    <div style={need}>Needs nothing — start here</div>
                    <div style={sub}>Creates the client record that properties, contacts and assignments hang off.</div>
                    <button style={{ ...s.btn, alignSelf:'flex-start', marginTop:'4px' }} onClick={() => navigateTo({ modal:'addClient' })}>+ New Client</button>
                  </div>

                  <div style={card}>
                    <div style={step}>STEP 2</div>
                    <div style={h}>New Property</div>
                    <div style={need}>Requires an existing client</div>
                    <div style={sub}>Creates a property under a client you pick in the form. Needs a Master Property ID and an address.</div>
                    <button style={{ ...s.btn, alignSelf:'flex-start', marginTop:'4px' }} onClick={() => navigateTo({ modal:'addProperty' })}>+ New Property</button>
                  </div>

                  <div style={card}>
                    <div style={step}>STEP 3</div>
                    <div style={h}>New Parcel</div>
                    <div style={need}>Requires an existing property</div>
                    <div style={sub}>Pick the property first — a parcel inherits its client, master property ID and state from it.</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'4px' }}>
                      <div>
                        <div style={{ fontSize:'10px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}>Client</div>
                        <select style={{ ...s.select, width:'100%' }} value={onbParcelClientId}
                          onChange={e => { setOnbParcelClientId(e.target.value); setOnbParcelPropertyId(''); }}>
                          <option value="">— Select a client —</option>
                          {(allClients||[]).map(cl => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize:'10px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}>Property</div>
                        <select style={{ ...s.select, width:'100%' }} value={onbParcelPropertyId} disabled={!onbParcelClientId}
                          onChange={e => setOnbParcelPropertyId(e.target.value)}>
                          <option value="">{onbParcelClientId ? (onbProps.length ? '— Select a property —' : 'This client has no properties yet') : 'Pick a client first'}</option>
                          {onbProps.map(pr => <option key={pr.id} value={pr.id}>{pr.name}{pr.master_property_id ? ` · #${pr.master_property_id}` : ''}</option>)}
                        </select>
                      </div>
                    </div>
                    <button style={{ ...s.btn, alignSelf:'flex-start', marginTop:'8px', opacity: onbChosen ? 1 : 0.5, cursor: onbChosen ? 'pointer' : 'not-allowed' }}
                      disabled={!onbChosen}
                      onClick={() => { setCreateParcelFor(onbChosen); navigateTo({ modal:'createParcel' }); }}>+ New Parcel</button>
                  </div>

                  <div style={card}>
                    <div style={step}>SEPARATE</div>
                    <div style={h}>New Consultant</div>
                    <div style={need}>Needs nothing — not part of the chain above</div>
                    <div style={sub}>Creates a consultant firm and its contacts. Assigned to properties afterwards.</div>
                    <button style={{ ...s.btn, alignSelf:'flex-start', marginTop:'4px' }} onClick={() => navigateTo({ modal:'addConsultant' })}>+ New Consultant</button>
                  </div>

                </div>
              </div>
            );
          })()}

          {activePage==='consultants' && <ConsultantsPage darkMode={darkMode} staffUser={staffUser} allClients={allClients} onDrillParcel={(p) => navigateTo({ drillParcel:p })} />}
          {activePage==='assessors_collectors' && <AssessorsCollectorsPage darkMode={darkMode} staffUser={staffUser} />}
          {activePage==='tablemgmt' && <TableManagementPage darkMode={darkMode} />}
          {activePage==='ntrg_empinfo' && (
            <div style={{ width:'100%' }}>
              <button style={s.btnBack} onClick={()=>navigateTo({ page:'ntrg' })}>← Back to NTRG</button>
              <EmployeeTable darkMode={darkMode} />
            </div>
          )}
          {activePage==='ntrg_orgchart' && (
            <div style={{ width:'100%' }}>
              <button style={s.btnBack} onClick={()=>navigateTo({ page:'ntrg' })}>← Back to NTRG</button>
              <OrgChart darkMode={darkMode} />
            </div>
          )}
          {activePage==='calendar' && <Calendar darkMode={darkMode} staffUser={staffUser} />}  
          {activePage==='activity' && <ActivityFeed darkMode={darkMode} staffUser={staffUser} />}
          {activePage==='tax_savings_packet' && <TaxSavingsPacket darkMode={darkMode} staffUser={staffUser} />}
          {activePage==='open_questions' && <OpenQuestions darkMode={darkMode} staffUser={staffUser} onOpenParcel={(parcelId, year) => openParcelDeepLink(parcelId, null, year)} />}

          {/* LIVE REPORTING — report-type picker (the single door to the report screens) */}
          {activePage==='reporting' && (
            <div style={{ width:'100%' }}>
              <button style={s.btnBack} onClick={() => navigateTo({ page:'dashboard' })}>← Back</button>
              <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'6px' }}>Live Reporting</div>
              <div style={{ fontSize:'13px', color:c.muted, marginBottom: reportNotice ? '10px' : '22px' }}>Choose a report type. More types arrive over time.</div>
              {reportNotice && (
                <div style={{ marginBottom:'18px', padding:'8px 12px', borderRadius:'8px', border:'1px solid var(--red)', background:'rgba(180,0,0,0.06)', color:'var(--red)', fontSize:'12px' }}>
                  {reportNotice}
                </div>
              )}
              {/* ⛔ TILES COME FROM THE REGISTRY, not from a list here. Adding a report type is a
                  definition file plus one registry line — the reason Appeal Results Summary and
                  Portfolio Tax Trend sat as hardcoded "Soon" stubs for months is that a new report
                  meant a new page AND a new tile. A registered definition wins over its
                  placeholder, so a tile flips from Soon to live the moment its definition lands. */}
              {/* MY SAVED REPORTS — this person's report_presets, newest name order. Opening one starts the report from its
                  saved options; Rename / Delete act on the card. */}
              {presetMsg && <div style={{ marginBottom:'12px', fontSize:'12px', color:'var(--red)' }}>{presetMsg}</div>}
              {presets.length > 0 && (<>
                <div style={{ fontSize:'12px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'8px' }}>My saved reports</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'24px' }}>
                  {presets.map((p) => {
                    const def = getReport(p.report_id);
                    const ys = p.options?.allYears ? 'all years' : (p.options?.years || []).join(', ');
                    const act = async (e, fn) => { e.stopPropagation(); const r = await fn(); if (r && !r.ok) setPresetMsg(r.error); else setPresets((await loadPresets(String(staffUser.id))).data || []); };
                    return (
                      <div key={p.id} role="button" tabIndex={0} onClick={() => { if (!def) return; setReportNotice(''); setReportPreset(p); setReportId(p.report_id); navigateTo({ page: 'report' }); }}
                        style={{ position:'relative', textAlign:'left', padding:'16px 18px', borderRadius:'12px', border:`2px solid ${c.cardBorder}`, cursor: def ? 'pointer' : 'default', background: isDark?'rgba(255,255,255,0.03)':'#ffffff', color:c.text, boxShadow: isDark?'none':'0 1px 4px rgba(0,0,0,0.05)' }}>
                        <div style={{ fontSize:'15px', fontWeight:700, marginBottom:'4px' }}>★ {p.name}</div>
                        <div style={{ fontSize:'12px', color:c.muted, lineHeight:1.4 }}>{def ? def.title : 'That report is no longer available.'}{ys ? ` · ${ys}` : ''}</div>
                        <div style={{ position:'absolute', top:'8px', right:'8px', display:'flex', gap:'4px' }}>
                          <button type="button" title="Rename" style={{ ...s.btnGhost, fontSize:'10px', padding:'1px 6px' }}
                            onClick={(e) => { const n = window.prompt('Rename this saved report', p.name); if (n && n.trim() && n.trim() !== p.name) act(e, () => renamePreset(p.id, n)); else e.stopPropagation(); }}>Rename</button>
                          <button type="button" title="Delete" style={{ ...s.btnGhost, fontSize:'10px', padding:'1px 6px', color:'#f87171' }}
                            onClick={(e) => { if (window.confirm(`Delete the saved report "${p.name}"?`)) act(e, () => deletePreset(p.id)); else e.stopPropagation(); }}>Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>)}
              {pickerSections({ isAdmin: isAdminStaff(staffUser) }).map((sec) => (
                <React.Fragment key={sec.title}>
                  <div style={{ fontSize:'12px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'8px' }}>{sec.title}</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'24px' }}>
                    {sec.tiles.map((rt, i) => (
                      <button key={rt.id || i} onClick={() => { if (!rt.soon) { setReportNotice(''); setReportPreset(null); setReportId(rt.id); navigateTo({ page: 'report' }); } }}
                        title={rt.soon ? 'Coming soon' : rt.title}
                        style={{ position:'relative', textAlign:'left', padding:'20px 18px', borderRadius:'12px', border:`2px solid ${c.cardBorder}`, cursor: rt.soon ? 'default' : 'pointer', background: isDark?(rt.soon?'rgba(255,255,255,0.02)':'rgba(255,255,255,0.03)'):(rt.soon?'#fafbfd':'#ffffff'), fontFamily:"'Public Sans',system-ui,sans-serif", color: rt.soon ? c.muted : c.text, opacity: rt.soon ? 0.7 : 1, transition:'all 0.15s', boxShadow: isDark?'none':'0 1px 4px rgba(0,0,0,0.05)' }}
                        onMouseEnter={e=>{ if(rt.soon) return; e.currentTarget.style.borderColor='var(--red)'; e.currentTarget.style.transform='scale(1.02)'; }}
                        onMouseLeave={e=>{ if(rt.soon) return; e.currentTarget.style.borderColor=c.cardBorder; e.currentTarget.style.transform='scale(1)'; }}>
                        <div style={{ fontSize:'15px', fontWeight:700, marginBottom:'6px' }}>{rt.title}</div>
                        <div style={{ fontSize:'12px', color:c.muted, lineHeight:1.4 }}>{rt.description}</div>
                        {rt.soon && <span style={{ position:'absolute', top:'10px', right:'10px', fontSize:'9px', fontWeight:'700', color:'var(--red)', background:'rgba(180,0,0,0.12)', border:'1px solid rgba(180,0,0,0.3)', borderRadius:'10px', padding:'1px 6px' }}>Soon</span>}
                      </button>
                    ))}
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
          {/* Every report renders through the generic view. The standalone PropertyExecutiveSummary
              page was deleted once its definition reconciled to the cent — two routes to two
              implementations of one report is a demo hazard, and the unverified one is the one
              someone opens by accident. It lives in git history, not in the app. */}
          {activePage==='report' && reportAllowed && !openReport.component && (
            <ReportView key={reportPreset?.id || reportId} definition={openReport} preset={reportPreset} allClients={allClients}
              onBack={() => navigateTo({ page: 'reporting' })} ownerId={staffUser?.id ? String(staffUser.id) : null}
              onOpenParcel={openReportRow} />
          )}
          {/* A CARD (registry `component`): Notice Progress, Productivity — its own screen under the report's title. */}
          {activePage==='report' && reportAllowed && openReport.component && (
            <div style={{ padding:'20px 24px', maxWidth:'1240px', margin:'0 auto' }}>
              <button onClick={() => navigateTo({ page: 'reporting' })} style={{ padding:'5px 12px', background:'transparent', border:`1px solid ${c.cardBorder}`, borderRadius:'7px', color:c.muted, fontSize:'12px', cursor:'pointer', marginBottom:'12px' }}>← Back</button>
              <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'6px' }}>{openReport.title}</div>
              <openReport.component darkMode={darkMode} staffUser={staffUser} allClients={allClients}
                onDrillParcel={(p) => navigateTo({ drillParcel:p })} />
            </div>
          )}

          {/* TASK ENGINE */}
          {activePage==='tasks' && <TaskEngine darkMode={darkMode} staffUser={staffUser} allClients={allClients} allProperties={allProperties} initialTask={drillTask} onClearInitialTask={()=>setDrillTask(null)} />}
          {activePage==='extractor' && (
  <NoticeExtractor darkMode={darkMode} allClients={allClients} />
)}

          {/* NOTICE AUDIT */}
          {activePage==='audit' && <NoticeAudit darkMode={darkMode} staffUser={staffUser} allClients={allClients} />}

          {/* FEEDBACK */}
          {activePage==='feedback' && (
            <Feedback
              darkMode={darkMode}
              staffUser={staffUser}
              initialPrefill={feedbackPrefill}
              onClearPrefill={() => setFeedbackPrefill(null)}
            />
          )}

          {/* UTILITIES — section bubble screen */}
          {activePage==='utilities' && (
            <div style={{ width:'100%' }}>
              <button style={s.btnBack} onClick={() => navigateTo({ page:'dashboard' })}>← Back</button>
              <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'6px' }}>Utilities</div>
              <div style={{ fontSize:'13px', color:c.muted, marginBottom:'22px' }}>Manage users, reference tables, and tools. More management screens arrive in M4.</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'24px' }}>
                {utilitiesChildren.filter(it=>!it.divider).map(it => (
                  <button key={it.id} onClick={() => { if (!it.soon) navigateTo({ page:it.id }); }}
                    title={it.soon ? `Coming in ${it.soon}` : it.label}
                    style={{ position:'relative', padding:'22px 16px', borderRadius:'12px', border:`2px solid ${c.cardBorder}`, cursor: it.soon ? 'default' : 'pointer', background: isDark?(it.soon?'rgba(255,255,255,0.02)':'rgba(255,255,255,0.03)'):(it.soon?'#fafbfd':'#ffffff'), fontFamily:"'Public Sans',system-ui,sans-serif", textAlign:'center', fontSize:'13px', fontWeight:'600', color: it.soon ? c.muted : c.text, opacity: it.soon ? 0.7 : 1, transition:'all 0.15s', boxShadow: isDark?'none':'0 1px 4px rgba(0,0,0,0.05)' }}
                    onMouseEnter={e=>{ if(it.soon) return; e.currentTarget.style.borderColor='var(--red)'; e.currentTarget.style.background=isDark?'rgba(180,0,0,0.08)':'rgba(180,0,0,0.06)'; e.currentTarget.style.transform='scale(1.03)'; }}
                    onMouseLeave={e=>{ if(it.soon) return; e.currentTarget.style.borderColor=c.cardBorder; e.currentTarget.style.background=isDark?'rgba(255,255,255,0.03)':'#ffffff'; e.currentTarget.style.transform='scale(1)'; }}>
                    {it.label}
                    {it.soon && <span style={{ position:'absolute', top:'8px', right:'8px', fontSize:'9px', fontWeight:'700', color:'var(--red)', background:'rgba(180,0,0,0.12)', border:'1px solid rgba(180,0,0,0.3)', borderRadius:'10px', padding:'1px 6px' }}>{it.soon}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* NTRG — section bubble screen */}
          {activePage==='ntrg' && (
            <div style={{ width:'100%' }}>
              <button style={s.btnBack} onClick={() => navigateTo({ page:'dashboard' })}>← Back</button>
              <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'6px' }}>NTRG</div>
              <div style={{ fontSize:'13px', color:c.muted, marginBottom:'22px' }}>Internal people &amp; firm resources. These sections arrive in M5.</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'24px' }}>
                {ntrgChildren.filter(it=>!it.divider).map(it => (
                  <button key={it.id} onClick={() => { if (!it.soon) navigateTo({ page:it.id }); }}
                    title={it.soon ? `Coming in ${it.soon}` : it.label}
                    style={{ position:'relative', padding:'22px 16px', borderRadius:'12px', border:`2px solid ${c.cardBorder}`, cursor: it.soon ? 'default' : 'pointer', background: isDark?(it.soon?'rgba(255,255,255,0.02)':'rgba(255,255,255,0.03)'):(it.soon?'#fafbfd':'#ffffff'), fontFamily:"'Public Sans',system-ui,sans-serif", textAlign:'center', fontSize:'13px', fontWeight:'600', color: it.soon ? c.muted : c.text, opacity: it.soon ? 0.7 : 1, transition:'all 0.15s', boxShadow: isDark?'none':'0 1px 4px rgba(0,0,0,0.05)' }}
                    onMouseEnter={e=>{ if(it.soon) return; e.currentTarget.style.borderColor='var(--red)'; e.currentTarget.style.background=isDark?'rgba(180,0,0,0.08)':'rgba(180,0,0,0.06)'; e.currentTarget.style.transform='scale(1.03)'; }}
                    onMouseLeave={e=>{ if(it.soon) return; e.currentTarget.style.borderColor=c.cardBorder; e.currentTarget.style.background=isDark?'rgba(255,255,255,0.03)':'#ffffff'; e.currentTarget.style.transform='scale(1)'; }}>
                    {it.label}
                    {it.soon && <span style={{ position:'absolute', top:'8px', right:'8px', fontSize:'9px', fontWeight:'700', color:'var(--red)', background:'rgba(180,0,0,0.12)', border:'1px solid rgba(180,0,0,0.3)', borderRadius:'10px', padding:'1px 6px' }}>{it.soon}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* DOCUMENTS BROWSER (staff-side, all-scope) */}
          {activePage==='documents' && (
            <div style={{ margin:'-24px -32px' }}>
              <DocumentsBrowser
                scope={{ type:'all' }}
                viewer={{
                  role:'staff',
                  user_id: staffUser?.id,
                  user_name: staffUser?.name || 'NTRG Staff',
                }}
                onClose={() => navigateTo({ page:'dashboard' })}
              />
            </div>
          )}

        </div>
      </main>

      {/* ── WIGLEY TIP TOAST ── */}
      {showTip && (
        <div style={{ position:'fixed', bottom:'24px', right:'24px', maxWidth:'320px', background: isDark ? 'var(--ink)' : '#ffffff', border:'1px solid rgba(180,0,0,0.4)', borderRadius:'12px', padding:'16px 18px', boxShadow:'0 8px 32px rgba(0,0,0,0.3)', zIndex:100, animation:'slideIn 0.3s ease' }}>
          <div style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
            <img src="/Wigley.png" alt="Wigley" style={{ width:'36px', height:'36px', objectFit:'contain', flexShrink:0 }} />
            <div>
              <div style={{ fontSize:'11px', fontWeight:'700', color:'var(--red)', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Wigley's Tip</div>
              <div style={{ fontSize:'13px', color: isDark ? '#e8eaf2' : 'var(--ink)', lineHeight:'1.5' }}>{currentTip}</div>
            </div>
            <button onClick={()=>setShowTip(false)} style={{ background:'none', border:'none', color:'var(--slate)', cursor:'pointer', fontSize:'16px', lineHeight:1, padding:'0', flexShrink:0 }}>✕</button>
          </div>
        </div>
      )}

      {/* ── WELCOME TOAST ── */}
      {showToast && (
        <div style={{ position:'fixed', bottom:'24px', right:'24px', maxWidth:'300px', background: isDark ? 'var(--ink)' : '#ffffff', border:'1px solid rgba(180,0,0,0.4)', borderRadius:'12px', padding:'16px 18px', boxShadow:'0 8px 32px rgba(0,0,0,0.25)', zIndex:100 }}>
          <div style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
            <img src="/Wigley.png" alt="Wigley" style={{ width:'36px', height:'36px', objectFit:'contain', flexShrink:0 }} />
            <div>
              <div style={{ fontSize:'11px', fontWeight:'700', color:'var(--red)', marginBottom:'4px' }}>GOOD TO SEE YOU!</div>
              <div style={{ fontSize:'13px', color: isDark ? '#e8eaf2' : 'var(--ink)', lineHeight:'1.5' }}>Welcome back, {staffFirstName}. Your WigleyHub is loading.</div>
            </div>
            <button onClick={()=>setShowToast(false)} style={{ background:'none', border:'none', color:'var(--slate)', cursor:'pointer', fontSize:'16px', lineHeight:1, padding:'0', flexShrink:0 }}>✕</button>
          </div>
        </div>
      )}

      {/* Phase 2b: stale deep-link not-found toast */}
      {deepLinkMsg && (
        <div style={{ position:'fixed', bottom:'24px', left:'50%', transform:'translateX(-50%)', background: isDark ? 'var(--ink)' : '#ffffff', border:'1px solid rgba(180,0,0,0.4)', borderRadius:'10px', padding:'12px 18px', boxShadow:'0 8px 32px rgba(0,0,0,0.25)', zIndex:200, display:'flex', alignItems:'center', gap:'12px' }}>
          <div style={{ fontSize:'13px', color: isDark ? '#e8eaf2' : 'var(--ink)' }}>{deepLinkMsg}</div>
          <button onClick={()=>setDeepLinkMsg('')} style={{ background:'none', border:'none', color:'var(--slate)', cursor:'pointer', fontSize:'15px', lineHeight:1, padding:0 }}>✕</button>
        </div>
      )}

      {/* ── ADD CONTACT MODAL ── (visibility driven solely by the modal snapshot dim; drillClient is always paired with it, so no drillClient close-crutch) */}
      {modal === 'contact' && contactModal && (
        <ContactModal
          isDark={isDark}
          clientId={drillClient?.id}
          contact={contactModal.contact}
          prefillPropertyId={contactModal.prefillPropertyId}
          onClose={() => { navigateTo({ modal: null }); setContactModal(null); }}
          onSuccess={() => {
            navigateTo({ modal: null }); setContactModal(null);
            setContactRefresh(x => x + 1);   // re-load the read-only Property Contacts card
            if (drillClient) loadClientContacts(drillClient.id).then(arr => {
              if (drillContact) navigateTo({ drillContact: arr.find(ct => ct.id === drillContact.id) || null });
            });
          }}
        />
      )}

      {modal === 'addProperty' && (
        <AddPropertyModal
          isDark={isDark}
          clients={allClients}
          defaultClient={drillClient && allClients.find(cl => cl.id === drillClient.id) ? drillClient : null}
          allProperties={allProperties}
          onClose={() => navigateTo({ modal: null })}
          onSuccess={(created) => {
            setAllProperties(prev => [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
            // ⛔ LAND ON THE NEW PROPERTY. The property drill is gated on drillClient AND drillProperty
            // (:2123), so both are set — from Onboarding there is no drilled client to inherit, and
            // navigating to a property without its client would render nothing at all.
            const owner = (allClients || []).find(cl => cl.id === created.client_id) || null;
            if (owner) {
              navigateTo({ page:'clients', drillClient: owner, drillProperty: created, drillContact:null, drillParcel:null, modal:null });
            } else {
              navigateTo({ modal: null });
            }
            setAddPropertyMsg(`✓ "${created.name}" added — parcels and contacts can be added next.`);
            setTimeout(() => setAddPropertyMsg(''), 6000);
          }}
        />
      )}

      {/* ── EDIT PROPERTY (Add+Edit modal in edit mode) ── */}
      {modal === 'editProperty' && editProperty && (
        <AddPropertyModal
          isDark={isDark}
          clients={allClients}
          property={editProperty}
          allProperties={allProperties}
          onClose={() => navigateTo({ modal: null })}
          onSuccess={(updated) => {
            setProperties(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
            setAllProperties(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
            setPropFull(updated);                                        // instant Details-card refresh
            navigateTo({ modal: null, drillProperty: { ...drillProperty, ...updated } }); // close overlay + keep the drill in sync (same id)
            setEditProperty(null);
          }}
        />
      )}

      {/* ── PARCEL MODAL (view/edit) ── */}
      {drillParcel && (
        <ParcelModal
          parcel={drillParcel}
          focusSection={drillParcelFocus}
          focusBill={drillParcelBill}
          darkMode={darkMode}
          staffUser={staffUser}
          allStaff={[]}
          onYearChange={setValueYear}
          onClose={() => { setDrillParcelFocus(null); setDrillParcelBill(null); setWorklistRefresh(n => n + 1); setProrataReload(n => n + 1); navigateTo({ drillParcel:null }); }}
          onParcelUpdated={(updated) => {
            setParcels(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p)); // refresh list row; STAY in the modal (closing is onClose's job)
          }}
        />
      )}

      {/* ── ADD CLIENT (Onboarding) ──────────────────────────────────────────────────────────
          ⛔ LANDS THE USER ON THE NEW CLIENT, not back on an empty Onboarding page. The client
          drill lives under activePage 'clients' with drillClient set (:1772), so both move. */}
      {modal === 'addClient' && (
        <AddClientModal
          isDark={isDark}
          client={null}
          onClose={() => navigateTo({ modal: null })}
          onSuccess={(created) => {
            setAllClients(prev => [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
            setClients(prev => [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
            navigateTo({ page:'clients', drillClient: created, drillProperty:null, drillContact:null, drillParcel:null, modal:null });
          }}
        />
      )}

      {/* ── EDIT CLIENT (client page) ─────────────────────────────────────────────────────────
          The shared client form in edit mode. The saved row replaces the client everywhere this page holds it
          (both lists + the drill); same id, so navigateTo pushes no history entry for it. */}
      {modal === 'editClient' && drillClient && (
        <AddClientModal
          isDark={isDark}
          client={drillClient}
          onClose={() => navigateTo({ modal: null })}
          onSuccess={(updated) => {
            setAllClients(prev => prev.map(x => x.id === updated.id ? updated : x));
            setClients(prev => prev.map(x => x.id === updated.id ? updated : x));
            navigateTo({ drillClient: updated, modal: null });
          }}
        />
      )}

      {/* ── ADD CONSULTANT (Onboarding) ───────────────────────────────────────────────────────
          ⛔ Lands on the consultants LIST, not the new consultant. navigateTo has no drillConsultant
          dimension — ConsultantsPage owns its own drill state internally — so there is nothing to
          hand it. Adding one is a bigger change than this commit. */}
      {modal === 'addConsultant' && (
        <AddConsultantModal
          isDark={isDark}
          consultant={null}
          onClose={() => navigateTo({ modal: null })}
          // ⛔ REPLACE, NOT PUSH. Pushing left the Add form's entry under the consultants list, so Back reopened a blank
          // Add Consultant form — which staff took for the edit screen, ticked Auto-renews on, and could not save
          // (it is a second ADD, needing every field and a contact again). 2026-11-03.
          onSuccess={() => navigateTo({ page:'consultants', modal:null }, { replace: true })}
        />
      )}

      {/* ── PARCEL MODAL (create) — E2a ── */}
      {modal === 'createParcel' && createParcelFor && (
        <ParcelModal
          mode="create"
          property={createParcelFor}
          parcel={null}
          darkMode={darkMode}
          staffUser={staffUser}
          allStaff={[]}
          onYearChange={setValueYear}
          onClose={() => navigateTo({ modal: null })}
          onParcelCreated={(created) => {
            setParcels(prev => [...prev, created]);
            setCreateParcelFor(null);
            navigateTo({ modal: null, drillParcel:created }); // close create overlay + reopen the new parcel in normal view/edit mode
          }}
        />
      )}

      <style>{`@keyframes slideIn { from { transform: translateY(20px); opacity:0; } to { transform: translateY(0); opacity:1; } }`}</style>

      {/* Upload Link Modal */}
      {modal === 'genToken' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <div style={{ background:isDark?'#2F3854':'#ffffff', borderRadius:'14px', padding:'28px', maxWidth:'520px', width:'100%', boxShadow:'0 8px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'20px', fontWeight:'700', color:isDark?'#e8eaf2':'var(--ink)', marginBottom:'6px' }}>Upload Link Generated ✓</div>
            <div style={{ fontSize:'13px', color:isDark?'#a8b0cc':'var(--slate)', marginBottom:'20px' }}>Send this link to <strong>{drillClient?.name}</strong>. It expires in 60 days.</div>
            <div style={{ background:isDark?'rgba(0,0,0,0.2)':'#f5f7fb', borderRadius:'8px', padding:'12px 14px', fontFamily:'monospace', fontSize:'12px', color:isDark?'var(--red)':'var(--ink)', wordBreak:'break-all', marginBottom:'16px' }}>{newTokenUrl}</div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button style={{ flex:1, padding:'11px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'8px', color: 'white', fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" }} onClick={()=>{ navigator.clipboard.writeText(newTokenUrl); }}>📋 Copy Link</button>
              <button style={{ padding:'11px 16px', background:'transparent', border:`1px solid ${isDark?'#434C68':'var(--line)'}`, borderRadius:'8px', color:isDark?'#a8b0cc':'var(--slate)', fontSize:'13px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" }} onClick={()=>navigateTo({ modal: null })}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:9999, display:'flex', flexDirection:'column' }}>
          <div style={{ background:'var(--ink)', padding:'10px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #374060', flexShrink:0 }}>
            <div style={{ fontSize:'13px', color:'#a8b0cc', fontFamily:'monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'600px' }}>{previewFile.name}</div>
            <div style={{ display:'flex', gap:'10px' }}>
              {previewFile.url && (
                <a href={previewFile.url} download={previewFile.name} style={{ padding:'6px 14px', background:'linear-gradient(135deg,#B40000,#9C0000)', borderRadius:'6px', color: 'white', fontSize:'12px', fontWeight:'700', textDecoration:'none' }}>⬇ Download</a>
              )}
              <button onClick={()=>setPreviewFile(null)} style={{ padding:'6px 14px', background:'rgba(248,113,113,0.15)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:'6px', color:'#f87171', fontSize:'12px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" }}>✕ Close</button>
            </div>
          </div>
          <div style={{ flex:1, overflow:'auto', background:'#f5f5f5' }}>
            {previewFile.type === 'html' ? (
              <iframe srcDoc={previewFile.html} style={{ width:'100%', height:'100%', border:'none', display:'block' }} title="Document Preview" sandbox="allow-same-origin" />
            ) : previewFile.url ? (
              <iframe src={`${previewFile.url}#toolbar=1&navpanes=0&view=FitH`} style={{ width:'100%', height:'100%', border:'none', display:'block' }} title="Document Preview" />
            ) : (
              // ⛔ NEVER render an iframe with a falsy url — `${undefined}#...` is a RELATIVE url, the SPA
              // catch-all serves index.html, and the app renders inside the pane (a masked failure). Show the error.
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', padding:'24px', textAlign:'center', color:'#B40000', fontSize:'13px' }}>
                Could not load this document — no file URL was returned (the file may not exist in storage).
              </div>
            )}
          </div>
        </div>
      )}

      {/* Global Floating Report button — appears on every page except Feedback itself */}
      {staffUser && (
        <FloatingReportButton
          darkMode={darkMode}
          activePage={activePage}
          hidden={activePage === 'feedback'}
          onReportRequested={(prefill) => {
            setFeedbackPrefill(prefill);
            navigateTo({ page:'feedback' });
          }}
          onOpenFeedback={() => { setFeedbackPrefill(null); navigateTo({ page:'feedback' }); }}
        />
      )}
    </div>
  );
}
